/* assets/ring-builder.js */
(function () {

  // ── State ──────────────────────────────────────────────
  const state = {
    setting: null, shape: 'round', metal: null,
    band: null, diamond: null, size: null,
    band_thickness: null, band_profile: null,
    diamond_size: '1.50', diamond_quality: null,
    engraving: '',
    pricing: {
      price: null,
      configuredSku: null,
      latestRequestId: null,
      isLoading: false,
      error: null,
      checkoutBusy: false,
      lastSuccessfulFormatted: null,
      lastSuccessfulRequestId: null,
      lastSuccessfulPayloadKey: '',
      lastCheckoutPayloadKey: ''
    }
  };

  /* ─── Configured SKU (client-side cart line key) ──────────────
     Each ring config gets a stable SKU derived from the user's
     selections, attached to the cart line as `properties[Configured SKU]`.

     Format (8 segments after the prefix):
       BM-RB-<setting>-<shape>-<metal>-<bandThickness>-<bandProfile>-<diamond>-<size>
     e.g. `BM-RB-3STN-BAG-PT950-MED-DBND-250-7`.

     Unknown / unselected segments emit `X` so partial states are still
     parseable and the SKU's positional structure stays intact across
     incomplete configurations.

     Canonical short codes for known options live in `SKU_CODES`.
     Anything not in the dictionary (merchant-added shapes/settings via
     metaobjects, future option additions) falls back to `skuSlug()` — a
     deterministic uppercase-alphanumeric truncation of the option title,
     so any newly-added option still produces a sensible code without
     requiring a code change. */
  const SKU_CODES = {
    setting: {
      'Solitaire': 'SOL',
      'Halo': 'HALO',
      'Hidden Halo': 'HHALO',
      'Three-Stone': '3STN',
      'Three Stone': '3STN',
      'Cathedral': 'CTHD',
      'Toi Et Moi': 'TOIM'
    },
    shape: {
      'Round': 'RND',
      'Oval': 'OVL',
      'Cushion': 'CSH',
      'Asscher': 'ASCH',
      'Emerald': 'EMR',
      'Marquise': 'MARQ',
      'Princess': 'PRCS',
      'Pear': 'PEAR',
      'Radiant': 'RAD',
      'Heart': 'HRT',
      'Baguette': 'BAG'
    },
    metal: {
      'Yellow Gold': 'YG18',
      '18K Gold': 'YG18',
      'White Gold': 'WG18',
      '18K White Gold': 'WG18',
      'Rose Gold': 'RG18',
      '18K Rose Gold': 'RG18',
      'Platinum': 'PT950',
      '950 Platinum': 'PT950'
    },
    bandThickness: {
      'Thin': 'THIN',
      'Medium': 'MED',
      'Bold': 'BOLD'
    },
    bandProfile: {
      'Classic': 'CLSC',
      'Rounded': 'RND',
      'Diamond Band': 'DBND'
    }
  };

  /* Slug fallback for merchant-defined options that aren't in `SKU_CODES`
     (custom ring designs, custom shapes added via metaobjects, etc.). */
  function skuSlug(value) {
    if (!value) return 'X';
    var slug = String(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
    return slug ? slug.slice(0, 6) : 'X';
  }

  function skuCode(group, value) {
    if (!value) return 'X';
    var dict = SKU_CODES[group] || {};
    return dict[value] || skuSlug(value);
  }

  /* Diamond size: input shape is `1.50ct` / `1.5 ct` / `0.75 ct`.
     Strip non-digits, drop the trailing decimal placeholder so `1.50` → `150`. */
  function skuDiamondCode() {
    var size = (diamondSelections && diamondSelections.size) || '';
    if (!size) return 'X';
    var match = String(size).match(/(\d+(?:\.\d+)?)/);
    if (!match) return 'X';
    var carats = parseFloat(match[1]);
    if (!isFinite(carats) || carats <= 0) return 'X';
    return String(Math.round(carats * 100));
  }

  /* Ring size: keep numeric, drop the dot so `7.5` → `75`. */
  function skuSizeCode() {
    // state.size may hold a region-aware display label (letters for AU/UK)
    // that strips to empty. Read the US-numeric canonical value from
    // data-readout-us on the active stop for SKU encoding.
    var size = state.size || '';
    var panel = document.querySelector('.rb-panel--ring-size');
    if (panel) {
      var active = panel.querySelector('.rb-dsize-stop.active');
      if (active) {
        var dv = (active.getAttribute('data-readout-us') || active.getAttribute('data-value') || '').trim();
        if (dv) size = dv;
      }
    }
    if (!size) return 'X';
    var clean = String(size).replace(/[^\d.]/g, '');
    if (!clean) return 'X';
    return clean.replace('.', '');
  }

  /* Metal can come from `state.metal`, or from `metalSelections.ring`
     (when the metal step uses split ring/setting selections).
     When ring and setting metals differ, encode both so the configured
     SKU matches the dual-metal review/cart display. */
  function skuMetalCode() {
    var ringMetal = metalSelections && metalSelections.ring;
    var settingMetal = metalSelections && metalSelections.setting;
    if (ringMetal && settingMetal && ringMetal !== settingMetal) {
      return skuCode('metal', ringMetal) + skuCode('metal', settingMetal);
    }
    var metal = state.metal || ringMetal || settingMetal || '';
    return skuCode('metal', metal);
  }

  function skuBandThicknessCode() {
    return skuCode('bandThickness', (bandSelections && bandSelections.thickness) || '');
  }

  function skuBandProfileCode() {
    return skuCode('bandProfile', (bandSelections && bandSelections.profile) || '');
  }

  function buildConfiguredSku() {
    return [
      'BM-RB',
      skuCode('setting', state.setting),
      skuCode('shape', state.shape),
      skuMetalCode(),
      skuBandThicknessCode(),
      skuBandProfileCode(),
      skuDiamondCode(),
      skuSizeCode()
    ].join('-');
  }

  const TOTAL_STEPS = 6;
  let currentStep = 1;
  let metalTabMode = 'ring';
  let bandTabMode = 'thickness';
  let diamondTabMode = 'size';
  const metalSelections = { ring: null, setting: null };
  const bandSelections = { thickness: null, profile: null };
  var selectedComponents = { setting: false, diamond: false, band: false };
  const diamondSelections = { size: null, quality: null };
  const METAL_CONTENT = (function () {
    const src = (window.RingAssets && window.RingAssets.metalContent) || {};
    function entry(key, defLabel, defDesc, defSwatch, defLightBorder) {
      const c = src[key] || {};
      return {
        label: c.label || defLabel,
        description: c.description || defDesc,
        swatch: c.swatch != null && c.swatch !== '' ? c.swatch : defSwatch,
        lightBorder: typeof c.lightBorder === 'boolean' ? c.lightBorder : !!defLightBorder
      };
    }
    const defaults = {
      'Yellow Gold': ['18K Yellow Gold', "Yellow gold sits against warm skin in a way no other metal can replicate.\n\nIt holds its colour without plating, so the finish you choose stays the finish you wear. It suits warm and olive undertones best, and reads particularly well against skin where bronze, amber and coral tones bring the complexion forward.\n\nRight for you if the veins on your wrist read green rather than blue, if your style leans classic, warm or vintage, or if you want a metal that will not need re-plating over the life of the ring.", '#E8C97A', false],
      'White Gold': ['18K White Gold', "White gold gives your diamond the cleanest possible backdrop.\n\nIts cool, bright surface acts as a mirror, making the stone read whiter, crisper and brighter than any warm-toned metal. It suits cool and neutral skin tones and pairs particularly well with higher colour grades.\n\nRight for you if you want the diamond to lead and the metal to disappear, if your style runs minimal and modern, or if you want a contemporary look without moving up to platinum. Worth knowing: the rhodium plating that gives white gold its bright finish softens over years of wear and can be re-plated when needed.", '#E8E8E8', true],
      'Rose Gold': ['18K Rose Gold', "Rose gold is the safest metal to pick when you are choosing for someone else, because it flatters almost every skin tone.\n\nIts colour comes from mixing pure gold with copper, which also makes it the hardest of the three gold tones and the most resistant to daily wear. Unlike white gold, it does not need plating: the blush sits in the alloy itself and deepens gently over time.\n\nRight for you if you want a warm metal that still reads modern rather than traditional, if you are buying without being able to check the wearer's undertones in person, or if you want the lowest-maintenance gold on the list.", '#E8B4A0', false],
      Platinum: ['950 Platinum', "Platinum is the only fully white metal in the list. It is naturally bright white without any plating, so the colour never fades and never needs restoring.\n\nIt is also noticeably heavier than gold (you will feel it on your finger) and dense enough that a scratch moves the metal rather than removes it. Over years of wear, platinum builds a soft satin patina that many owners come to prefer over the original polish.\n\nRight for you if you want a lifetime metal with almost no maintenance, if you have reacted to other metals in the past, or if you want the highest-purity option in the range. It suits any skin tone and sits particularly well against cool undertones.", '#C8D0D8', true]
    };
    const out = {};
    Object.keys(src).forEach(function (k) {
      const d = defaults[k];
      if (d) {
        out[k] = entry(k, d[0], d[1], d[2], d[3]);
      } else {
        out[k] = entry(k, k, '', '#cccccc', false);
      }
    });
    /* Always retain the canonical defaults (`Yellow Gold`, `White Gold`,
       `Rose Gold`, `Platinum`) so the fuzzy resolver in
       `resolveMetalContent()` can fall back to them when the merchant's
       metaobject titles differ (e.g. `18K Gold`, `950 Platinum`) and
       carry no `description` field. */
    Object.keys(defaults).forEach(function (k) {
      if (!out[k]) {
        const d = defaults[k];
        out[k] = entry(k, d[0], d[1], d[2], d[3]);
      }
    });
    return out;
  }());
  const METAL_RENDER_PRESETS = {
    'Yellow Gold': { color: 0xe1bf70, metalness: 0.97, roughness: 0.26, envMapIntensity: 0.9, clearcoat: 0.68, clearcoatRoughness: 0.1 },
    'White Gold': { color: 0xe8e8e8, metalness: 0.98, roughness: 0.24, envMapIntensity: 0.92, clearcoat: 0.7, clearcoatRoughness: 0.1 },
    'Rose Gold': { color: 0xe6ad9a, metalness: 0.97, roughness: 0.27, envMapIntensity: 0.86, clearcoat: 0.66, clearcoatRoughness: 0.11 },
    Platinum: { color: 0xd0d5dc, metalness: 0.98, roughness: 0.23, envMapIntensity: 0.95, clearcoat: 0.72, clearcoatRoughness: 0.09 }
  };
  const METAL_REALISM_LIMITS = {
    metalnessMin: 0.92,
    metalnessMax: 0.99,
    roughnessMin: 0.22,
    roughnessMax: 0.45,
    clearcoatMin: 0.45,
    clearcoatMax: 0.78,
    clearcoatRoughnessMin: 0.07,
    clearcoatRoughnessMax: 0.2,
    envMapIntensityMin: 0.55,
    envMapIntensityMax: 0.98
  };

  function firstMetalContentKey() {
    var keys = Object.keys(METAL_CONTENT);
    return keys.length ? keys[0] : 'Yellow Gold';
  }

  /* Map merchant-defined metal titles (e.g. "18K Gold", "950 Platinum",
     "18k White Gold") to a canonical METAL_CONTENT key so we can pull the
     correct long description even when the metaobject's `description`
     field is blank. */
  function canonicalMetalKey(title) {
    if (!title) return '';
    var normalised = String(title).toLowerCase();
    if (normalised.indexOf('rose') !== -1) return 'Rose Gold';
    if (normalised.indexOf('white') !== -1) return 'White Gold';
    if (normalised.indexOf('platinum') !== -1) return 'Platinum';
    if (normalised.indexOf('yellow') !== -1) return 'Yellow Gold';
    if (normalised.indexOf('gold') !== -1) return 'Yellow Gold';
    return '';
  }

  function resolveMetalContent(activeMetal) {
    var activeContent = (activeMetal && METAL_CONTENT[activeMetal]) || {};
    var canonical = canonicalMetalKey(activeMetal);
    var canonicalContent = canonical && METAL_CONTENT[canonical];

    /* ponytail: metaobject subtitles land on merchant-specific keys (`18K Gold`);
       side-card copy must use the long locale description from canonical keys. */
    var description = activeContent.description || '';
    if (canonicalContent && canonicalContent.description) {
      if (!description || canonicalContent.description.length > description.length) {
        description = canonicalContent.description;
      }
    }

    return {
      label: displayMetalLabel(activeMetal, activeContent.label || activeMetal || (canonicalContent && canonicalContent.label) || ''),
      description: description,
      swatch: activeContent.swatch || (canonicalContent && canonicalContent.swatch) || '#cccccc',
      lightBorder: typeof activeContent.lightBorder === 'boolean'
        ? activeContent.lightBorder
        : !!(canonicalContent && canonicalContent.lightBorder)
    };
  }

  /* Merchant metaobject titles often read `18K Gold`; customer-facing copy is
     `18K Yellow Gold`. Keep `data-value`/state as the merchant key for n8n. */
  function displayMetalLabel(metalValue, labelHint) {
    if (!metalValue) return labelHint || '';
    var content = METAL_CONTENT[metalValue];
    if (content && content.label) return content.label;
    if (labelHint) return labelHint;
    var n = String(metalValue).toLowerCase();
    if (n.indexOf('gold') !== -1 && n.indexOf('yellow') === -1 && n.indexOf('white') === -1 && n.indexOf('rose') === -1) {
      return '18K Yellow Gold';
    }
    return metalValue;
  }

  function firstMetalRenderPresetKey() {
    var keys = Object.keys(METAL_RENDER_PRESETS);
    return keys.length ? keys[0] : 'Yellow Gold';
  }
  // Preset from source/ring_model__Mat_Diamond.json
  // KHR_materials_transmission: 0.95, KHR_materials_ior: 2.33, KHR_materials_volume thickness: 1
  const DIAMOND_RENDER_PRESET = {
    color: 0xffffff,
    metalness: 0,
    roughness: 0.0,
    transmission: 0.6,
    opacity: 1,
    ior: 2.33,
    thickness: 1.0,
    envMapIntensity: 1.3,
    clearcoat: 1,
    clearcoatRoughness: 0.0,
    dispersion: 0.05
  };
  const QUALITY_PRESETS = {
    mobile: {
      antialias: false,
      pixelRatioMax: 1.25,
      toneMappingExposure: 0.86,
      shadowMapSize: 512,
      fillLightIntensity: 0.1,
      keyLightIntensity: 0.65,
      envMapIntensityMultiplier: 0.92,
      bloom: { enabled: false, strength: 0, radius: 0, threshold: 1 }
    },
    balanced: {
      antialias: true,
      pixelRatioMax: 1.6,
      toneMappingExposure: 0.9,
      shadowMapSize: 1024,
      fillLightIntensity: 0.12,
      keyLightIntensity: 0.9,
      envMapIntensityMultiplier: 1,
      bloom: { enabled: true, strength: 0.08, radius: 0.35, threshold: 0.93 }
    },
    premium: {
      antialias: true,
      pixelRatioMax: 2,
      toneMappingExposure: 0.94,
      shadowMapSize: 2048,
      fillLightIntensity: 0.14,
      keyLightIntensity: 1.05,
      envMapIntensityMultiplier: 1.08,
      bloom: { enabled: true, strength: 0.12, radius: 0.42, threshold: 0.9 }
    }
  };
  const DEFAULT_TAGGING_CONFIG = {
    mode: 'explicit',
    roleKeys: ['rbRole', 'rb_role', 'jewelryRole', 'role'],
    fallbackHeuristics: false,
    roles: {
      metal: { values: ['metal', 'ring_metal', 'setting_metal', 'band_metal'], meshNames: [], materialNames: [] },
      diamond: { values: ['diamond', 'stone', 'gem', 'center_stone'], meshNames: [], materialNames: [] }
    },
    byComponent: {
      band: {},
      setting: {},
      diamond: {}
    },
    componentDefaults: {
      band: 'metal',
      setting: 'metal',
      diamond: 'diamond'
    }
  };
  const DEFAULT_VIEW_PRESET = {
    fitMaxAxis: 2.0,
    cameraDistanceMultiplier: 1.8,
    modelYOffsetFactor: 0.0,
    minDistanceFactor: 0.3,
    maxDistanceFactor: 5.0
  };
  /* Shape step framing: the ring model stands upright with the diamond table
     facing UP (+Y), so a level front/side camera only ever sees the ring edge
     or the finger hole. A small polar angle puts the camera above-and-slightly-
     front, looking DOWN at the table so the stone faces the viewer (band
     horizontal, stone face-up — the design reference). */
  const SHAPE_STONE_FRONT_CAMERA = {
    azimuthDeg: 0,
    polarDeg: 5,
    distanceMultiplier: 1.0,
    targetYFactor: 0
  };
  const DEFAULT_HDR_ENV = 'https://cdn.jsdelivr.net/gh/mrdoob/three.js@r170/examples/textures/equirectangular/venice_sunset_1k.hdr';
  const DIAMOND_HINT_RE = /(diamond|gem|stone|brilliant|carat|center|melee|vvs|vs1|si1)/i;
  const METAL_HINT_RE = /(ring|band|shank|prong|setting|metal|gold|platinum|silver|white|rose|yellow)/i;
  const COMPONENT_TYPES = ['band', 'bandGems', 'setting', 'settingGems', 'diamond'];
  const GEM_ARRAY_TYPES = { bandGems: true, settingGems: true };
  const BAND_GEM_COUNT = 14;
  const COMPONENT_KEY_SKIP = { default: true, variants: true, bySetting: true, byShape: true, byShapeCarat: true, bySize: true, byQuality: true, byThickness: true, byProfile: true, byCombo: true, urls: true };
  const SETTING_SLIDE_GROUPS = ['setting-solitaire', 'setting-halo', 'setting-hidden-halo'];
  const SHAPE_SLIDE_GROUPS = (window.RingAssets && window.RingAssets.components && window.RingAssets.components.shapeSlideGroups && window.RingAssets.components.shapeSlideGroups.length)
    ? window.RingAssets.components.shapeSlideGroups
    : ['shape-round', 'shape-oval', 'shape-cushion', 'shape-emerald', 'shape-radiant', 'shape-pear', 'shape-asscher', 'shape-princess', 'shape-marquise', 'shape-heart'];
  const BAND_THICKNESS_SLIDE_GROUPS = ['band-thickness-thin', 'band-thickness-medium', 'band-thickness-bold'];
  const BAND_PROFILE_SLIDE_GROUPS = ['band-profile-classic', 'band-profile-rounded', 'band-profile-diamond-band'];
  const DIAMOND_SIZE_SLIDE_GROUPS = ['diamond-size'];
  const DIAMOND_SIZE_STOPS = [
    '0.50', '0.75', '1.00', '1.25', '1.50', '1.75', '2.00',
    '2.50', '3.00', '3.50', '4.00', '5.00', '6.00', '7.00'
  ];
  const DIAMOND_SIZE_VISIBLE_DOT_STOPS = [
    '0.50', '1.00', '1.50', '2.00', '3.00', '5.00', '7.00'
  ];
  const DIAMOND_SIZE_ANCHOR_STOPS = [
    '0.50', '1.00', '1.50', '2.00', '3.00', '5.00', '7.00'
  ];
  const SLIDESHOW_GROUPS = SETTING_SLIDE_GROUPS
    .concat(SHAPE_SLIDE_GROUPS)
    .concat(BAND_THICKNESS_SLIDE_GROUPS)
    .concat(BAND_PROFILE_SLIDE_GROUPS)
    .concat(DIAMOND_SIZE_SLIDE_GROUPS);

  // ── DOM refs ───────────────────────────────────────────
  let intro, builder, backBtn, nextBtn, progress, atcWrap, tabsNav;
  let introNameInput, ringNameLabel;
  let reviewBackBtn, engravingInput, engravingCount, resetViewBtn;
  let rightPanel, collapsiblePanels;

  // ── THREE.js ───────────────────────────────────────────
  let threeReady = false;
  var ensureThreeLoadAttempts = 0;
  let loader, model;
  let dracoLoader = null;
  let ktx2Loader = null;
  let canvasContainer = null;
  let componentRoot = null;
  let componentModels = { band: null, bandGems: null, setting: null, settingGems: null, diamond: null };
  let componentRequestToken = 0;
  let displayUpdateToken = 0;
  /** After first assembly load, keep OrbitControls position/target when swapping GLBs. */
  var rbAssemblyCameraInitialised = false;
  var rbShapeFrontFramePending = false;
  /** Refreshed when diamond-size stops are rebuilt from 3D model carats. */
  var diamondSizeDragApi = null;
  /**
   * Parsed GLTF templates per URL (session). Already avoids repeat **downloads**; clones still
   * allocate GPU memory per active assembly. Cap + LRU evicts least-recently-used templates so
   * browsing many unique assets does not retain every parsed scene forever (lowers session RAM).
   * Optional: `window.BMRB_MODEL_CACHE_MAX` (number, min 8) before `ensureThree()` runs.
   */
  var RB_MODEL_CACHE_MAX =
    typeof window !== 'undefined' &&
      window.BMRB_MODEL_CACHE_MAX &&
      typeof window.BMRB_MODEL_CACHE_MAX === 'number' &&
      Number.isFinite(window.BMRB_MODEL_CACHE_MAX)
      ? Math.max(8, Math.min(128, Math.floor(window.BMRB_MODEL_CACHE_MAX)))
      : 28;
  let modelLoadPromiseCache = {};
  let modelLoadCacheOrder = [];

  function touchModelLoadCache(url) {
    if (!url) return;
    var order = modelLoadCacheOrder;
    var i = order.indexOf(url);
    if (i >= 0) order.splice(i, 1);
    order.push(url);
  }

  function evictOneModelLoadCache() {
    var victim = modelLoadCacheOrder.shift();
    if (victim && Object.prototype.hasOwnProperty.call(modelLoadPromiseCache, victim)) {
      delete modelLoadPromiseCache[victim];
    }
  }

  let modelMaterialRefs = { metal: [], diamond: [], metalByComponent: { setting: [], band: [] } };
  let qualityPresetName = 'balanced';
  let qualityPreset = QUALITY_PRESETS.balanced;
  let taggingConfig = DEFAULT_TAGGING_CONFIG;
  let collectionTuning = null;

  /** R3F: run after React commits the primitive (double rAF). */
  function rbR3fRunAfterPaint(fn) {
    if (typeof requestAnimationFrame !== 'function') {
      setTimeout(fn, 0);
      return;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(fn);
    });
  }

  /**
   * Fully frees GPU memory for a detached Object3D subtree (geometries, materials, textures).
   * Skips materials tagged `rbRingBuilderSharedMaterial` (R3F gem proxy — global singleton).
   * Uses WeakSets so shared geometries/materials/textures within the subtree are disposed once.
   */
  function disposeModelResources(root) {
    if (!root) return;
    var disposedGeometry = new WeakSet();
    var disposedMaterial = new WeakSet();
    var disposedTexture = new WeakSet();

    function disposeTexture(tex) {
      if (!tex || !tex.isTexture || disposedTexture.has(tex)) return;
      disposedTexture.add(tex);
      if (typeof tex.dispose === 'function') tex.dispose();
    }

    function disposeMaterialDeep(material) {
      if (!material || typeof material.dispose !== 'function') return;
      if (disposedMaterial.has(material)) return;
      if (material.userData && material.userData.rbRingBuilderSharedMaterial) return;
      disposedMaterial.add(material);

      var texKeys = [
        'map',
        'lightMap',
        'bumpMap',
        'normalMap',
        'specularMap',
        'alphaMap',
        'aoMap',
        'displacementMap',
        'emissiveMap',
        'roughnessMap',
        'metalnessMap',
        'clearcoatMap',
        'clearcoatNormalMap',
        'sheenColorMap',
        'transmissionMap',
        'thicknessMap'
      ];
      var k;
      for (k = 0; k < texKeys.length; k += 1) {
        var tk = texKeys[k];
        if (material[tk] && material[tk].isTexture) disposeTexture(material[tk]);
      }

      if (material.uniforms) {
        Object.keys(material.uniforms).forEach(function (uk) {
          if (uk === 'envMap') return;
          var u = material.uniforms[uk];
          if (u && u.value && u.value.isTexture) disposeTexture(u.value);
        });
      }

      material.dispose();
    }

    root.traverse(function (node) {
      if (!node || !node.isMesh) return;

      /** Gem refraction uses a **cloned** geometry in R3F (`GemRingBuilderOverlay`); proxy buffers are safe to dispose. */

      if (node.geometry && !disposedGeometry.has(node.geometry)) {
        disposedGeometry.add(node.geometry);
        if (typeof node.geometry.dispose === 'function') node.geometry.dispose();
      }

      var materials = Array.isArray(node.material) ? node.material : [node.material];
      materials.forEach(disposeMaterialDeep);
    });
  }

  /** After clearing the R3F primitive so React does not touch disposed buffers. */
  function scheduleDisposeModelResources(root) {
    if (!root) return;
    rbR3fRunAfterPaint(function () {
      disposeModelResources(root);
    });
  }

  // ── Carousel ──────────────────────────────────────────
  let rbSlideshows = {};
  let activeSlideGroup = 'setting-halo';
  /* Video-aware group handoff: while switching into a slideshow group whose
     active video hasn't presented a frame yet, the OUTGOING group is kept
     visible (`rbHandoffOutgoing`) underneath the incoming one until the new
     video can actually paint. `rbHandoffToken` invalidates a pending handoff
     when a newer switch (or a full hide) supersedes it. */
  let rbHandoffToken = 0;
  let rbHandoffOutgoing = null;
  let rbHandoffActive = false;

  // ══════════════════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════════════════
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function deepMerge(base, source) {
    if (!source || typeof source !== 'object') return base;
    var output = Array.isArray(base) ? base.slice() : Object.assign({}, base);
    Object.keys(source).forEach(function (key) {
      var sourceValue = source[key];
      var baseValue = output[key];
      if (Array.isArray(sourceValue)) {
        output[key] = sourceValue.slice();
        return;
      }
      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        baseValue &&
        typeof baseValue === 'object' &&
        !Array.isArray(baseValue)
      ) {
        output[key] = deepMerge(baseValue, sourceValue);
        return;
      }
      output[key] = sourceValue;
    });
    return output;
  }

  function getRingAssetsConfig() {
    return window.RingAssets || {};
  }

  function normalizeTierName(tierName) {
    if (!tierName) return null;
    var normalized = String(tierName).trim().toLowerCase();
    if (normalized === 'ultra') return 'premium';
    if (normalized === 'high') return 'premium';
    if (normalized === 'low') return 'mobile';
    return normalized;
  }

  function detectAutoQualityTier() {
    var lowWidth = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    var lowMemory = navigator.deviceMemory && navigator.deviceMemory <= 4;
    var lowCpu = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
    if (lowWidth || lowMemory || lowCpu) return 'mobile';
    return 'balanced';
  }

  function resolveQualityPreset() {
    var assets = getRingAssetsConfig();
    var requested = normalizeTierName(assets.qualityTier || assets.quality);
    var selectedTier = requested && QUALITY_PRESETS[requested] ? requested : detectAutoQualityTier();
    qualityPresetName = selectedTier;
    qualityPreset = deepMerge(QUALITY_PRESETS.balanced, QUALITY_PRESETS[selectedTier] || QUALITY_PRESETS.balanced);
  }

  function resolveCollectionTuning() {
    var assets = getRingAssetsConfig();
    var collectionName = assets.collection || assets.collectionName || '';
    var presets = assets.collectionPresets || {};
    var preset = collectionName && presets[collectionName] ? presets[collectionName] : {};
    collectionTuning = deepMerge({}, preset || {});
  }

  function resolveTaggingConfig() {
    var assets = getRingAssetsConfig();
    taggingConfig = deepMerge(DEFAULT_TAGGING_CONFIG, assets.tagging || {});
  }

  function getCollectionSetting(path, fallbackValue) {
    if (!collectionTuning || typeof collectionTuning !== 'object') return fallbackValue;

    var keys = Array.isArray(path) ? path : [path];
    var value = collectionTuning;
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      if (!value || typeof value !== 'object' || !Object.prototype.hasOwnProperty.call(value, key)) {
        return fallbackValue;
      }
      value = value[key];
    }
    return value === undefined ? fallbackValue : value;
  }

  function getSceneBackgroundHex() {
    var assets = getRingAssetsConfig();
    var directValue = null;
    if (assets.scene && assets.scene.backgroundColor) {
      directValue = assets.scene.backgroundColor;
    } else if (assets.backgroundColor) {
      directValue = assets.backgroundColor;
    }
    var value = directValue !== null ? directValue : getCollectionSetting(['scene', 'backgroundColor'], getCollectionSetting('backgroundColor', 0xeff2e6));
    return toColorHex(value, 0xeff2e6);
  }

  function getToneMappingExposure() {
    var baseExposure = qualityPreset && qualityPreset.toneMappingExposure ? qualityPreset.toneMappingExposure : 0.9;
    var collectionExposure = getCollectionSetting('toneMappingExposure', null);
    if (collectionExposure === null) {
      collectionExposure = getCollectionSetting(['render', 'toneMappingExposure'], null);
    }
    return collectionExposure !== null ? numberOr(collectionExposure, numberOr(baseExposure, 0.9)) : numberOr(baseExposure, 0.9);
  }

  function getLightingPreset() {
    var base = {
      fillLightIntensity: qualityPreset && qualityPreset.fillLightIntensity ? qualityPreset.fillLightIntensity : 0.12,
      keyLightIntensity: qualityPreset && qualityPreset.keyLightIntensity ? qualityPreset.keyLightIntensity : 0.9,
      shadowMapSize: qualityPreset && qualityPreset.shadowMapSize ? qualityPreset.shadowMapSize : 1024,
      shadowOpacity: 0.1
    };

    var collectionLighting = getCollectionSetting('lighting', {});
    return deepMerge(base, collectionLighting || {});
  }

  function getBloomPreset() {
    var base = qualityPreset && qualityPreset.bloom ? deepMerge({}, qualityPreset.bloom) : {
      enabled: true, strength: 0.08, radius: 0.35, threshold: 0.93
    };
    var collectionBloom = getCollectionSetting('bloom', {});
    return deepMerge(base, collectionBloom || {});
  }

  function getViewPreset() {
    var base = deepMerge({}, DEFAULT_VIEW_PRESET);
    var collectionView = getCollectionSetting('view', {});
    var assetsView = getRingAssetsConfig().view || {};
    return deepMerge(deepMerge(base, collectionView || {}), assetsView || {});
  }

  function toLookupKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  function numberOr(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clampNumber(value, min, max, fallback) {
    var parsed = numberOr(value, fallback);
    if (parsed < min) return min;
    if (parsed > max) return max;
    return parsed;
  }

  function sanitiseMetalPreset(preset) {
    var out = deepMerge({}, preset || {});
    out.metalness = clampNumber(
      out.metalness,
      METAL_REALISM_LIMITS.metalnessMin,
      METAL_REALISM_LIMITS.metalnessMax,
      0.97
    );
    out.roughness = clampNumber(
      out.roughness,
      METAL_REALISM_LIMITS.roughnessMin,
      METAL_REALISM_LIMITS.roughnessMax,
      0.26
    );
    out.clearcoat = clampNumber(
      out.clearcoat,
      METAL_REALISM_LIMITS.clearcoatMin,
      METAL_REALISM_LIMITS.clearcoatMax,
      0.68
    );
    out.clearcoatRoughness = clampNumber(
      out.clearcoatRoughness,
      METAL_REALISM_LIMITS.clearcoatRoughnessMin,
      METAL_REALISM_LIMITS.clearcoatRoughnessMax,
      0.1
    );
    out.envMapIntensity = clampNumber(
      out.envMapIntensity,
      METAL_REALISM_LIMITS.envMapIntensityMin,
      METAL_REALISM_LIMITS.envMapIntensityMax,
      0.9
    );
    return out;
  }

  function mergeTransform(baseTransform, overrideTransform) {
    return deepMerge(baseTransform || {}, overrideTransform || {});
  }

  function isValidRingBuilderModelUrl(url) {
    if (!url || typeof url !== 'string') return false;
    var u = url.trim();
    if (!u || u === 'https:' || u === 'http:') return false;
    try {
      var parsed = new URL(u, typeof window !== 'undefined' ? window.location.href : 'https://example.com');
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (e) {
      return false;
    }
  }

  function getRingBuilderModelCdnBase() {
    var root = document.getElementById('ring-builder');
    var fromDom = root && root.getAttribute('data-model-cdn-base');
    var fromAssets = window.RingAssets && window.RingAssets.modelCdnBase;
    var base = (fromAssets || fromDom || '').trim().replace(/\/$/, '');
    if (!base && window.RingAssets) {
      base = 'https://assets.bondedmatter.com/media/rings';
    }
    if (window.RingAssets) window.RingAssets.modelCdnBase = base;
    return base;
  }

  function settingNameToFilePrefix(name) {
    return String(name || '')
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }

  function escapeRegExp(str) {
    return String(str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function cleanSettingFilePrefix(prefix) {
    return String(prefix || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/_+$/g, '');
  }

  /** Cached per setting prefix after scanning byShapeCarat keys or a successful HEAD probe. */
  var settingNamingCache = {};
  var settingNamingProbePromises = {};
  /** Prefixes we've already kicked a naming probe for, so the render gate defers at most once. */
  var settingNamingProbeAttempted = {};

  /** Cached band filename style per setting prefix (see buildBandMetalFileKey). */
  var settingBandStyleCache = {};
  var settingBandStyleProbePromises = {};
  var settingBandStyleProbeAttempted = {};

  /**
   * Newer settings (e.g. Bezel) prefix component filenames and double the setting prefix on
   * setting-metal keys (`bezel_bezel_round_…`). Halo keeps the legacy single-prefix layout.
   */
  function inferSettingNamingFromKeys(byShapeCarat, settingPrefix) {
    var naming = { metalDoubled: false, componentPrefix: false };
    var p = cleanSettingFilePrefix(settingPrefix);
    if (!p || !byShapeCarat || typeof byShapeCarat !== 'object') return naming;
    var doubledRe = new RegExp('^' + escapeRegExp(p) + '_' + escapeRegExp(p) + '_');
    var gemRe = new RegExp('^' + escapeRegExp(p) + '_gem_');
    Object.keys(byShapeCarat).forEach(function (key) {
      var k = String(key).toLowerCase().replace(/\.glb$/, '');
      if (doubledRe.test(k)) naming.metalDoubled = true;
      if (gemRe.test(k)) naming.componentPrefix = true;
      if (k.indexOf(p + '_') === 0 && k.indexOf('_band_') !== -1) naming.componentPrefix = true;
    });
    if (naming.metalDoubled) naming.componentPrefix = true;
    return naming;
  }

  function rememberSettingNaming(settingPrefix, naming) {
    var key = toLookupKey(settingPrefix);
    if (!key || !naming) return;
    settingNamingCache[key] = {
      metalDoubled: !!naming.metalDoubled,
      componentPrefix: !!naming.componentPrefix
    };
  }

  /**
   * Can we trust this inference enough to cache it (and skip the HEAD probe)?
   * - A doubled metal key was seen  -> definitively doubled.
   * - Keys exist with no prefixing  -> definitively legacy (e.g. Halo).
   * - Components are prefixed but NO doubled metal key is present -> AMBIGUOUS: the
   *   metaobject may only list gem keys (`bezel_gem_…`) while the metal file is the
   *   doubled `bezel_bezel_…`. Caching `metalDoubled:false` here would synthesise
   *   `bezel_round_…` (404) and the setting would silently drop out. Leave uncached
   *   so `probeSettingNamingStyle` HEAD-checks the real metal file.
   */
  function settingNamingIsConfident(naming, byShapeCarat) {
    if (!naming) return false;
    if (naming.metalDoubled) return true;
    var keys = byShapeCarat && typeof byShapeCarat === 'object' ? Object.keys(byShapeCarat) : [];
    if (!keys.length) return false;
    return !naming.componentPrefix;
  }

  function rememberSettingNamingFromKeysIfConfident(settingPrefix, byShapeCarat) {
    var naming = inferSettingNamingFromKeys(byShapeCarat, settingPrefix);
    if (settingNamingIsConfident(naming, byShapeCarat)) {
      rememberSettingNaming(settingPrefix, naming);
    }
  }

  function getSettingNamingStyle(settingPrefix, rawSetting) {
    var cacheKey = toLookupKey(settingPrefix);
    if (cacheKey && settingNamingCache[cacheKey]) return settingNamingCache[cacheKey];
    var byShapeCarat = rawSetting && rawSetting.byShapeCarat;
    var naming = inferSettingNamingFromKeys(byShapeCarat, settingPrefix);
    if (settingNamingIsConfident(naming, byShapeCarat)) {
      rememberSettingNaming(settingPrefix, naming);
    }
    return naming;
  }

  /**
   * Band style for a setting. Returns the cached HEAD-probed value when known;
   * otherwise falls back to the metal naming style so Bezel/Halo keep their existing
   * shape+carat band keys until the probe confirms. (Solitaire-style shape/carat-less
   * bands can only be detected by the probe, never inferred from naming.)
   */
  function getSettingBandStyle(settingPrefix, rawSetting) {
    var cacheKey = toLookupKey(settingPrefix);
    if (cacheKey && settingBandStyleCache[cacheKey]) return settingBandStyleCache[cacheKey];
    var naming = getSettingNamingStyle(settingPrefix, rawSetting);
    // Halo (no componentPrefix) is unambiguous → shapeCarat. componentPrefix settings
    // need the probe to distinguish Bezel (prefixShapeCarat) from Solitaire
    // (prefixNoShapeCarat); return null until the probe resolves.
    if (naming && naming.componentPrefix) return null;
    return 'shapeCarat';
  }

  /**
   * HEAD-probe which band filename style a setting uses. Tries, in order:
   *   1. `<p>_band_<profile>_<thickness>`                    (Solitaire: no shape/carat)
   *   2. `<p>_<shape>_band_<profile>_<thickness>_<carat>_ct` (Bezel: prefixed shape+carat)
   *   3. `<shape>_band_<profile>_<thickness>_<carat>_ct`     (Halo: shape+carat)
   * Reference tokens use the current selection when present, else round/1.00/straight/thin.
   */
  function probeSettingBandStyle(settingValue) {
    var prefix = settingNameToFilePrefix(settingValue || state.setting);
    var cacheKey = toLookupKey(prefix);
    if (!prefix || !cacheKey) return Promise.resolve();
    if (settingBandStyleCache[cacheKey]) return Promise.resolve();
    if (settingBandStyleProbePromises[cacheKey]) return settingBandStyleProbePromises[cacheKey];

    var base = getRingBuilderModelCdnBase();
    if (!base) return Promise.resolve();

    var shape = normalizeShapeToken(state.shape) || 'round';
    var profile = bandProfileToFileToken(state.band_profile) || 'straight';
    var thickness = bandThicknessToFileToken(state.band_thickness) || 'thin';
    var caratTok = caratToFileToken(state.diamond_size) || '1_00';

    var candidates = [
      { style: 'prefixNoShapeCarat', key: prefix + '_band_' + profile + '_' + thickness },
      { style: 'prefixShapeCarat', key: prefix + '_' + shape + '_band_' + profile + '_' + thickness + '_' + caratTok + '_ct' },
      { style: 'shapeCarat', key: shape + '_band_' + profile + '_' + thickness + '_' + caratTok + '_ct' }
    ];

    function tryIdx(i) {
      if (i >= candidates.length) return;
      var url = buildR2ModelUrl(base, candidates[i].key, '');
      return fetch(url, { method: 'HEAD', mode: 'cors' })
        .then(function (res) {
          if (res.ok) {
            settingBandStyleCache[cacheKey] = candidates[i].style;
            return;
          }
          return tryIdx(i + 1);
        })
        .catch(function () { return tryIdx(i + 1); });
    }

    var chain = Promise.resolve().then(function () { return tryIdx(0); });
    settingBandStyleProbePromises[cacheKey] = chain;
    return chain;
  }

  /** HEAD-probe doubled vs legacy setting-metal keys (e.g. bezel_bezel_round vs bezel_round). */
  function probeSettingNamingStyle(settingValue) {
    var prefix = settingNameToFilePrefix(settingValue || state.setting);
    var cacheKey = toLookupKey(prefix);
    if (!prefix) return Promise.resolve();
    if (settingNamingCache[cacheKey]) return Promise.resolve();
    if (settingNamingProbePromises[cacheKey]) return settingNamingProbePromises[cacheKey];

    var base = getRingBuilderModelCdnBase();
    if (!base) return Promise.resolve();

    var doubledKey = buildSettingMetalFileKey(prefix, 'round', '1_00', {
      metalDoubled: true,
      componentPrefix: true
    });
    var singleKey = buildSettingMetalFileKey(prefix, 'round', '1_00', {
      metalDoubled: false,
      componentPrefix: false
    });

    var chain = Promise.resolve();
    if (doubledKey && doubledKey !== singleKey) {
      chain = fetch(buildR2ModelUrl(base, doubledKey, prefix), { method: 'HEAD', mode: 'cors' })
        .then(function (res) {
          if (res.ok) {
            rememberSettingNaming(prefix, { metalDoubled: true, componentPrefix: true });
            return;
          }
          return fetch(buildR2ModelUrl(base, singleKey, prefix), { method: 'HEAD', mode: 'cors' }).then(function (res2) {
            if (res2.ok) {
              rememberSettingNaming(prefix, { metalDoubled: false, componentPrefix: false });
            }
          });
        })
        .catch(function () { /* ignore probe errors */ });
    }

    settingNamingProbePromises[cacheKey] = chain;
    return chain;
  }

  function listSettingMetalKeys(rawSetting, settingPrefix) {
    if (!rawSetting || !rawSetting.byShapeCarat) return [];
    return Object.keys(rawSetting.byShapeCarat).filter(function (k) {
      return isSettingMetalModelKey(k, settingPrefix);
    });
  }

  function pickPreferredSettingMetalKey(metalKeys, settingPrefix) {
    if (!metalKeys || !metalKeys.length) return null;
    for (var i = 0; i < metalKeys.length; i++) {
      var parsed = parseShapeCaratFromKey(metalKeys[i], settingPrefix);
      if (parsed && normalizeShapeFilenameToken(parsed.shape) === 'round' && parsed.carat === '1.00') {
        return metalKeys[i];
      }
    }
    return metalKeys[0];
  }

  function syncSettingTopLevelUrl(raw, settingPrefix, base) {
    if (!raw || !raw.byShapeCarat || !base) return;
    var metalKeys = listSettingMetalKeys(raw, settingPrefix);
    if (!metalKeys.length) return;
    var pickKey = pickPreferredSettingMetalKey(metalKeys, settingPrefix);
    if (!pickKey) return;
    var picked = unwrapAssetEntry(raw.byShapeCarat[pickKey]);
    if (picked && isValidRingBuilderModelUrl(picked.url)) {
      raw.url = picked.url;
    }
  }

  function resolveSettingMetalAsset(rawSetting, settingPrefix) {
    if (!rawSetting) return null;
    var prefix = settingPrefix || settingNameToFilePrefix(state.setting);
    ensureShapeAndCaratDefaults();

    var fromShapeCarat = resolveByShapeCarat(
      rawSetting,
      state.shape,
      state.diamond_size,
      prefix,
      { metalOnly: true }
    );
    if (fromShapeCarat) return fromShapeCarat;

    var metalKeys = listSettingMetalKeys(rawSetting, prefix);
    if (metalKeys.length) {
      var pickKey = pickPreferredSettingMetalKey(metalKeys, prefix);
      var fromMap = unwrapAssetEntry(rawSetting.byShapeCarat[pickKey]);
      if (fromMap && fromMap.url) return fromMap;
    }

    var synth = synthesizeShapeCaratAsset(state.shape, state.diamond_size, {
      kind: 'setting',
      settingPrefix: prefix
    });
    if (synth) return synth;

    return unwrapAssetEntry(rawSetting);
  }

  function buildSettingMetalFileKey(settingPrefix, shapeSlug, caratTok, naming) {
    var p = cleanSettingFilePrefix(settingPrefix);
    if (!p || !shapeSlug || !caratTok) return '';
    if (naming && naming.metalDoubled) {
      return p + '_' + p + '_' + shapeSlug + '_' + caratTok + '_ct';
    }
    // Halo's sub-1CT setting metal was exported doubled (`halo_halo_...`), while
    // its 1CT+ files are single-prefix — so naming detection (correctly) resolves
    // metalDoubled=false from the 1CT files. Force the doubled stem for Halo at
    // 0.5ct/0.75ct only; 1CT+ and every other setting are unchanged.
    if (p === 'halo' && (caratTok === '0_50' || caratTok === '0_75')) {
      return p + '_' + p + '_' + shapeSlug + '_' + caratTok + '_ct';
    }
    return p + '_' + shapeSlug + '_' + caratTok + '_ct';
  }

  function buildDiamondFileKey(settingPrefix, shapeSlug, caratTok, naming) {
    if (!shapeSlug || !caratTok) return '';
    if (naming && naming.componentPrefix) {
      var p = cleanSettingFilePrefix(settingPrefix);
      if (!p) return '';
      return p + '_gem_' + shapeSlug + '_' + caratTok + '_ct';
    }
    // Halo's sub-1CT main diamonds were exported with a `halo_gem_` prefix,
    // while 1CT and above use the standard unprefixed `gem_` stem. Only the
    // 0.5ct / 0.75ct Halo keys need the prefix; everything else is unchanged.
    if (cleanSettingFilePrefix(settingPrefix) === 'halo'
        && (caratTok === '0_50' || caratTok === '0_75')) {
      return 'halo_gem_' + shapeSlug + '_' + caratTok + '_ct';
    }
    return 'gem_' + shapeSlug + '_' + caratTok + '_ct';
  }

  /**
   * Setting accent gems (e.g. a hidden halo's encircling diamonds). The file stem
   * mirrors the setting-metal naming style — settings that double their metal prefix
   * also double it on the gems stem — but the R2 folder is always the single-prefix
   * `<p>_gems` (e.g. file `hidden_halo_hidden_halo_gems_round_1_00_ct.glb` lives in
   * the `hidden_halo_gems/` folder). The folder collapse is handled in
   * resolveR2FolderForKey.
   */
  function buildSettingGemsFileKey(settingPrefix, shapeSlug, caratTok, naming) {
    var p = cleanSettingFilePrefix(settingPrefix);
    if (!p || !shapeSlug || !caratTok) return '';
    if (naming && naming.metalDoubled) {
      return p + '_' + p + '_gems_' + shapeSlug + '_' + caratTok + '_ct';
    }
    // Halo's sub-1CT setting gems were exported doubled (`halo_halo_gems_...`),
    // while its 1CT+ files are single-prefix. Force the doubled stem for Halo at
    // 0.5ct/0.75ct only; 1CT+ and every other setting are unchanged. The folder
    // collapse to `halo_gems/` is handled by resolveR2FolderForKey.
    if (p === 'halo' && (caratTok === '0_50' || caratTok === '0_75')) {
      return p + '_' + p + '_gems_' + shapeSlug + '_' + caratTok + '_ct';
    }
    return p + '_gems_' + shapeSlug + '_' + caratTok + '_ct';
  }

  /**
   * Band filenames come in three observed styles (a band is independent of the metal
   * naming style — Bezel & Solitaire both double the setting-metal prefix, yet Bezel's
   * band carries shape+carat while Solitaire's band carries neither):
   *   - 'prefixNoShapeCarat' : `<p>_band_<profile>_<thickness>`                       (Solitaire)
   *   - 'prefixShapeCarat'   : `<p>_<shape>_band_<profile>_<thickness>_<carat>_ct`     (Bezel)
   *   - 'shapeCarat'         : `<shape>_band_<profile>_<thickness>_<carat>_ct`         (Halo)
   * The style is HEAD-probed per setting (see probeSettingBandStyle) and cached.
   */
  function buildBandMetalFileKey(settingPrefix, shapeSlug, profile, thickness, caratTok, bandStyle) {
    if (!profile || !thickness) return '';
    var p = cleanSettingFilePrefix(settingPrefix);
    if (bandStyle === 'prefixNoShapeCarat') {
      if (!p) return '';
      return p + '_band_' + profile + '_' + thickness;
    }
    if (!shapeSlug || !caratTok) return '';
    var stem = shapeSlug + '_band_' + profile + '_' + thickness + '_' + caratTok + '_ct';
    if (bandStyle === 'prefixShapeCarat') {
      if (!p) return '';
      return p + '_' + stem;
    }
    // Halo's sub-1CT bands were exported with a `halo_` prefix, while its 1CT+
    // bands (and thus the probed shapeCarat style) are unprefixed. Force the
    // prefix for Halo at 0.5ct/0.75ct only; 1CT+ and other settings unchanged.
    if (p === 'halo' && (caratTok === '0_50' || caratTok === '0_75')) {
      return p + '_' + stem;
    }
    return stem;
  }

  function buildBandGemsFileKey(settingPrefix, shapeSlug, thickness, caratTok, bandStyle) {
    if (!thickness) return '';
    var p = cleanSettingFilePrefix(settingPrefix);
    if (bandStyle === 'prefixNoShapeCarat') {
      if (!p) return '';
      return p + '_band_gems_' + thickness;
    }
    if (!shapeSlug || !caratTok) return '';
    var stem = shapeSlug + '_band_gems_' + thickness + '_' + caratTok + '_ct';
    if (bandStyle === 'prefixShapeCarat') {
      if (!p) return '';
      return p + '_' + stem;
    }
    // Halo's sub-1CT band gems carry a `halo_` prefix (its 1CT+ band gems, and
    // thus the probed shapeCarat style, do not). Force the prefix for Halo at
    // 0.5ct/0.75ct only; 1CT+ and every other setting are unchanged.
    if (p === 'halo' && (caratTok === '0_50' || caratTok === '0_75')) {
      return p + '_' + stem;
    }
    return stem;
  }

  function isSettingMetalModelKey(key, settingPrefix) {
    var k = String(key || '').toLowerCase().replace(/\.glb$/, '');
    var p = cleanSettingFilePrefix(settingPrefix);
    if (!p || !isSettingShapeModelKey(k, p)) return false;
    // Main-gem keys accidentally listed on the setting (bezel_gem_round_…) are not setting metal.
    if (k.indexOf(p + '_gem_') === 0) return false;
    return true;
  }

  function resolveR2FolderForKey(fileKey, settingPrefix) {
    var k = String(fileKey).toLowerCase().replace(/\.glb$/, '');
    if (k.indexOf('gem_') !== -1) return 'main_gem';
    if (k.indexOf('band_gems') !== -1) return 'band_gems';
    if (k.indexOf('_band_') !== -1) return 'band';
    var gemsParts = k.split('_gems_');
    if (gemsParts.length > 1) return collapseDoubledPrefix(gemsParts[0]) + '_gems';
    if (settingPrefix) return settingPrefix;
    return '';
  }

  // Setting accent-gem files double the metal prefix on the stem
  // (`hidden_halo_hidden_halo_gems_…`) but live in the single-prefix folder
  // (`hidden_halo_gems/`). Collapse an exact `X_X` repetition back to `X`.
  function collapseDoubledPrefix(s) {
    var str = String(s || '');
    var parts = str.split('_');
    if (parts.length >= 2 && parts.length % 2 === 0) {
      var half = parts.length / 2;
      var a = parts.slice(0, half).join('_');
      var b = parts.slice(half).join('_');
      if (a && a === b) return a;
    }
    return str;
  }

  function buildR2ModelUrl(base, fileKey, settingPrefix) {
    if (!base || !fileKey) return '';
    var key = String(fileKey).toLowerCase().replace(/\.glb$/, '');
    var folder = resolveR2FolderForKey(key, settingPrefix);
    var glbName = key + '.glb';
    return folder ? base + '/' + folder + '/' + glbName : base + '/' + glbName;
  }

  function hydrateByShapeCaratMap(map, base, settingPrefix) {
    if (!map || typeof map !== 'object' || !base) return;
    Object.keys(map).forEach(function (key) {
      var entry = map[key];
      if (!entry || typeof entry !== 'object') return;
      if (!isValidRingBuilderModelUrl(entry.url)) {
        entry.url = buildR2ModelUrl(base, key, settingPrefix);
      }
    });
  }

  function hydrateRingAssetsR2Urls() {
    var base = getRingBuilderModelCdnBase();
    if (!base) {
      console.warn('Ring Builder: model CDN base URL is not set — 3D GLBs will not load.');
      return;
    }
    var assets = window.RingAssets;
    if (!assets || !assets.components) return;
    var components = assets.components;

    if (components.setting && components.setting.bySetting) {
      Object.keys(components.setting.bySetting).forEach(function (settingName) {
        var raw = components.setting.bySetting[settingName];
        if (!raw || typeof raw !== 'object') return;
        var prefix = settingNameToFilePrefix(settingName);
        if (raw.byShapeCarat) {
          hydrateByShapeCaratMap(raw.byShapeCarat, base, prefix);
          rememberSettingNamingFromKeysIfConfident(prefix, raw.byShapeCarat);
          syncSettingTopLevelUrl(raw, prefix, base);
        }
      });
    }

    if (components.diamond && components.diamond.byShape) {
      Object.keys(components.diamond.byShape).forEach(function (shapeName) {
        var raw = components.diamond.byShape[shapeName];
        if (!raw || typeof raw !== 'object') return;
        if (raw.byShapeCarat) hydrateByShapeCaratMap(raw.byShapeCarat, base, '');
        if (!isValidRingBuilderModelUrl(raw.url)) {
          var token = settingNameToFilePrefix(shapeName);
          raw.url = buildR2ModelUrl(base, 'gem_' + token + '_1_00_ct', '');
        }
      });
    }

    if (components.settingGems && components.settingGems.byShape) {
      Object.keys(components.settingGems.byShape).forEach(function (shapeName) {
        var raw = components.settingGems.byShape[shapeName];
        if (!raw || typeof raw !== 'object' || !raw.byShapeCarat) return;
        hydrateByShapeCaratMap(raw.byShapeCarat, base, '');
      });
    }

    if (components.band) {
      var bandProfiles = {
        Classic: 'round_band_straight_medium_1_00_ct',
        Rounded: 'round_band_rounded_medium_1_00_ct',
        'Diamond Band': 'round_band_pavee_medium_1_00_ct'
      };
      if (!components.band.byProfile) components.band.byProfile = {};
      Object.keys(bandProfiles).forEach(function (profileName) {
        var entry = components.band.byProfile[profileName];
        if (!entry || typeof entry !== 'object') {
          entry = { tags: { defaultRole: 'metal' } };
          components.band.byProfile[profileName] = entry;
        }
        if (!isValidRingBuilderModelUrl(entry.url)) {
          entry.url = buildR2ModelUrl(base, bandProfiles[profileName], '');
        }
      });
      if (!isValidRingBuilderModelUrl(components.band.default)) {
        components.band.default = buildR2ModelUrl(base, bandProfiles.Classic, '');
      }
    }

    if (components.bandGems && components.bandGems.byProfile) {
      var paveeGems = components.bandGems.byProfile['Diamond Band'];
      if (!paveeGems || typeof paveeGems !== 'object') {
        paveeGems = { tags: { defaultRole: 'diamond' }, urls: [] };
        components.bandGems.byProfile['Diamond Band'] = paveeGems;
      }
      if (!Array.isArray(paveeGems.urls) || !paveeGems.urls.length ||
        paveeGems.urls.every(function (u) { return !isValidRingBuilderModelUrl(u); })) {
        // Single-file pavé run (the per-stone `…_ct_<idx>` files are gone). Keep a
        // one-element array so any array-path consumer still loads a real GLB; the
        // synthesiser is authoritative and supplies the shape/carat-correct file.
        paveeGems.urls = [buildR2ModelUrl(base, 'round_band_gems_medium_1_00_ct', '')];
      }
    }

    if (components.diamond && !isValidRingBuilderModelUrl(components.diamond.default)) {
      components.diamond.default = buildR2ModelUrl(base, 'gem_round_1_00_ct', '');
    }
  }

  function caratToFileToken(caratRaw) {
    var normalized = normalizeCaratKey(caratRaw);
    if (!normalized) return '';
    var parts = normalized.split('.');
    if (parts.length < 2) return '';
    return parts[0] + '_' + parts[1];
  }

  function bandProfileToFileToken(profileRaw) {
    var k = toLookupKey(profileRaw);
    if (!k) return 'straight';
    if (k.indexOf('diamond') !== -1 || k.indexOf('pave') !== -1) return 'pavee';
    if (k.indexOf('round') !== -1) return 'rounded';
    if (k.indexOf('classic') !== -1 || k.indexOf('straight') !== -1) return 'straight';
    return k.replace(/[^a-z0-9]+/g, '');
  }

  function bandThicknessToFileToken(thicknessRaw) {
    var k = toLookupKey(thicknessRaw);
    if (!k) return 'medium';
    if (k.indexOf('thin') !== -1) return 'thin';
    if (k.indexOf('bold') !== -1 || k.indexOf('thick') !== -1) return 'bold';
    if (k.indexOf('medium') !== -1) return 'medium';
    return k.replace(/[^a-z0-9]+/g, '');
  }

  function isDiamondBandProfile(profileRaw) {
    var k = toLookupKey(profileRaw);
    return k.indexOf('diamond') !== -1 || k.indexOf('pave') !== -1;
  }

  function synthesizeBandAsset(kind) {
    var base = getRingBuilderModelCdnBase();
    if (!base) return null;
    var shape = normalizeShapeToken(state.shape);
    var caratTok = caratToFileToken(state.diamond_size);
    var thickness = bandThicknessToFileToken(state.band_thickness);
    // Thickness is always part of the key; shape/carat are only required by the
    // shape+carat band styles (the build helpers guard that and return '' otherwise).
    if (!thickness) return null;

    var settingPrefix = settingNameToFilePrefix(state.setting);
    var components = getComponentAssetsConfig();
    var rawSetting = components && components.setting && components.setting.bySetting
      ? pickRawSettingEntry(components.setting.bySetting, state.setting)
      : null;
    var bandStyle = getSettingBandStyle(settingPrefix, rawSetting);
    if (!bandStyle) return null;

    if (kind === 'bandGems') {
      // Band gems ship as ONE GLB containing the whole pavé run. Naming follows the
      // setting's band style: `…_band_gems_<thickness>` (Solitaire) or
      // `[<p>_]<shape>_band_gems_<thickness>_<carat>_ct` (Bezel/Halo).
      var gemsKey = buildBandGemsFileKey(settingPrefix, shape, thickness, caratTok, bandStyle);
      var gemsUrl = buildR2ModelUrl(base, gemsKey, '');
      if (!isValidRingBuilderModelUrl(gemsUrl)) return null;
      return { url: gemsUrl, tags: { defaultRole: 'diamond' } };
    }

    if (kind === 'band') {
      var profile = bandProfileToFileToken(state.band_profile);
      if (!profile) return null;
      var bandKey = buildBandMetalFileKey(settingPrefix, shape, profile, thickness, caratTok, bandStyle);
      var bandUrl = buildR2ModelUrl(base, bandKey, '');
      if (!isValidRingBuilderModelUrl(bandUrl)) return null;
      return { url: bandUrl, tags: { defaultRole: 'metal' } };
    }

    return null;
  }

  function synthesizeShapeCaratAsset(shapeRaw, caratRaw, opts) {
    opts = opts || {};
    var base = getRingBuilderModelCdnBase();
    if (!base) return null;
    var shapeSlug = opts.shapeSlug || getShapeFileSlug(shapeRaw);
    if (!shapeSlug) shapeSlug = normalizeShapeToken(shapeRaw);
    var caratTok = caratToFileToken(caratRaw);
    if (!shapeSlug || !caratTok) return null;

    var settingPrefix = opts.settingPrefix || settingNameToFilePrefix(state.setting);
    var components = getComponentAssetsConfig();
    var rawSetting = components && components.setting && components.setting.bySetting
      ? pickRawSettingEntry(components.setting.bySetting, state.setting)
      : null;
    var naming = getSettingNamingStyle(settingPrefix, rawSetting);

    var fileKey = '';
    var defaultRole = 'metal';
    var folderPrefix = settingPrefix;

    if (opts.kind === 'diamond') {
      fileKey = buildDiamondFileKey(settingPrefix, shapeSlug, caratTok, naming);
      defaultRole = 'diamond';
      folderPrefix = '';
    } else if (opts.kind === 'settingGems') {
      fileKey = buildSettingGemsFileKey(settingPrefix, shapeSlug, caratTok, naming);
      if (!fileKey && opts.settingGemsPrefix) {
        fileKey = opts.settingGemsPrefix + '_' + shapeSlug + '_' + caratTok + '_ct';
      }
      defaultRole = 'diamond';
      folderPrefix = '';
    } else {
      if (!settingPrefix) return null;
      fileKey = buildSettingMetalFileKey(settingPrefix, shapeSlug, caratTok, naming);
    }

    var url = buildR2ModelUrl(base, fileKey, folderPrefix);
    if (!isValidRingBuilderModelUrl(url)) return null;
    return { url: url, tags: { defaultRole: defaultRole } };
  }

  function unwrapAssetEntry(entry) {
    if (!entry) return null;
    if (typeof entry === 'string') {
      if (!isValidRingBuilderModelUrl(entry)) return null;
      return { url: entry };
    }
    if (typeof entry !== 'object') return null;

    if (Array.isArray(entry.urls)) {
      var validUrls = entry.urls.filter(isValidRingBuilderModelUrl);
      if (!validUrls.length) return null;
      return {
        urls: validUrls,
        transform: entry.transform || {},
        tags: entry.tags || {},
        materialProfile: entry.materialProfile || null
      };
    }

    if (typeof entry.url === 'string') {
      if (!isValidRingBuilderModelUrl(entry.url)) return null;
      return {
        url: entry.url,
        transform: entry.transform || {},
        tags: entry.tags || {},
        materialProfile: entry.materialProfile || null
      };
    }
    return null;
  }

  function findAssetMatch(map, candidates) {
    if (!map || typeof map !== 'object') return null;
    var keys = Object.keys(map);

    for (var i = 0; i < candidates.length; i += 1) {
      var candidate = candidates[i];
      if (!candidate) continue;
      if (Object.prototype.hasOwnProperty.call(map, candidate)) {
        var direct = unwrapAssetEntry(map[candidate]);
        if (direct) return direct;
      }
      var normalizedCandidate = toLookupKey(candidate);
      for (var j = 0; j < keys.length; j += 1) {
        if (toLookupKey(keys[j]) === normalizedCandidate) {
          var matched = unwrapAssetEntry(map[keys[j]]);
          if (matched) return matched;
        }
      }
    }
    return null;
  }

  // "5.0 CT" | "5" | "5_00" | "5.00" -> "5.00" (canonical 2dp string)
  function normalizeCaratKey(raw) {
    if (raw == null) return '';
    var s = String(raw).toLowerCase().replace(/ct|carat/g, '').trim();
    s = s.replace('_', '.');
    var n = parseFloat(s);
    if (!Number.isFinite(n)) return '';
    return n.toFixed(2);
  }

  // "round" | "round-brilliant" | "Princess" -> canonical shape token used in filenames
  function normalizeShapeToken(raw) {
    var k = toLookupKey(raw);
    if (!k) return 'round';
    if (k.indexOf('round') !== -1 || k.indexOf('brilliant') !== -1) return 'round';
    if (k.indexOf('princess') !== -1) return 'princess';
    if (k.indexOf('radiant') !== -1) return 'radiant';
    if (k.indexOf('pear') !== -1) return 'pear';
    if (k.indexOf('oval') !== -1) return 'oval';
    if (k.indexOf('emerald') !== -1) return 'emerald';
    if (k.indexOf('cushion') !== -1) return 'cushion';
    if (k.indexOf('marquise') !== -1) return 'marquise';
    if (k.indexOf('asscher') !== -1) return 'asscher';
    if (k.indexOf('heart') !== -1) return 'heart';
    return k.replace(/[^a-z0-9]+/g, '');
  }

  // band:      [<setting>_]<shape>_band_<profile>_<thickness>_<carat>_ct
  // band_gems: [<setting>_]<shape>_band_gems_<thickness>_<carat>_ct(_<idx>)   (no profile)
  // The `(?:^|_)` before the shape lets an optional leading <setting>_ prefix (e.g. `bezel_`)
  // pass through, so both Halo (unprefixed) and Bezel (prefixed) bands resolve their SHAPE
  // correctly instead of the token before the carat being misread as the thickness.
  function parseBandKey(key) {
    var k = String(key).toLowerCase().replace(/\.glb$/, '');
    var gem = k.match(/(?:^|_)([a-z0-9-]+)_band_gems_(thin|medium|bold)_(\d+)_(\d{2})_ct(?:_(\d+))?$/);
    if (gem) {
      return {
        shape: gem[1],
        kind: 'bandGems',
        thickness: gem[2],
        carat: gem[3] + '.' + gem[4],
        gemIndex: gem[5] != null ? parseInt(gem[5], 10) : null
      };
    }
    var band = k.match(/(?:^|_)([a-z0-9-]+)_band_(straight|rounded|pavee)_(thin|medium|bold)_(\d+)_(\d{2})_ct$/);
    if (band) {
      return {
        shape: band[1],
        kind: 'band',
        profile: band[2],
        thickness: band[3],
        carat: band[4] + '.' + band[5]
      };
    }
    return null;
  }

  // "halo_round_5_00_ct" | "halo_elongated_cushion_1_00_ct" -> { shape, carat }
  // Prefix-aware: strips the optional <setting>_ prefix (doubled for prefixed settings, e.g.
  // `bezel_bezel_<shape>`) plus the component marker (`gem_` for the diamond, `gems_` for setting
  // gems) so the shape token is recovered intact — including multi-word shapes like
  // `elongated_cushion`. Returns null unless something was stripped (callers then fall back to
  // parseShapeCaratFromKey's last-token heuristic) and null for band keys (parseBandKey owns those).
  function parseShapeCaratFromSettingModelKey(key, settingPrefix, opts) {
    opts = opts || {};
    var k = String(key).toLowerCase().replace(/\.glb$/, '');
    if (k.indexOf('_band_') !== -1) return null;
    var m = k.match(/_(\d+)_(\d{2})_ct$/);
    if (!m) return null;
    var carat = m[1] + '.' + m[2];
    var beforeCarat = k.slice(0, k.length - m[0].length);

    function cleanPrefix(p) {
      return String(p || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/_+$/g, '');
    }
    function stripLeading(slug, token) {
      if (!token) return slug;
      var withSep = token + '_';
      return slug.indexOf(withSep) === 0 ? slug.slice(withSep.length) : slug;
    }

    var shape = beforeCarat;
    var stripped = false;

    var gemsPrefix = cleanPrefix(opts.gemsPrefix);
    if (gemsPrefix) {
      var afterGems = stripLeading(shape, gemsPrefix);
      if (afterGems !== shape) { shape = afterGems; stripped = true; }
    }

    var prefix = cleanPrefix(settingPrefix);
    if (prefix) {
      var afterPrefix = stripLeading(shape, prefix);
      if (afterPrefix !== shape) {
        shape = afterPrefix;
        stripped = true;
        // Prefixed settings double up the prefix (e.g. `bezel_bezel_<shape>`).
        shape = stripLeading(shape, prefix);
      }
    }

    // `gem`/`gems` are component markers between the optional prefix and the shape, not the shape.
    var afterMarker = stripLeading(shape, 'gems');
    if (afterMarker !== shape) {
      shape = afterMarker;
      stripped = true;
    } else {
      afterMarker = stripLeading(shape, 'gem');
      if (afterMarker !== shape) { shape = afterMarker; stripped = true; }
    }

    if (!stripped || !shape) return null;
    return { shape: shape, carat: carat };
  }

  function parseShapeCaratFromKey(key, settingPrefix) {
    // Bands first — prefixed or not — so the shape isn't misread as the thickness token.
    var band = parseBandKey(key);
    if (band) return { shape: band.shape, carat: band.carat };
    // Prefix-aware parse keeps multi-word shapes intact when the prefix is known.
    var fromSetting = parseShapeCaratFromSettingModelKey(key, settingPrefix);
    if (fromSetting) return fromSetting;
    // Fallback: shape is the token immediately before the carat. Works for prefixed keys with
    // no known prefix (e.g. `bezel_gem_round_5_00_ct`, `bezel_bezel_radiant_3_00_ct`).
    var m = String(key).toLowerCase().replace(/\.glb$/, '').match(/_([a-z]+)_(\d+)_(\d{2})_ct$/);
    if (!m) return null;
    return { shape: m[1], carat: m[2] + '.' + m[3] };
  }

  function pickRawMapEntry(map, value) {
    if (!map) return null;
    var want = toLookupKey(value);
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) {
      if (toLookupKey(keys[i]) === want) return map[keys[i]];
    }
    for (var j = 0; j < keys.length; j++) {
      if (want && toLookupKey(keys[j]).indexOf(want) !== -1) return map[keys[j]];
    }
    return null;
  }

  function pickRawSettingEntry(bySetting, settingValue) {
    return pickRawMapEntry(bySetting, settingValue);
  }

  function pickRawShapeEntry(byShape, shapeValue) {
    return pickRawMapEntry(byShape, shapeValue);
  }

  function extractShapeTokenFromSettingModelKey(key, modelKeys) {
    var k = String(key).toLowerCase().replace(/\.glb$/, '');
    var caratSuffix = k.match(/_\d+_\d{2}_ct$/);
    if (!caratSuffix) return null;
    var beforeCarat = k.slice(0, k.length - caratSuffix[0].length);
    var keys = modelKeys || [k];
    var prefix = '';
    if (keys.length > 1) {
      var minLen = keys.reduce(function (min, item) {
        return Math.min(min, String(item).length);
      }, String(keys[0]).length);
      for (var i = 0; i < minLen; i++) {
        var ch = String(keys[0])[i];
        for (var j = 1; j < keys.length; j++) {
          if (String(keys[j])[i] !== ch) {
            i = minLen;
            break;
          }
        }
        if (i < minLen) prefix += ch;
      }
      var lastUnderscore = prefix.lastIndexOf('_');
      if (lastUnderscore > 0) prefix = prefix.slice(0, lastUnderscore + 1);
    } else {
      var firstSeg = k.split('_')[0];
      if (firstSeg) prefix = firstSeg + '_';
    }
    if (prefix && beforeCarat.indexOf(prefix) === 0) {
      var shapePart = beforeCarat.slice(prefix.length);
      if (shapePart) return shapePart.replace(/_/g, '');
    }
    var m = k.match(/_([a-z]+)_\d+_\d{2}_ct$/);
    return m ? m[1] : null;
  }

  var shapeAvailabilityCache = {};
  var shapeAvailabilityProbeId = 0;

  function isSettingShapeModelKey(key, settingPrefix) {
    var k = String(key || '').toLowerCase().replace(/\.glb$/, '');
    if (!k || k.indexOf('_gems_') !== -1 || k.indexOf('_band_') !== -1 || k.indexOf('band_gems') !== -1) return false;
    var prefix = String(settingPrefix || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/_+$/g, '');
    if (!prefix) return false;
    var escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('^' + escaped + '_[a-z0-9_]+_\\d+_\\d{2}_ct$').test(k);
  }

  function getShapeFileSlug(shapeValueOrCard) {
    if (shapeValueOrCard && shapeValueOrCard.dataset) {
      var fromToken = String(shapeValueOrCard.dataset.shapeToken || '').trim().toLowerCase();
      if (fromToken) {
        return fromToken.replace(/[^a-z0-9_]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      }
      var fromValue = String(shapeValueOrCard.dataset.value || '').trim().toLowerCase();
      if (fromValue) {
        return fromValue.replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      }
      return '';
    }
    var cards = qsa('[data-rb-option][data-key="shape"]');
    var want = toLookupKey(shapeValueOrCard);
    for (var i = 0; i < cards.length; i++) {
      if (toLookupKey(cards[i].dataset.value) === want) return getShapeFileSlug(cards[i]);
    }
    var fallback = String(shapeValueOrCard || '').trim().toLowerCase();
    return fallback.replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  }

  function getAvailableShapeTokensForSetting(settingValue) {
    var components = getComponentAssetsConfig();
    if (!components || !components.setting || !components.setting.bySetting) return [];
    var raw = pickRawSettingEntry(components.setting.bySetting, settingValue || state.setting);
    if (!raw) return [];
    var shapes = {};
    var settingPrefix = settingNameToFilePrefix(settingValue || state.setting);

    if (raw.byShapeCarat) {
      var mapKeys = Object.keys(raw.byShapeCarat);
      rememberSettingNamingFromKeysIfConfident(settingPrefix, raw.byShapeCarat);
      mapKeys.forEach(function (k) {
        if (!isSettingMetalModelKey(k, settingPrefix)) return;
        var parsed = parseShapeCaratFromSettingModelKey(k, settingPrefix);
        if (!parsed || !parsed.shape) return;
        shapes[normalizeShapeFilenameToken(parsed.shape)] = true;
      });
    }

    return Object.keys(shapes);
  }

  // Match 3D model filename tokens (halo_radiant_5_25_ct -> radiant), not fuzzy aliases.
  function normalizeShapeFilenameToken(raw) {
    return String(raw || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function getShapeOptionToken(cardOrValue) {
    if (cardOrValue && cardOrValue.dataset) {
      if (cardOrValue.dataset.shapeToken) return normalizeShapeFilenameToken(cardOrValue.dataset.shapeToken);
      return normalizeShapeFilenameToken(cardOrValue.dataset.value);
    }
    return normalizeShapeFilenameToken(cardOrValue);
  }

  function isShapeStripCardVisible(card) {
    return card && !card.hidden && !card.classList.contains('rb-shape-option--unavailable');
  }

  function applyShapeTokensToCards(tokens, restrict) {
    var cards = qsa('[data-rb-option][data-key="shape"]');
    // Settings without 3D models yet: no shape tokens to filter on — show every option.
    if (restrict && (!tokens || !tokens.length)) {
      restrict = false;
    }
    cards.forEach(function (card) {
      var token = getShapeOptionToken(card);
      var show = !restrict || tokens.indexOf(token) !== -1;
      card.hidden = !show;
      card.classList.toggle('rb-shape-option--unavailable', !show);
      card.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (!show) {
        card.classList.remove('active');
        card.setAttribute('aria-selected', 'false');
      }
    });

    if (!restrict) return;

    var current = getShapeOptionToken(state.shape);
    if (tokens.indexOf(current) !== -1) {
      scrollShapeStripToFirstAvailable();
      return;
    }

    var pickToken = tokens.indexOf('round') !== -1 ? 'round' : tokens[0];
    var pickCard = null;
    for (var i = 0; i < cards.length; i++) {
      if (!isShapeStripCardVisible(cards[i])) continue;
      if (getShapeOptionToken(cards[i]) === pickToken) {
        pickCard = cards[i];
        break;
      }
    }
    if (!pickCard) {
      for (var j = 0; j < cards.length; j++) {
        if (isShapeStripCardVisible(cards[j])) {
          pickCard = cards[j];
          break;
        }
      }
    }
    if (!pickCard) return;

    state.shape = pickCard.dataset.value;
    setSelected('shape', state.shape);
    scrollShapeStripToFirstAvailable();
    if (selectedComponents.diamond) {
      ensureThree();
      if (threeReady) updateDisplayedModelFromState();
    }
    applyDiamondSizeStopsForSettingShape();
  }

  function probeShapeAvailabilityForSetting(settingValue) {
    var settingKey = toLookupKey(settingValue || state.setting);
    if (!settingKey) return;

    if (shapeAvailabilityCache[settingKey] && shapeAvailabilityCache[settingKey].tokens) {
      applyShapeTokensToCards(shapeAvailabilityCache[settingKey].tokens, true);
      return;
    }

    if (shapeAvailabilityCache[settingKey] && shapeAvailabilityCache[settingKey].promise) {
      shapeAvailabilityCache[settingKey].promise.then(function (tokens) {
        if (toLookupKey(state.setting) === settingKey) applyShapeTokensToCards(tokens, true);
      });
      return;
    }

    var probeId = ++shapeAvailabilityProbeId;
    var prefix = settingNameToFilePrefix(settingValue || state.setting);
    var base = getRingBuilderModelCdnBase();
    var cards = qsa('[data-rb-option][data-key="shape"]');
    if (!prefix || !base || !cards.length) {
      applyShapeTokensToCards([], true);
      return;
    }

    var promise = Promise.all(
      cards.map(function (card) {
        var slug = getShapeFileSlug(card);
        if (!slug) return Promise.resolve(null);
        var naming = getSettingNamingStyle(prefix, null);
        var probeKeys = [
          buildSettingMetalFileKey(prefix, slug, '1_00', naming),
          buildSettingMetalFileKey(prefix, slug, '1_00', { metalDoubled: false, componentPrefix: false }),
          buildSettingMetalFileKey(prefix, slug, '1_00', { metalDoubled: true, componentPrefix: true })
        ];
        var seen = {};
        probeKeys = probeKeys.filter(function (key) {
          if (!key || seen[key]) return false;
          seen[key] = true;
          return isSettingMetalModelKey(key, prefix);
        });
        if (!probeKeys.length) return Promise.resolve(null);

        function tryKey(idx) {
          if (idx >= probeKeys.length) return Promise.resolve(null);
          var key = probeKeys[idx];
          var url = buildR2ModelUrl(base, key, prefix);
          return fetch(url, { method: 'HEAD', mode: 'cors' })
            .then(function (res) {
              if (!res.ok) return tryKey(idx + 1);
              if (key.indexOf(prefix + '_' + prefix + '_') === 0) {
                rememberSettingNaming(prefix, { metalDoubled: true, componentPrefix: true });
              } else {
                rememberSettingNaming(prefix, { metalDoubled: false, componentPrefix: false });
              }
              return normalizeShapeFilenameToken(slug);
            })
            .catch(function () { return tryKey(idx + 1); });
        }
        return tryKey(0);
      })
    ).then(function (results) {
      var tokens = [];
      results.forEach(function (t) {
        if (t && tokens.indexOf(t) === -1) tokens.push(t);
      });
      shapeAvailabilityCache[settingKey] = { tokens: tokens };
      return tokens;
    });

    shapeAvailabilityCache[settingKey] = { promise: promise };
    promise.then(function (tokens) {
      if (probeId !== shapeAvailabilityProbeId) return tokens;
      if (toLookupKey(state.setting) !== settingKey) return tokens;
      applyShapeTokensToCards(tokens, true);
      if (selectedComponents.diamond) {
        applyDiamondSizeStopsForSettingShape();
        ensureThree();
        if (threeReady) updateDisplayedModelFromState();
      }
      return tokens;
    });
  }

  function applyShapeAvailabilityForSetting() {
    if (!state.setting) {
      applyShapeTokensToCards([], false);
      return;
    }

    var tokens = getAvailableShapeTokensForSetting(state.setting);
    if (tokens.length > 0) {
      shapeAvailabilityCache[toLookupKey(state.setting)] = { tokens: tokens };
      applyShapeTokensToCards(tokens, true);
      return;
    }

    applyShapeTokensToCards([], false);
    probeShapeAvailabilityForSetting(state.setting);
  }

  function getModelKeyShapeAndCarat(key, allKeys) {
    var parsed = parseShapeCaratFromKey(key);
    if (parsed) return parsed;
    var token = extractShapeTokenFromSettingModelKey(key, allKeys);
    var m = String(key).toLowerCase().match(/_(\d+)_(\d{2})_ct$/);
    if (token && m) return { shape: token, carat: m[1] + '.' + m[2] };
    return null;
  }

  function getShapeLookupToken(shapeValue) {
    var cards = qsa('[data-rb-option][data-key="shape"]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].dataset.value === shapeValue) return getShapeOptionToken(cards[i]);
    }
    return normalizeShapeFilenameToken(shapeValue);
  }

  function modelShapeMatchesSelection(modelShapeToken, shapeValue) {
    var wantToken = getShapeLookupToken(shapeValue);
    var wantNorm = normalizeShapeToken(shapeValue);
    var modelToken = normalizeShapeFilenameToken(modelShapeToken);
    return modelToken === wantToken || normalizeShapeToken(modelShapeToken) === wantNorm;
  }

  /** Canonical diamond-size stops: 1 & 1.5 CT, then 2–7 CT in 0.25 CT steps (Figma / recording). */
  function buildDiamondSizeCaratGrid() {
    var carats = ['1.00', '1.50'];
    var c = 2;
    while (c <= 7) {
      carats.push(c.toFixed(2));
      c += 0.25;
    }
    return carats;
  }

  function getAvailableCaratsForSettingShape(settingValue, shapeValue) {
    var components = getComponentAssetsConfig();
    if (!components || !components.setting || !components.setting.bySetting) return [];
    var raw = pickRawSettingEntry(components.setting.bySetting, settingValue || state.setting);
    if (!raw || !raw.byShapeCarat) return [];
    var mapKeys = Object.keys(raw.byShapeCarat);
    var seen = {};
    var carats = [];
    mapKeys.forEach(function (k) {
      var info = getModelKeyShapeAndCarat(k, mapKeys);
      if (!info) return;
      if (!modelShapeMatchesSelection(info.shape, shapeValue || state.shape)) return;
      var ct = normalizeCaratKey(info.carat);
      if (!ct || seen[ct]) return;
      seen[ct] = true;
      carats.push(ct);
    });
    carats.sort(function (a, b) {
      return parseFloat(a) - parseFloat(b);
    });
    return carats;
  }

  function diamondSizeValuesMatch(a, b) {
    if (!a || !b) return false;
    return normalizeCaratKey(a) === normalizeCaratKey(b);
  }

  function getDiamondSizeStopList() {
    return DIAMOND_SIZE_STOPS.slice();
  }

  function formatCaratStopLabel(caratKey) {
    var n = parseFloat(caratKey);
    if (!Number.isFinite(n)) return String(caratKey) + ' CT';
    var frac = Math.abs(n * 100 - Math.round(n * 100));
    var txt;
    if (frac < 0.001) {
      txt = n.toFixed(2).replace(/\.?0+$/, '');
    } else {
      txt = n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    }
    return txt + ' CT';
  }

  function formatCaratOptionValue(caratKey) {
    return formatCaratStopLabel(caratKey);
  }

  function formatReviewSizeDisplay(raw) {
    var s = String(raw || '').trim();
    if (!s) return '—';
    if (/\bsize\b/i.test(s)) return s;
    return 'Size ' + s;
  }

  function isDiamondAnchorCarat(caratKey) {
    var normalized = normalizeCaratKey(caratKey);
    return DIAMOND_SIZE_ANCHOR_STOPS.some(function (stop) {
      return normalizeCaratKey(stop) === normalized;
    });
  }

  function isDiamondVisibleDotCarat(caratKey) {
    var normalized = normalizeCaratKey(caratKey);
    return DIAMOND_SIZE_VISIBLE_DOT_STOPS.some(function (stop) {
      return normalizeCaratKey(stop) === normalized;
    });
  }

  function formatWholeCaratAnchorLabel(caratKey) {
    var n = parseFloat(caratKey);
    if (!Number.isFinite(n)) return '';
    return n.toFixed(2).replace(/\.?0+$/, '') + ' CT';
  }

  function popularCaratLabelSuffix(template) {
    if (!template) return 'Most Popular';
    var match = String(template).match(/most popular/i);
    return match ? template.slice(match.index).trim() : 'Most Popular';
  }

  function getDiamondSizePopularLabelTemplate() {
    var root = document.getElementById('ring-builder');
    var fromRoot =
      root && root.getAttribute('data-diamond-size-popular-label');
    if (fromRoot && String(fromRoot).trim()) return String(fromRoot).trim();
    var panel = qs('.rb-panel--diamond-size');
    var popularEl = panel && panel.querySelector('.rb-dsize-stop--popular .rb-dsize-label');
    return popularEl ? (popularEl.textContent || '').trim() : null;
  }

  function setPopularLabelStacked(labelEl, readoutLabel, popularLabel) {
    // Render the popular label as two stacked lines (carat + "Most Popular") so
    // the tag drops onto a lower row instead of overlapping the knob's CT
    // readout near 1.5 CT. Styled by `.rb-dsize-label--stack` in the CSS.
    if (!labelEl) return;
    labelEl.classList.add('rb-dsize-label--stack');
    var suffix = popularCaratLabelSuffix(popularLabel || labelEl.textContent);
    labelEl.textContent = '';
    var ctSpan = document.createElement('span');
    ctSpan.className = 'rb-dsize-label__ct';
    ctSpan.textContent = readoutLabel;
    var tagSpan = document.createElement('span');
    tagSpan.className = 'rb-dsize-label__tag';
    tagSpan.textContent = suffix;
    labelEl.appendChild(ctSpan);
    labelEl.appendChild(tagSpan);
  }

  function applyDiamondSizeSparseLabels(panel, popularLabel) {
    if (!panel) return;
    panel.classList.add('rb-panel--diamond-size--sparse');
    var stops = qsa('.rb-dsize-stop', panel);
    stops.forEach(function (stop) {
      var ct =
        stop.getAttribute('data-carat-key') ||
        normalizeCaratKey(stop.dataset.value);
      var readoutLabel = formatCaratStopLabel(ct);
      stop.setAttribute('data-readout-label', readoutLabel);
      stop.classList.remove('rb-dsize-stop--anchor');

      var isAnchor = isDiamondAnchorCarat(ct);
      var isVisibleDot = isDiamondVisibleDotCarat(ct);
      var isPopular = stop.classList.contains('rb-dsize-stop--popular');
      if (isAnchor) stop.classList.add('rb-dsize-stop--anchor');
      stop.classList.toggle('rb-dsize-stop--dot-visible', isVisibleDot);

      var labelEl = stop.querySelector('.rb-dsize-label');
      if (!labelEl) return;
      if (isPopular) {
        setPopularLabelStacked(labelEl, readoutLabel, popularLabel);
      } else if (isAnchor) {
        labelEl.classList.remove('rb-dsize-label--stack');
        labelEl.textContent = formatWholeCaratAnchorLabel(ct);
      } else {
        labelEl.classList.remove('rb-dsize-label--stack');
        labelEl.textContent = '';
      }
    });
  }

  function syncDiamondSizeReadout(panel, activeStop, knobPct) {
    if (!panel) return;
    var readout = panel.querySelector('#rb-dsize-readout');
    var primary = panel.querySelector('#rb-dsize-readout-primary');
    var unit = panel.querySelector('#rb-dsize-readout-unit');
    if (!readout && !primary && !unit) return;

    var readoutValue = '';
    if (activeStop) {
      readoutValue =
        (activeStop.getAttribute('data-readout-label') || '').trim() ||
        formatCaratStopLabel(
          activeStop.getAttribute('data-carat-key') || activeStop.dataset.value
        );
    }

    var pct = knobPct;
    if (!Number.isFinite(pct) && activeStop) {
      pct = parseFloat(activeStop.style.left || '0');
    }
    if (!Number.isFinite(pct)) pct = 0;

    if (readout) {
      readout.classList.remove(
        'rb-dsize-readout--edge-start',
        'rb-dsize-readout--edge-end'
      );
      if (pct <= 6) {
        readout.classList.add('rb-dsize-readout--edge-start');
      } else if (pct >= 94) {
        readout.classList.add('rb-dsize-readout--edge-end');
      }
    }
    if (primary && readoutValue) {
      primary.textContent = readoutValue.replace(/\s*CT$/i, '');
    }
    if (unit) {
      unit.textContent = 'CT';
      unit.style.display = '';
    }
    var liveRegion = panel.querySelector('#rb-dsize-live');
    if (liveRegion && readoutValue) {
      liveRegion.textContent = readoutValue;
    }
  }

  function pickNearestCaratInList(targetRaw, carats) {
    if (!carats || !carats.length) return '';
    var target = parseFloat(normalizeCaratKey(targetRaw));
    if (!Number.isFinite(target)) target = 1.5;
    var best = carats[0];
    var bestD = Infinity;
    for (var i = 0; i < carats.length; i++) {
      var d = Math.abs(parseFloat(carats[i]) - target);
      if (d < bestD) {
        bestD = d;
        best = carats[i];
      }
    }
    return formatCaratOptionValue(best);
  }

  function rebuildDiamondSizeStops(carats, popularLabel) {
    var panel = qs('.rb-panel--diamond-size');
    if (!panel || !carats || carats.length < 2) return false;
    var track = panel.querySelector('.rb-dsize-track');
    if (!track) return false;

    qsa('.rb-dsize-stop', panel).forEach(function (el) {
      el.remove();
    });

    var popularTarget = 1.5;
    var popularIdx = 0;
    var popularDist = Infinity;
    for (var pi = 0; pi < carats.length; pi++) {
      var pd = Math.abs(parseFloat(carats[pi]) - popularTarget);
      if (pd < popularDist) {
        popularDist = pd;
        popularIdx = pi;
      }
    }

    var n = carats.length;
    carats.forEach(function (ct, idx) {
      var left = n <= 1 ? 0 : (idx / (n - 1)) * 100;
      var readoutLabel = formatCaratStopLabel(ct);
      var isAnchor = isDiamondAnchorCarat(ct);
      var isVisibleDot = isDiamondVisibleDotCarat(ct);
      var isPopular = idx === popularIdx;

      var stop = document.createElement('div');
      var classes = 'rb-dsize-stop';
      if (idx === 0 || idx === n - 1) classes += ' rb-dsize-stop--edge';
      if (isPopular) classes += ' rb-dsize-stop--popular';
      if (isAnchor) classes += ' rb-dsize-stop--anchor';
      if (isVisibleDot) classes += ' rb-dsize-stop--dot-visible';
      stop.className = classes;
      stop.style.left = left + '%';
      stop.setAttribute('data-rb-option', '');
      stop.setAttribute('data-key', 'diamond_size');
      stop.setAttribute('data-value', formatCaratOptionValue(ct));
      stop.setAttribute('data-slide-group', 'diamond-size');
      stop.setAttribute('data-slide', '0');
      stop.setAttribute('data-carat-key', ct);
      stop.setAttribute('data-readout-label', readoutLabel);

      var dot = document.createElement('span');
      dot.className = 'rb-dsize-dot';
      var label = document.createElement('span');
      label.className = 'rb-dsize-label';
      if (isPopular) {
        setPopularLabelStacked(label, readoutLabel, popularLabel);
        stop.setAttribute('data-rb-mobile-detail', readoutLabel);
      } else {
        label.textContent = isAnchor ? formatWholeCaratAnchorLabel(ct) : '';
      }
      stop.appendChild(dot);
      stop.appendChild(label);
      track.appendChild(stop);
    });

    panel.classList.add('rb-panel--diamond-size--sparse');
    panel.setAttribute('data-rb-carat-min', String(parseFloat(carats[0])));
    panel.setAttribute('data-rb-carat-max', String(parseFloat(carats[n - 1])));
    return true;
  }

  function getDiamondSizeCaratRange(panel) {
    panel = panel || qs('.rb-panel--diamond-size');
    var min = parseFloat(panel && panel.getAttribute('data-rb-carat-min'));
    var max = parseFloat(panel && panel.getAttribute('data-rb-carat-max'));
    if (!Number.isFinite(min)) min = 1;
    if (!Number.isFinite(max)) max = 7;
    return { min: min, max: max };
  }

  function applyDiamondSizeStopsForSettingShape() {
    var available = getAvailableCaratsForSettingShape(state.setting, state.shape);
    var carats = available.length >= 2 ? available : getDiamondSizeStopList();
    if (carats.length < 2) return;

    if (!rebuildDiamondSizeStops(carats, getDiamondSizePopularLabelTemplate())) return;
    if (diamondSizeDragApi && typeof diamondSizeDragApi.refreshStopCache === 'function') {
      diamondSizeDragApi.refreshStopCache();
    }

    var prevSize = state.diamond_size;
    var pickValue = pickNearestCaratInList(prevSize, carats);
    var caratChanged = !diamondSizeValuesMatch(prevSize, pickValue);
    if (caratChanged) {
      diamondSelections.size = pickValue;
      state.diamond_size = pickValue;
      state.diamond = [diamondSelections.size, diamondSelections.quality].filter(Boolean).join(' / ');
    }

    setSelected('diamond_size', pickValue);

    if (caratChanged && threeReady) {
      updateDisplayedModelForCaratDebounced();
    }
  }

  function scrollShapeStripToFirstAvailable() {
    var slider = document.getElementById('rb-shape-slider');
    if (!slider) return;
    var visible = Array.prototype.filter.call(
      slider.querySelectorAll('[data-key="shape"]'),
      isShapeStripCardVisible
    );
    if (!visible.length) return;
    var active = null;
    for (var i = 0; i < visible.length; i++) {
      if (visible[i].classList.contains('active')) active = visible[i];
    }
    alignCardToStripStart(active || visible[0]);
  }

  // Exact shape+carat, then nearest carat for the same shape (no cross-shape fallback).
  function resolveByShapeCarat(rawEntry, shapeRaw, caratRaw, settingPrefix, resolveOpts) {
    if (!rawEntry || !rawEntry.byShapeCarat) return null;
    var map = rawEntry.byShapeCarat;
    var shapeKey = normalizeShapeFilenameToken(
      getShapeLookupToken(shapeRaw) || normalizeShapeToken(shapeRaw)
    );
    var carat = normalizeCaratKey(caratRaw);
    var prefix = settingPrefix || settingNameToFilePrefix(state.setting);
    resolveOpts = resolveOpts || {};

    var index = {};
    Object.keys(map).forEach(function (k) {
      var kLower = String(k).toLowerCase();
      if (resolveOpts.metalOnly && prefix && kLower.indexOf(prefix + '_gem_') === 0) return;
      var parsed = parseShapeCaratFromSettingModelKey(k, prefix);
      if (!parsed) parsed = parseShapeCaratFromKey(k, prefix);
      if (!parsed || !parsed.shape) return;
      var tok = normalizeShapeFilenameToken(parsed.shape);
      (index[tok] = index[tok] || {})[parsed.carat] = map[k];
    });

    function pick(sh, ct) {
      var byCt = index[sh];
      if (!byCt) return null;
      if (ct && byCt[ct]) return unwrapAssetEntry(byCt[ct]);
      var cs = Object.keys(byCt).map(parseFloat).filter(Number.isFinite).sort(function (a, b) { return a - b; });
      if (!cs.length) return null;
      var target = parseFloat(ct);
      if (!Number.isFinite(target)) {
        return unwrapAssetEntry(byCt[cs[0].toFixed(2)]);
      }
      var nearest = cs.reduce(function (p, c) {
        return Math.abs(c - target) < Math.abs(p - target) ? c : p;
      }, cs[0]);
      // Single-carat index (probe stub or sparse metaobject): synthesise exact carat on R2.
      if (cs.length === 1 && Math.abs(nearest - target) > 0.001) return null;
      return unwrapAssetEntry(byCt[nearest.toFixed(2)]);
    }

    return pick(shapeKey, carat) || null;
  }

  function extractGemIndex(key) {
    var m = String(key).toLowerCase().match(/(?:_|-)(\d+)$/);
    return m ? parseInt(m[1], 10) : 0;
  }

  function stripGemIndexSuffix(key) {
    return String(key).toLowerCase().replace(/(?:_|-)\d+$/, '');
  }

  function parseCaratFromGemKey(base) {
    var m = String(base).toLowerCase().match(/_(\d+)_(\d{2})_ct$/);
    if (!m) return null;
    return m[1] + '.' + m[2];
  }

  // halo_gems_round_1_00_ct_0 … _11 -> urls[] for shape+carat (nearest carat, round fallback)
  function resolveGemUrlsByShapeCarat(rawEntry, shapeRaw, caratRaw, gemsPrefix) {
    if (!rawEntry || !rawEntry.byShapeCarat) return null;
    var map = rawEntry.byShapeCarat;
    var shapeKey = normalizeShapeFilenameToken(
      getShapeLookupToken(shapeRaw) || normalizeShapeToken(shapeRaw)
    );
    var carat = normalizeCaratKey(caratRaw);
    var index = {};

    Object.keys(map).forEach(function (k) {
      var base = stripGemIndexSuffix(k);
      var parsed = parseShapeCaratFromSettingModelKey(base, null, { gemsPrefix: gemsPrefix });
      if (!parsed) parsed = parseShapeCaratFromKey(base);
      var shapeTok = null;
      var caratKey = null;
      if (parsed && parsed.shape) {
        shapeTok = normalizeShapeFilenameToken(parsed.shape);
        caratKey = parsed.carat;
      } else {
        caratKey = parseCaratFromGemKey(base);
        if (caratKey) shapeTok = '__caratOnly';
      }
      if (!shapeTok || !caratKey) return;
      var entry = unwrapAssetEntry(map[k]);
      if (!entry || !entry.url) return;
      var byCt = (index[shapeTok] = index[shapeTok] || {});
      var list = (byCt[caratKey] = byCt[caratKey] || []);
      list.push({ url: entry.url, idx: extractGemIndex(k) });
    });

    function pickUrls(sh, ct) {
      var byCt = index[sh];
      if (!byCt) return null;
      function sortUrls(list) {
        return list.sort(function (a, b) { return a.idx - b.idx; }).map(function (x) { return x.url; });
      }
      if (ct && byCt[ct] && byCt[ct].length) return sortUrls(byCt[ct]);
      var cs = Object.keys(byCt).map(parseFloat).filter(Number.isFinite).sort(function (a, b) { return a - b; });
      if (!cs.length) return null;
      var target = parseFloat(ct);
      var nearest = Number.isFinite(target)
        ? cs.reduce(function (p, c) { return Math.abs(c - target) < Math.abs(p - target) ? c : p; }, cs[0])
        : cs[0];
      var nearestList = byCt[nearest.toFixed(2)];
      return nearestList && nearestList.length ? sortUrls(nearestList) : null;
    }

    var urls = pickUrls(shapeKey, carat);
    if (!urls || !urls.length) urls = pickUrls('__caratOnly', carat);
    if (!urls || !urls.length) return null;
    return {
      urls: urls,
      tags: rawEntry.tags || { defaultRole: 'diamond' },
      transform: rawEntry.transform
    };
  }

  function getComponentAssetsConfig() {
    var assets = getRingAssetsConfig();
    if (!assets.components || typeof assets.components !== 'object') return null;
    return assets.components;
  }

  function hasComponentConfig() {
    return !!getComponentAssetsConfig();
  }

  function getBandComboKeys() {
    var thickness = state.band_thickness || '';
    var profile = state.band_profile || '';
    if (!thickness || !profile) return [];
    return [
      thickness + '|' + profile,
      profile + '|' + thickness,
      thickness + ' / ' + profile,
      profile + ' / ' + thickness
    ];
  }

  function resolveComponentAsset(type) {
    var components = getComponentAssetsConfig();
    if (!components) return null;

    var config = components[type];
    if (!config) return null;

    var componentTransform = components.transforms && components.transforms[type] ? components.transforms[type] : {};
    var directEntry = unwrapAssetEntry(config);
    if (directEntry) {
      directEntry.transform = mergeTransform(componentTransform, directEntry.transform);
      return directEntry;
    }

    var candidates = [];
    if (type === 'setting') {
      candidates = [state.setting];
    } else if (type === 'settingGems') {
      candidates = [state.shape, state.diamond_size];
    } else if (type === 'diamond') {
      candidates = [state.shape, state.diamond_size, state.diamond_quality];
    } else if (type === 'band' || type === 'bandGems') {
      candidates = getBandComboKeys().concat([state.band_thickness, state.band_profile, state.band]);
    }

    var resolved = null;
    if (type === 'setting') {
      var rawSetting = pickRawSettingEntry(config.bySetting, state.setting);
      var settingPrefix = settingNameToFilePrefix(state.setting);
      var scResolved = resolveSettingMetalAsset(rawSetting, settingPrefix);
      if (scResolved) {
        scResolved.transform = mergeTransform(componentTransform, scResolved.transform);
        return scResolved;
      }
    }
    if (type === 'diamond') {
      var rawShape = pickRawShapeEntry(config.byShape, state.shape);
      var gemResolved = resolveByShapeCarat(rawShape, state.shape, state.diamond_size);
      if (!gemResolved) {
        gemResolved = synthesizeShapeCaratAsset(state.shape, state.diamond_size, { kind: 'diamond' });
      }
      if (gemResolved) {
        gemResolved.transform = mergeTransform(componentTransform, gemResolved.transform);
        return gemResolved;
      }
    }
    if (type === 'settingGems') {
      var rawGemShape = pickRawShapeEntry(config.byShape, state.shape);
      var sgResolved = resolveGemUrlsByShapeCarat(
        rawGemShape,
        state.shape,
        state.diamond_size,
        settingNameToFilePrefix(state.setting) + '_gems'
      );
      if (!sgResolved) {
        var gemsPrefix = settingNameToFilePrefix(state.setting) + '_gems';
        var synthGem = synthesizeShapeCaratAsset(state.shape, state.diamond_size, {
          kind: 'settingGems',
          settingGemsPrefix: gemsPrefix
        });
        if (synthGem) {
          sgResolved = {
            urls: [synthGem.url],
            tags: synthGem.tags,
            transform: rawGemShape && rawGemShape.transform
          };
        }
      }
      if (sgResolved) {
        sgResolved.transform = mergeTransform(componentTransform, sgResolved.transform);
        return sgResolved;
      }
    }
    if (type === 'band') {
      var synthBand = synthesizeBandAsset('band');
      if (synthBand) {
        synthBand.transform = mergeTransform(componentTransform, synthBand.transform);
        return synthBand;
      }
    }
    if (type === 'bandGems' && isDiamondBandProfile(state.band_profile)) {
      var synthBandGems = synthesizeBandAsset('bandGems');
      if (synthBandGems) {
        synthBandGems.transform = mergeTransform(componentTransform, synthBandGems.transform);
        return synthBandGems;
      }
    }
    if (type === 'setting') resolved = findAssetMatch(config.bySetting, candidates);
    if (!resolved && type === 'settingGems') resolved = findAssetMatch(config.byShape, candidates);
    if (!resolved && type === 'diamond') resolved = findAssetMatch(config.byShape, candidates);
    if (!resolved && type === 'diamond') resolved = findAssetMatch(config.bySize, candidates);
    if (!resolved && type === 'diamond') resolved = findAssetMatch(config.byQuality, candidates);
    if (!resolved && (type === 'band' || type === 'bandGems')) resolved = findAssetMatch(config.byCombo, candidates);
    if (!resolved && (type === 'band' || type === 'bandGems')) resolved = findAssetMatch(config.byThickness, candidates);
    if (!resolved && (type === 'band' || type === 'bandGems')) resolved = findAssetMatch(config.byProfile, candidates);
    if (!resolved) resolved = findAssetMatch(config.variants, candidates);
    if (!resolved) resolved = findAssetMatch(config, candidates);
    if (!resolved && typeof config === 'object') {
      var fallbackKeys = Object.keys(config).filter(function (key) { return !COMPONENT_KEY_SKIP[key]; });
      if (fallbackKeys.length === 1) {
        resolved = unwrapAssetEntry(config[fallbackKeys[0]]);
      }
    }
    if (!resolved) resolved = unwrapAssetEntry(config.default);

    if (GEM_ARRAY_TYPES[type]) {
      if (resolved && Array.isArray(resolved.urls) && resolved.urls.length > 0) {
        resolved.transform = mergeTransform(componentTransform, resolved.transform);
        return resolved;
      }
      if (resolved && resolved.urls) {
        resolved.transform = mergeTransform(componentTransform, resolved.transform);
        return resolved;
      }
      return null;
    }

    if (!resolved || !resolved.url) return null;

    resolved.transform = mergeTransform(componentTransform, resolved.transform);
    return resolved;
  }

  function resolveComponentUrl(type) {
    var asset = resolveComponentAsset(type);
    return asset && asset.url ? asset.url : null;
  }

  function normalizeSettingSlideKey(value) {
    var normalized = toLookupKey(value);
    if (!normalized) return 'halo';
    if (normalized.indexOf('hidden') !== -1 && normalized.indexOf('halo') !== -1) return 'hidden-halo';
    if (normalized.indexOf('toi') !== -1 && normalized.indexOf('moi') !== -1) return 'toi-et-moi';
    if (normalized.indexOf('halo') !== -1) return 'halo';
    if (normalized.indexOf('three') !== -1 || normalized.indexOf('trio') !== -1) return 'three-stone';
    if (normalized.indexOf('solitaire') !== -1) return 'solitaire';
    return 'solitaire';
  }

  function getSettingSlideGroup(settingValue) {
    var raw = settingValue || state.setting || '';
    /* If the merchant has a dedicated slideshow for this exact design (e.g.
       setting-toi-et-moi, setting-cathedral, setting-bezel, setting-hidden-halo),
       prefer it over the canonical group so the right panel reflects the
       selected option instead of falling back to a related canonical slideshow. */
    if (raw) {
      var slug = 'setting-' + toSlideToken(raw);
      if (SETTING_SLIDE_GROUPS.indexOf(slug) !== -1) return slug;
    }
    return 'setting-' + normalizeSettingSlideKey(settingValue || state.setting);
  }

  function toSlideToken(value) {
    return toLookupKey(value).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function getShapeSlideGroup(shapeValue) {
    var raw = shapeValue || state.shape || '';
    var normalized = toSlideToken(raw);
    if (normalized) {
      var exact = 'shape-' + normalized;
      if (SHAPE_SLIDE_GROUPS.indexOf(exact) !== -1) return exact;
    }
    for (var i = 0; i < SHAPE_SLIDE_GROUPS.length; i++) {
      var slug = SHAPE_SLIDE_GROUPS[i].replace('shape-', '');
      if (normalized.indexOf(slug) !== -1) return SHAPE_SLIDE_GROUPS[i];
    }
    return SHAPE_SLIDE_GROUPS[0] || 'shape-round';
  }

  function getBandThicknessSlideGroup(thicknessValue) {
    var normalized = toSlideToken(thicknessValue || bandSelections.thickness || state.band_thickness || 'thin');
    if (normalized.indexOf('medium') !== -1) return 'band-thickness-medium';
    if (normalized.indexOf('bold') !== -1) return 'band-thickness-bold';
    return 'band-thickness-thin';
  }

  function getBandProfileSlideGroup(profileValue) {
    var normalized = toSlideToken(profileValue || bandSelections.profile || state.band_profile || 'classic');
    if (normalized.indexOf('diamond') !== -1) return 'band-profile-diamond-band';
    if (normalized.indexOf('round') !== -1) return 'band-profile-rounded';
    return 'band-profile-classic';
  }

  function getDiamondSizeSlideGroup() {
    return 'diamond-size';
  }

  function getBandSlideGroup() {
    if (bandTabMode === 'profile') return getBandProfileSlideGroup();
    return getBandThicknessSlideGroup();
  }

  function getSlideGroupForStep(step) {
    if (step === 1) return getSettingSlideGroup();
    if (step === 2) return getShapeSlideGroup();
    if (step === 4) return getBandSlideGroup();
    if (step === 5 && diamondTabMode === 'size') return getDiamondSizeSlideGroup();
    return getSettingSlideGroup();
  }

  function getRightPanelState(step) {
    if (step === TOTAL_STEPS + 1) {
      return { mode: 'review', slideGroup: null, view: null };
    }
    if (step === 3) {
      return {
        mode: 'collapsible',
        slideGroup: null,
        view: metalTabMode === 'setting' ? 'metal-setting' : 'metal-ring'
      };
    }
    if (step === 5 && diamondTabMode === 'quality') {
      return { mode: 'collapsible', slideGroup: null, view: 'diamond-quality' };
    }
    if (step === 6) {
      return { mode: 'collapsible', slideGroup: null, view: 'size' };
    }
    return { mode: 'slideshow', slideGroup: getSlideGroupForStep(step), view: null };
  }

  function getSlideshow(group) {
    if (!rbSlideshows[group]) rbSlideshows[group] = document.getElementById('rb-swiper-' + group);
    return rbSlideshows[group];
  }

  function getRbSlider(group) {
    var slideshow = getSlideshow(group);
    if (!slideshow || !slideshow.sliderInstance || !slideshow.sliderInstance.slider) return null;
    return slideshow.sliderInstance.slider;
  }

  function pauseVideosIn(root) {
    if (!root) return;
    qsa('video', root).forEach(function (video) {
      try {
        video.pause();
      } catch (e) {
        /* ignore */
      }
    });
  }

  /** MP4 slides defer `src` until active; images load via native `src` + `loading="lazy"`. */
  function unloadRbSlideMedia(slide) {
    if (!slide) return;
    qsa('video[data-rb-media-src]', slide).forEach(function (el) {
      try {
        el.pause();
      } catch (e) {
        /* ignore */
      }
      el.classList.remove('rb-video-ready');
      el.removeAttribute('src');
      if (typeof el.load === 'function') {
        try {
          el.load();
        } catch (loadErr) {
          /* ignore */
        }
      }
    });
  }

  function loadRbSlideMedia(slide) {
    if (!slide) return;
    qsa('video[data-rb-media-src]', slide).forEach(function (el) {
      var url = el.getAttribute('data-rb-media-src');
      if (!url || el.getAttribute('src') === url) return;
      // Fade the video in only once its first frame is decoded so the
      // transition never exposes the browser's black pre-decode paint.
      if (!el.dataset.rbReadyBound) {
        el.dataset.rbReadyBound = 'true';
        var markReady = function () {
          el.classList.add('rb-video-ready');
        };
        // `playing` is the safety net: if `loadeddata`/`canplay` are slow or
        // missed, the first painted frame (playback started) still reveals the
        // video so the active slide can't stay stranded on the cream backdrop.
        el.addEventListener('loadeddata', markReady);
        el.addEventListener('canplay', markReady);
        el.addEventListener('playing', markReady);
        el.addEventListener('error', markReady);
      }
      el.classList.remove('rb-video-ready');
      el.setAttribute('src', url);
      el.preload = 'auto';
    });
  }

  function resolveRbActiveSlide(slideshow) {
    if (!slideshow) return null;
    return (
      qs('.swiper-slide-active:not(.swiper-slide-duplicate)', slideshow) ||
      qs('.swiper-slide-active', slideshow) ||
      qs('.swiper-slide[data-index="0"]:not(.swiper-slide-duplicate)', slideshow) ||
      qs('.swiper-slide:not(.swiper-slide-duplicate)', slideshow)
    );
  }

  function playVideosInSlide(slide) {
    if (!slide) return;
    qsa('video', slide).forEach(function (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      var promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function () {
          /* iOS may block until the slideshow is visible; a later sync will retry */
        });
      }
    });
  }

  /**
   * Load MP4 sources only for the active slide in the active slideshow.
   * Native `<video>` slides do not use `video-element`; start/stop on swiper changes.
   */
  function syncRbSlideshowMedia(activeGroup) {
    SLIDESHOW_GROUPS.forEach(function (group) {
      var slideshow = getSlideshow(group);
      if (!slideshow) return;

      /* Mid-handoff, the outgoing group must stay playing + loaded + visible
         underneath the preparing incoming group; skip it so a follow-up sync
         (from goSlide / ensureRbSliderReady) can't strip its src and cut it to
         blank before the incoming video is ready. */
      if (rbHandoffOutgoing && slideshow === rbHandoffOutgoing && group !== activeGroup) {
        return;
      }

      pauseVideosIn(slideshow);

      var isActive = !!activeGroup && group === activeGroup && slideshow.classList.contains('is-active');
      if (!isActive) {
        qsa('.swiper-slide', slideshow).forEach(unloadRbSlideMedia);
        return;
      }

      // Eagerly load + keep every slide of the *active* slideshow decoded.
      // The previous prefetch-only-±1 window meant a slide that scrolled out
      // had its `src` stripped (and `.rb-video-ready` cleared); when autoplay
      // looped it back, it flashed to its `opacity:0` state and only faded in
      // after re-decoding — a visible flicker mid-crossfade. Loading all slides
      // keeps `.rb-video-ready` true throughout, so every transition is a clean
      // video→video dissolve. Memory stays bounded: only this one active group
      // holds sources; all other groups fully unload via the `!isActive` branch.
      qsa('.swiper-slide', slideshow).forEach(loadRbSlideMedia);
    });

    if (!activeGroup) return;
    var activeSlideshow = getSlideshow(activeGroup);
    if (!activeSlideshow || !activeSlideshow.classList.contains('is-active')) return;

    playVideosInSlide(resolveRbActiveSlide(activeSlideshow));
  }

  function ensureRbVideoHandlers(group) {
    var slideshow = getSlideshow(group);
    if (!slideshow || slideshow.dataset.rbVideoHandlersBound === 'true') return;

    var bind = function () {
      var slider = getRbSlider(group);
      if (!slider) return false;
      slideshow.dataset.rbVideoHandlersBound = 'true';
      var onSlide = function () {
        // Always sync against the CURRENT active group, never this handler's
        // captured `group`: a background slideshow's autoplay keeps firing
        // realIndexChange after we've switched away, and syncing to its stale
        // group would unload the genuinely-active slide's video (blanks a
        // single-slide group like Bezel that has no autoplay to self-recover).
        syncRbSlideshowMedia(activeSlideGroup);
      };
      slider.on('slideChangeTransitionEnd', onSlide);
      slider.on('realIndexChange', onSlide);
      onSlide();
      return true;
    };

    if (bind()) return;
    var attempt = 0;
    var poll = window.setInterval(function () {
      attempt += 1;
      if (bind() || attempt > 50) {
        window.clearInterval(poll);
      }
    }, 80);
  }

  function initRbSlideshowVideos() {
    syncRbSlideshowMedia(activeSlideGroup);
    if (activeSlideGroup) ensureRbVideoHandlers(activeSlideGroup);
  }

  /** The `<video>` inside a group's currently-active slide (or null for image slides). */
  function getGroupActiveVideo(slideshow) {
    var slide = resolveRbActiveSlide(slideshow);
    if (!slide) return null;
    return qs('video[data-rb-media-src]', slide);
  }

  /** A video is safe to reveal only once it has both buffered and decoded a frame. */
  function isVideoDisplayable(video) {
    // HAVE_CURRENT_DATA (2) = current frame available; `.rb-video-ready` is set
    // by the loadeddata/canplay/playing handlers once a frame has been decoded.
    return !!video && video.readyState >= 2 && video.classList.contains('rb-video-ready');
  }

  /**
   * Resolve when the video has actually PRESENTED a frame to the compositor —
   * a stronger signal than `loadeddata`/`canplay` (which only mean data is
   * buffered). Prefers `requestVideoFrameCallback`; falls back to `playing` +
   * a double rAF; always guarded by a timeout so a handoff can never hang.
   */
  function waitForVideoFrame(video, cb) {
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      cb();
    }
    var timer = window.setTimeout(finish, 700);

    if (typeof video.requestVideoFrameCallback === 'function') {
      try {
        video.requestVideoFrameCallback(function () { finish(); });
        return;
      } catch (e) {
        /* fall through to event-based path */
      }
    }

    var onPaint = function () {
      video.removeEventListener('playing', onPaint);
      // Two rAFs so the browser has composited at least one painted frame
      // before we reveal — avoids revealing on the pre-paint black buffer.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(finish);
      });
    };
    if (!video.paused && video.readyState >= 2) {
      onPaint();
    } else {
      video.addEventListener('playing', onPaint);
    }
  }

  /** Load every slide of a group + play its active slide, WITHOUT touching other groups. */
  function prepareGroupMedia(group) {
    var slideshow = getSlideshow(group);
    if (!slideshow) return;
    refreshRbSlider(group, true);
    qsa('.swiper-slide', slideshow).forEach(loadRbSlideMedia);
    playVideosInSlide(resolveRbActiveSlide(slideshow));
  }

  /** Commit the final single-active class state and run a full media sync. */
  function applyGroupActiveClasses(group) {
    SLIDESHOW_GROUPS.forEach(function (key) {
      var slideshow = getSlideshow(key);
      if (!slideshow) return;
      var active = key === group;
      slideshow.classList.toggle('is-active', active);
      slideshow.classList.remove('rb-entering', 'rb-preparing');
      slideshow.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
  }

  /**
   * Reveal the prepared incoming group and, once its cross-fade finishes, drop
   * the outgoing layer + run the authoritative media sync (pauses/unloads the
   * outgoing group). Re-resolves the active video each pass so a slide change
   * mid-preparation (e.g. goSlide moving to the selected card) waits on the
   * slide that will actually be shown. Bounded by `deadline`.
   */
  function revealWhenIncomingReady(group, outgoing, token, deadline) {
    if (token !== rbHandoffToken) return;
    var incoming = getSlideshow(group);
    if (!incoming) {
      rbHandoffActive = false;
      rbHandoffOutgoing = null;
      applyGroupActiveClasses(group);
      syncRbSlideshowMedia(group);
      return;
    }

    var video = getGroupActiveVideo(incoming);
    if (!video || isVideoDisplayable(video) || performance.now() >= deadline) {
      completeHandoff(group, outgoing, token);
      return;
    }

    // Keep it loading + playing, then wait for a genuinely presented frame.
    var slide = resolveRbActiveSlide(incoming);
    loadRbSlideMedia(slide);
    playVideosInSlide(slide);
    waitForVideoFrame(video, function () {
      revealWhenIncomingReady(group, outgoing, token, deadline);
    });
  }

  function completeHandoff(group, outgoing, token) {
    if (token !== rbHandoffToken) return;
    var incoming = getSlideshow(group);
    if (!incoming) {
      rbHandoffActive = false;
      rbHandoffOutgoing = null;
      applyGroupActiveClasses(group);
      syncRbSlideshowMedia(group);
      return;
    }

    // Reveal: incoming fades 0→1 while still `rb-entering` (stays above the
    // outgoing layer), so the two cross-dissolve — never a cut to blank.
    incoming.classList.remove('rb-preparing');

    var finalize = function () {
      if (token !== rbHandoffToken) return;
      // Incoming is fully opaque and covering the panel now; retire the
      // outgoing layer (hidden beneath) and run the authoritative media sync.
      rbHandoffActive = false;
      rbHandoffOutgoing = null;
      SLIDESHOW_GROUPS.forEach(function (key) {
        var slideshow = getSlideshow(key);
        if (!slideshow) return;
        var active = key === group;
        slideshow.classList.toggle('is-active', active);
        slideshow.classList.remove('rb-entering', 'rb-preparing');
        slideshow.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      syncRbSlideshowMedia(group);
    };

    var onEnd = function (e) {
      if (e.target !== incoming || e.propertyName !== 'opacity') return;
      incoming.removeEventListener('transitionend', onEnd);
      window.clearTimeout(guard);
      finalize();
    };
    incoming.addEventListener('transitionend', onEnd);
    // Guard slightly longer than the .22s reveal in case transitionend is
    // missed (interrupted, reduced-motion instant, tab backgrounded).
    var guard = window.setTimeout(function () {
      incoming.removeEventListener('transitionend', onEnd);
      finalize();
    }, 400);
  }

  function setSlideshowVisibility(group) {
    // Redundant re-entrant calls (setRightPanelMode → syncStepSlideshow →
    // goSlideForCard all target the same group) must not cancel an in-flight
    // handoff into that group — that would reveal the incoming layer instantly
    // and re-introduce the cut. Let the running handoff finish undisturbed.
    if (group === activeSlideGroup && rbHandoffActive) return;

    var previousGroup = activeSlideGroup;
    // The layer actually painted right now: mid-handoff that's the protected
    // outgoing group, NOT the (still-transparent) preparing incoming group that
    // already owns `activeSlideGroup`.
    var currentlyVisible = rbHandoffOutgoing || (previousGroup ? getSlideshow(previousGroup) : null);

    activeSlideGroup = group;
    rbHandoffToken += 1;
    var token = rbHandoffToken;
    if (group) ensureRbVideoHandlers(group);

    var incoming = group ? getSlideshow(group) : null;
    // Only keep an OUTGOING layer if it is genuinely on screen (a prior
    // slideshow). Coming from a collapsible step there's nothing to preserve.
    var outgoing = (
      incoming &&
      currentlyVisible &&
      currentlyVisible !== incoming &&
      currentlyVisible.classList.contains('is-active')
    ) ? currentlyVisible : null;
    var incomingVideo = incoming ? getGroupActiveVideo(incoming) : null;
    var incomingAlreadyShown = !!incoming && incoming.classList.contains('is-active') && currentlyVisible === incoming;

    /* Gate the reveal whenever the incoming active slide is a video that can't
       paint yet — whether or not there's an outgoing layer to keep. Image
       slides + already-ready videos + an already-shown group take the instant
       path. */
    var needsHandoff =
      !!incoming &&
      !!incomingVideo &&
      !isVideoDisplayable(incomingVideo) &&
      !incomingAlreadyShown &&
      !!outgoing;

    if (!needsHandoff) {
      rbHandoffActive = false;
      rbHandoffOutgoing = null;
      applyGroupActiveClasses(group);
      syncRbSlideshowMedia(group);
      return;
    }

    rbHandoffActive = true;
    rbHandoffOutgoing = outgoing;
    SLIDESHOW_GROUPS.forEach(function (key) {
      var slideshow = getSlideshow(key);
      if (!slideshow) return;
      if (key === group) {
        // Incoming: live + on top, but held transparent until its frame paints.
        slideshow.classList.add('is-active', 'rb-entering', 'rb-preparing');
        slideshow.setAttribute('aria-hidden', 'false');
      } else if (slideshow === outgoing) {
        // Outgoing: stays visible underneath through the handoff.
        slideshow.classList.add('is-active');
        slideshow.classList.remove('rb-entering', 'rb-preparing');
        slideshow.setAttribute('aria-hidden', 'true');
      } else {
        slideshow.classList.remove('is-active', 'rb-entering', 'rb-preparing');
        slideshow.setAttribute('aria-hidden', 'true');
      }
    });

    prepareGroupMedia(group);
    revealWhenIncomingReady(group, outgoing, token, performance.now() + 1200);
  }

  function hideAllSlideshows() {
    rbHandoffToken += 1;
    rbHandoffActive = false;
    rbHandoffOutgoing = null;
    SLIDESHOW_GROUPS.forEach(function (key) {
      var slideshow = getSlideshow(key);
      if (!slideshow) return;
      slideshow.classList.remove('is-active', 'rb-entering', 'rb-preparing');
      slideshow.setAttribute('aria-hidden', 'true');
    });
    syncRbSlideshowMedia(null);
  }

  function setCollapsibleView(view) {
    if (!collapsiblePanels) return;
    qsa('[data-rb-collapsible-view]', collapsiblePanels).forEach(function (panel) {
      panel.classList.toggle('is-active', panel.dataset.rbCollapsibleView === view);
    });
    collapsiblePanels.setAttribute('data-active-view', view || '');
  }

  function setRightPanelMode(step) {
    var panelState = getRightPanelState(step);

    if (rightPanel) {
      rightPanel.classList.toggle('is-collapsible-mode', panelState.mode === 'collapsible');
    }

    if (collapsiblePanels) {
      collapsiblePanels.setAttribute('aria-hidden', panelState.mode === 'collapsible' ? 'false' : 'true');
      setCollapsibleView(panelState.mode === 'collapsible' ? panelState.view : '');
    }

    if (panelState.mode === 'slideshow') {
      setSlideshowVisibility(panelState.slideGroup);
      ensureRbSliderReady(panelState.slideGroup, true);
      syncStepSlideshow(step);
      return;
    }

    hideAllSlideshows();
  }

  function refreshRbSlider(group, startAutoplay) {
    var slider = getRbSlider(group);
    if (!slider) return false;

    if (typeof slider.update === 'function') {
      slider.update();
    }

    if (
      slider.params &&
      slider.params.autoplay &&
      slider.autoplay
    ) {
      if (startAutoplay && typeof slider.autoplay.start === 'function') {
        slider.autoplay.start();
      }
      if (!startAutoplay && typeof slider.autoplay.stop === 'function') {
        slider.autoplay.stop();
      }
    }

    return true;
  }

  function ensureRbSliderReady(group, startAutoplay) {
    if (refreshRbSlider(group, startAutoplay)) {
      syncRbSlideshowMedia(group);
      return;
    }

    var attempt = 0;
    var poll = setInterval(function () {
      attempt += 1;
      if (refreshRbSlider(group, startAutoplay)) {
        clearInterval(poll);
        syncRbSlideshowMedia(group);
      } else if (attempt > 50) {
        clearInterval(poll);
      }
    }, 80);
  }

  function setStepAutoplayMode(step) {
    var panelState = getRightPanelState(step);
    if (panelState.mode !== 'slideshow') {
      SLIDESHOW_GROUPS.forEach(function (group) {
        refreshRbSlider(group, false);
      });
      return;
    }
    var activeGroup = panelState.slideGroup || getSlideGroupForStep(step);
    SLIDESHOW_GROUPS.forEach(function (group) {
      refreshRbSlider(group, group === activeGroup);
    });
  }

  function goSlide(group, i) {
    var slider = getRbSlider(group);
    if (!slider || Number.isNaN(i)) return;

    var slideshow = getSlideshow(group);
    var totalSlides = parseInt(slideshow && slideshow.dataset ? slideshow.dataset.items : '', 10);
    if (Number.isNaN(totalSlides) || totalSlides < 1) {
      totalSlides = slider.slides ? slider.slides.length : 0;
    }
    if (totalSlides < 1) return;

    var target = i;
    if (target < 0) target = 0;
    if (target > totalSlides - 1) target = totalSlides - 1;

    if (slider.params && slider.params.loop && typeof slider.slideToLoop === 'function') {
      slider.slideToLoop(target);
    } else if (typeof slider.slideTo === 'function') {
      slider.slideTo(target);
    }
    window.requestAnimationFrame(function () {
      syncRbSlideshowMedia(group);
    });
  }

  function goSlideForCard(card) {
    if (!card || card.dataset.slide === undefined) return;

    var indexInGroup = parseInt(card.dataset.slide, 10);
    if (Number.isNaN(indexInGroup)) return;

    var slideGroup = card.dataset.slideGroup || activeSlideGroup;
    /* Custom merchant ring designs (e.g. "Hidden Halo", "Cathedral", "Bezel") render
       cards with data-slide-group="setting-<handle>" that don't match the three
       canonical setting slideshows. Normalise via getSettingSlideGroup so any
       Setting card maps to setting-solitaire | setting-halo | setting-three-stone.
       Same fallback applies to Shape cards in case of custom shape names. */
    if (!SLIDESHOW_GROUPS.length || SLIDESHOW_GROUPS.indexOf(slideGroup) === -1) {
      var key = card.dataset.key;
      var value = card.dataset.value;
      if (key === 'setting') {
        slideGroup = getSettingSlideGroup(value);
      } else if (key === 'shape') {
        slideGroup = getShapeSlideGroup(value);
      }
    }
    setSlideshowVisibility(slideGroup);
    ensureRbSliderReady(slideGroup, true);
    goSlide(slideGroup, indexInGroup);
  }

  function syncStepSlideshow(step) {
    var group = getSlideGroupForStep(step);
    setSlideshowVisibility(group);
    ensureRbSliderReady(group, true);

    var panels = qsa('.rb-panel[data-panel="' + step + '"]');
    var panel = null;
    for (var i = 0; i < panels.length; i += 1) {
      if (panels[i].classList.contains('active')) {
        panel = panels[i];
        break;
      }
    }
    if (!panel && panels.length) panel = panels[0];
    if (!panel) {
      goSlide(group, 0);
      return;
    }

    var option = qs('[data-rb-option].active', panel) || qs('[data-rb-option][data-slide]', panel);
    if (option) {
      goSlideForCard(option);
    } else {
      goSlide(group, 0);
    }
  }

  function ensureShapeAndCaratDefaults() {
    if (!state.shape) state.shape = 'round';
    if (!state.diamond_size) {
      state.diamond_size = '1.50';
      diamondSelections.size = state.diamond_size;
    }
  }

  /* ponytail: show a round centre stone in the 3D preview as soon as a setting
     is chosen (step 1), before the user visits the Shape step. */
  function ensureRoundDiamondPreviewForSetting() {
    ensureShapeAndCaratDefaults();
    selectedComponents.diamond = true;

    var roundCard = null;
    qsa('[data-rb-option][data-key="shape"]').forEach(function (card) {
      if (roundCard) return;
      if (card.hidden || card.getAttribute('aria-hidden') === 'true') return;
      if (getShapeOptionToken(card) === 'round') roundCard = card;
    });

    if (roundCard) {
      state.shape = roundCard.dataset.value;
      setSelected('shape', state.shape);
    } else if (!state.shape) {
      state.shape = 'round';
    }

    applyDiamondSizeStopsForSettingShape();
  }

  function ensureDefaultSettingSelection() {
    ensureShapeAndCaratDefaults();

    if (state.setting) {
      if (!selectedComponents.setting) selectedComponents.setting = true;
      ensureRoundDiamondPreviewForSetting();
      return;
    }

    var solitaireCard = qs('[data-rb-option][data-key="setting"][data-value="Solitaire"]');
    if (solitaireCard) {
      state.setting = 'Solitaire';
      selectedComponents.setting = true;
      ensureRoundDiamondPreviewForSetting();
      setSelected('setting', state.setting);
      syncReview();
      goSlideForCard(solitaireCard);
      return;
    }

    var firstSettingCard = qs('[data-rb-option][data-key="setting"][data-value]');
    if (!firstSettingCard) {
      var comp = getComponentAssetsConfig();
      var by = comp && comp.setting && comp.setting.bySetting;
      var keys = by && typeof by === 'object' ? Object.keys(by) : [];
      if (keys.length) {
        state.setting = keys[0];
        selectedComponents.setting = true;
        ensureRoundDiamondPreviewForSetting();
        setSelected('setting', state.setting);
        syncReview();
      }
      return;
    }

    state.setting = firstSettingCard.dataset.value;
    selectedComponents.setting = true;
    ensureRoundDiamondPreviewForSetting();
    setSelected('setting', state.setting);
    syncReview();
    goSlideForCard(firstSettingCard);
  }

  function firstVisibleOptionValue(key) {
    var cards = qsa('[data-rb-option][data-key="' + key + '"][data-value]');
    for (var i = 0; i < cards.length; i += 1) {
      var card = cards[i];
      if (!card) continue;
      if (card.hasAttribute('hidden')) continue;
      if (card.getAttribute('aria-hidden') === 'true') continue;
      return card.dataset.value || '';
    }
    return '';
  }

  function ensureDefaultBandSelection() {
    // Keep any previously restored choice (URL/session/local state) intact.
    if (state.band_thickness && state.band_profile) {
      if (!bandSelections.thickness) bandSelections.thickness = state.band_thickness;
      if (!bandSelections.profile) bandSelections.profile = state.band_profile;
      selectedComponents.band = true;
      state.band = [bandSelections.thickness, bandSelections.profile].filter(Boolean).join(' & ');
      return;
    }

    var thickness = state.band_thickness || bandSelections.thickness || firstVisibleOptionValue('band_thickness') || 'Thin';
    var profile = state.band_profile || bandSelections.profile || firstVisibleOptionValue('band_profile') || 'Classic';

    state.band_thickness = thickness;
    state.band_profile = profile;
    bandSelections.thickness = thickness;
    bandSelections.profile = profile;
    state.band = [thickness, profile].filter(Boolean).join(' & ');
    selectedComponents.band = true;
  }

  function ensureDefaultMetalSelections() {
    var defaultMetal =
      state.metal ||
      metalSelections.ring ||
      metalSelections.setting ||
      firstVisibleOptionValue('metal') ||
      firstMetalContentKey();

    if (!metalSelections.ring) metalSelections.ring = defaultMetal;
    if (!metalSelections.setting) metalSelections.setting = defaultMetal;
    if (!state.metal) state.metal = defaultMetal;
  }

  function resetModelView() {
    if (window.BMRingBuilderR3F && typeof window.BMRingBuilderR3F.resetView === 'function') {
      window.BMRingBuilderR3F.resetView();
    }
  }

  function loadModelFromCache(url) {
    if (!url || !loader || !isValidRingBuilderModelUrl(url)) return Promise.resolve(null);
    if (!modelLoadPromiseCache[url]) {
      while (modelLoadCacheOrder.length >= RB_MODEL_CACHE_MAX) {
        evictOneModelLoadCache();
      }
      modelLoadPromiseCache[url] = new Promise(function (resolve, reject) {
        loader.load(url, resolve, undefined, reject);
      });
      // Never cache a failure permanently. A miss caused by a transient network
      // error — or by a wrong-prefix key synthesised before the setting naming
      // convention was known (e.g. Bezel) — must be retryable, otherwise the
      // poisoned rejection replays on every later step and the model can never
      // come back. Callers still receive the original rejection for this attempt.
      modelLoadPromiseCache[url].catch(function () {
        if (modelLoadPromiseCache[url]) {
          delete modelLoadPromiseCache[url];
          var oi = modelLoadCacheOrder.indexOf(url);
          if (oi >= 0) modelLoadCacheOrder.splice(oi, 1);
        }
      });
    }
    touchModelLoadCache(url);
    return modelLoadPromiseCache[url];
  }

  function cloneModelScene(sceneRoot) {
    if (!sceneRoot) return null;
    if (THREE.SkeletonUtils && typeof THREE.SkeletonUtils.clone === 'function') {
      return THREE.SkeletonUtils.clone(sceneRoot);
    }
    return sceneRoot.clone(true);
  }

  function clearSingleModel(options) {
    if (!model) return;
    if (!options || !options.keepViewer) {
      if (window.BMRingBuilderR3F && typeof window.BMRingBuilderR3F.setDisplayedRoot === 'function') {
        window.BMRingBuilderR3F.setDisplayedRoot(null);
      }
    }
    modelMaterialRefs = { metal: [], diamond: [], metalByComponent: { setting: [], band: [] } };
    var modelToDispose = model;
    model = null;
    scheduleDisposeModelResources(modelToDispose);
  }

  function ensureComponentRoot() {
    if (componentRoot) return componentRoot;
    componentRoot = new THREE.Group();
    componentRoot.name = 'rb-component-root';
    return componentRoot;
  }

  function applyNodeTransform(node, transform) {
    if (!node || !transform || typeof transform !== 'object') return;

    if (transform.position && typeof transform.position === 'object') {
      node.position.set(
        Number(transform.position.x || 0),
        Number(transform.position.y || 0),
        Number(transform.position.z || 0)
      );
    }

    if (transform.rotation && typeof transform.rotation === 'object') {
      var rotationScale = (transform.rotationUnit === 'deg' || transform.rotationDegrees) ? (Math.PI / 180) : 1;
      node.rotation.set(
        Number(transform.rotation.x || 0) * rotationScale,
        Number(transform.rotation.y || 0) * rotationScale,
        Number(transform.rotation.z || 0) * rotationScale
      );
    }

    if (transform.scale !== undefined) {
      if (typeof transform.scale === 'number') {
        node.scale.setScalar(Number(transform.scale));
      } else if (transform.scale && typeof transform.scale === 'object') {
        node.scale.set(
          Number(transform.scale.x || 1),
          Number(transform.scale.y || 1),
          Number(transform.scale.z || 1)
        );
      }
    }
  }

  function clearComponentAssembly() {
    componentRequestToken += 1;
    if (!componentRoot) {
      componentModels = { band: null, bandGems: null, setting: null, settingGems: null, diamond: null };
      return;
    }

    modelMaterialRefs = { metal: [], diamond: [], metalByComponent: { setting: [], band: [] } };

    if (window.BMRingBuilderR3F && typeof window.BMRingBuilderR3F.setDisplayedRoot === 'function') {
      window.BMRingBuilderR3F.setDisplayedRoot(null);
    }

    COMPONENT_TYPES.forEach(function (type) {
      if (!componentModels[type]) return;
      componentRoot.remove(componentModels[type]);
      scheduleDisposeModelResources(componentModels[type]);
      componentModels[type] = null;
    });

    if (componentRoot.parent) componentRoot.parent.remove(componentRoot);
    componentRoot = null;
    componentModels = { band: null, bandGems: null, setting: null, settingGems: null, diamond: null };
  }

  function fitObjectInView() { return null; }

  function getDisplayedModelRoot() {
    if (componentRoot && hasLoadedComponentAssembly()) return componentRoot;
    return model || null;
  }

  function frameShapeStoneFrontView() {
    var api = window.BMRingBuilderR3F;
    var rootObject = getDisplayedModelRoot();
    if (!api || !rootObject || typeof api.applyCameraPreset !== 'function') return false;
    rootObject.updateMatrixWorld(true);
    api.applyCameraPreset(rootObject, SHAPE_STONE_FRONT_CAMERA);
    if (typeof api.resize === 'function') api.resize();
    return true;
  }

  function scheduleShapeStoneFrontFrame() {
    rbR3fRunAfterPaint(function () {
      frameShapeStoneFrontView();
      window.setTimeout(frameShapeStoneFrontView, 160);
      window.setTimeout(frameShapeStoneFrontView, 400);
      window.setTimeout(frameShapeStoneFrontView, 750);
    });
  }

  /* Metal (3) / band (4) add width to the ring (band, side stones) that the shape
     step's framing never accounted for, so the ring overflows the viewport and
     drifts off-centre. Refit the camera to the FULL current assembly so it sits
     centred with even breathing room on both edges. */
  function frameFullRingView() {
    var api = window.BMRingBuilderR3F;
    var rootObject = getDisplayedModelRoot();
    if (!api || !rootObject || typeof api.fitCameraFromModel !== 'function') return false;
    rootObject.updateMatrixWorld(true);
    // resize first so camera.aspect is current before fitCameraFromModel reads it.
    if (typeof api.resize === 'function') api.resize();
    api.fitCameraFromModel(rootObject);
    return true;
  }

  function scheduleFullRingFrame() {
    rbR3fRunAfterPaint(function () {
      frameFullRingView();
      window.setTimeout(frameFullRingView, 160);
      window.setTimeout(frameFullRingView, 400);
      window.setTimeout(frameFullRingView, 750);
    });
  }

  function frameModelRoot(rootObject) {
    if (!rootObject) return;
    var apiFit = window.BMRingBuilderR3F;
    if (!apiFit || typeof apiFit.fitCameraFromModel !== 'function') return;
    rbR3fRunAfterPaint(function () {
      if (!rbAssemblyCameraInitialised) {
        apiFit.fitCameraFromModel(rootObject);
        rbAssemblyCameraInitialised = true;
      }
      apiFit.resize();
    });
  }

  function syncComponentVisibility() {
    COMPONENT_TYPES.forEach(function (type) {
      if (!componentModels[type]) return;
      componentModels[type].visible = false;
    });

    if (selectedComponents.setting) {
      if (componentModels.setting) componentModels.setting.visible = true;
    }
    if (selectedComponents.diamond) {
      if (componentModels.diamond) componentModels.diamond.visible = true;
      if (componentModels.settingGems) componentModels.settingGems.visible = true;
    }
    if (selectedComponents.band) {
      if (componentModels.band) componentModels.band.visible = true;
      if (componentModels.bandGems) {
        componentModels.bandGems.visible = state.band_profile === 'Diamond Band';
      }
    }
  }

  function applyGemSliderValue(componentType, count) {
    var group = componentModels[componentType];
    if (!group) return;
    group.children.forEach(function (child) {
      if (child.userData && typeof child.userData.rbGemIndex === 'number') {
        child.visible = child.userData.rbGemIndex < count;
      }
    });
  }

  function syncGemSliderVisibility() {
    var haloSlider = qs('[data-rb-gem-slider="settingGems"]');
    var bandSlider = qs('[data-rb-gem-slider="bandGems"]');

    if (haloSlider) {
      var showHalo = componentModels.settingGems && componentModels.settingGems.children.length > 0;
      haloSlider.style.display = showHalo ? '' : 'none';
      if (showHalo) {
        var haloRange = qs('[data-rb-gem-range="settingGems"]');
        if (haloRange) {
          haloRange.max = componentModels.settingGems.children.length;
          applyGemSliderValue('settingGems', parseInt(haloRange.value, 10));
        }
      }
    }

    if (bandSlider) {
      var showBand = state.band_profile === 'Diamond Band' && componentModels.bandGems && componentModels.bandGems.children.length > 0;
      bandSlider.style.display = showBand ? '' : 'none';
      if (showBand) {
        var bandRange = qs('[data-rb-gem-range="bandGems"]');
        if (bandRange) {
          bandRange.max = componentModels.bandGems.children.length;
          applyGemSliderValue('bandGems', parseInt(bandRange.value, 10));
        }
      }
    }
  }

  function loadGemArray(urlArray, componentType, transform, tags) {
    return Promise.all(urlArray.map(function (url, index) {
      return loadModelFromCache(url).then(function (gltf) {
        if (!gltf || !gltf.scene) return null;
        var node = cloneModelScene(gltf.scene);
        node.name = 'rb-gem-' + componentType + '-' + index;
        node.userData = Object.assign({}, node.userData || {}, {
          rbComponentType: componentType,
          rbGemIndex: index
        });
        if (tags && typeof tags === 'object') {
          node.userData.rbComponentTags = deepMerge({}, tags);
        }
        node.traverse(function (child) {
          if (!child || child === node) return;
          child.userData = Object.assign({}, child.userData || {}, {
            rbComponentType: componentType,
            rbGemIndex: index
          });
          if (tags) child.userData.rbComponentTags = deepMerge({}, tags);
        });
        node.visible = true;
        return node;
      }).catch(function (err) {
        console.warn('Ring Builder: gem load failed (' + componentType + ' #' + index + ')', err);
        return null;
      });
    })).then(function (gems) {
      var group = new THREE.Group();
      group.name = 'rb-component-' + componentType;
      group.userData.rbComponentType = componentType;
      if (tags) group.userData.rbComponentTags = deepMerge({}, tags);
      gems.forEach(function (g) { if (g) group.add(g); });
      applyNodeTransform(group, transform);
      return group;
    });
  }

  function loadComponentAssembly() {
    if (!threeReady || !loader || !hasComponentConfig()) {
      return Promise.resolve(false);
    }

    var componentAssets = {};
    COMPONENT_TYPES.forEach(function (type) {
      var allowed =
        (type === 'setting' && selectedComponents.setting) ||
        (type === 'settingGems' && selectedComponents.diamond) ||
        (type === 'diamond' && selectedComponents.diamond) ||
        (type === 'band' && selectedComponents.band) ||
        (type === 'bandGems' && selectedComponents.band);
      componentAssets[type] = allowed ? resolveComponentAsset(type) : null;
    });

    var hasAnyAsset = COMPONENT_TYPES.some(function (type) {
      var a = componentAssets[type];
      if (!a) return false;
      return !!(a.url || (Array.isArray(a.urls) && a.urls.length > 0));
    });

    if (!hasAnyAsset) {
      clearComponentAssembly();
      return Promise.resolve(false);
    }

    var requestToken = ++componentRequestToken;
    var jobs = COMPONENT_TYPES.map(function (type) {
      var componentAsset = componentAssets[type];
      if (!componentAsset) return Promise.resolve({ type: type, node: null, asset: null });

      if (GEM_ARRAY_TYPES[type] && Array.isArray(componentAsset.urls) && componentAsset.urls.length > 0) {
        return loadGemArray(
          componentAsset.urls,
          type,
          componentAsset.transform || null,
          componentAsset.tags || null
        ).then(function (groupNode) {
          return { type: type, node: groupNode, asset: componentAsset };
        }).catch(function (err) {
          console.warn('Ring Builder: gem array load failed (' + type + ')', err);
          return { type: type, node: null, asset: componentAsset };
        });
      }

      var componentUrl = componentAsset.url;
      if (!componentUrl) return Promise.resolve({ type: type, node: null, asset: componentAsset });

      return loadModelFromCache(componentUrl).then(function (gltf) {
        if (!gltf || !gltf.scene) return { type: type, node: null, asset: componentAsset };
        var node = cloneModelScene(gltf.scene);
        node.name = 'rb-component-' + type;
        node.userData = Object.assign({}, node.userData || {}, {
          rbComponentType: type,
          rbMaterialProfile: componentAsset.materialProfile || ''
        });
        if (componentAsset.tags && typeof componentAsset.tags === 'object') {
          node.userData.rbComponentTags = deepMerge({}, componentAsset.tags);
        }

        var componentTags = node.userData.rbComponentTags || null;
        node.traverse(function (child) {
          if (!child || child === node) return;
          child.userData = Object.assign({}, child.userData || {}, {
            rbComponentType: type
          });
          if (componentTags && !child.userData.rbComponentTags) {
            child.userData.rbComponentTags = componentTags;
          }
        });

        // Band gems now arrive as one GLB. Index the top-level children so the pavé
        // reveal slider (applyGemSliderValue → rbGemIndex) keeps working; if the file
        // merges all stones into a single child the slider becomes all-or-nothing, but
        // the stones still render.
        if (type === 'bandGems') {
          node.children.forEach(function (child, idx) {
            if (child && child.userData) child.userData.rbGemIndex = idx;
          });
        }

        applyNodeTransform(node, componentAsset.transform || null);
        return { type: type, node: node, asset: componentAsset };
      }).catch(function (err) {
        console.warn('Ring Builder: component load failed (' + type + ')', err);
        return { type: type, node: null, asset: componentAsset };
      });
    });

    return Promise.all(jobs).then(function (entries) {
      if (requestToken !== componentRequestToken) return 'stale';

      var nextModels = {
        band: null,
        bandGems: null,
        setting: null,
        settingGems: null,
        diamond: null
      };
      var loadedCount = 0;
      entries.forEach(function (entry) {
        if (!entry.node) return;
        entry.node.visible = false;
        nextModels[entry.type] = entry.node;
        loadedCount += 1;
      });

      if (!loadedCount) {
        // Nothing loaded this pass. If a model is already on screen, keep it —
        // a transient miss (naming probe not yet settled, or one failed fetch)
        // must not blank a working assembly. The next update rebuilds. Only clear
        // when there's genuinely nothing displayed yet.
        if (!hasLoadedComponentAssembly()) {
          clearComponentAssembly();
        }
        return false;
      }

      var root = ensureComponentRoot();

      COMPONENT_TYPES.forEach(function (type) {
        if (!componentModels[type]) return;
        root.remove(componentModels[type]);
        scheduleDisposeModelResources(componentModels[type]);
        componentModels[type] = null;
      });

      COMPONENT_TYPES.forEach(function (type) {
        if (!nextModels[type]) return;
        componentModels[type] = nextModels[type];
        root.add(nextModels[type]);
      });

      clearSingleModel({ keepViewer: true });

      try {
        root.position.set(0, 0, 0);
        root.rotation.set(0, 0, 0);
        root.scale.set(0.08, 0.08, 0.08);
        root.position.set(0, -0.2, 0);

        applyModelMaterialBaseline(root);
        applyActiveMetalToModel();

        root.updateMatrixWorld(true);

        var apiAsm = window.BMRingBuilderR3F;
        if (apiAsm && typeof apiAsm.setDisplayedRoot === 'function') {
          apiAsm.setDisplayedRoot(root);
          rbR3fRunAfterPaint(function () {
            if (!rbAssemblyCameraInitialised) {
              if (typeof apiAsm.applyAssemblyCameraDefaults === 'function') {
                apiAsm.applyAssemblyCameraDefaults();
              }
              rbAssemblyCameraInitialised = true;
            }
            if (typeof apiAsm.resize === 'function') apiAsm.resize();
          });
        }
      } catch (e) {
        console.warn('Ring Builder: post-load setup error', e);
      }

      syncComponentVisibility();
      syncGemSliderVisibility();

      if (rbShapeFrontFramePending) {
        rbShapeFrontFramePending = false;
        scheduleShapeStoneFrontFrame();
      }

      return true;
    });
  }

  function updateDisplayedModelFromState() {
    if (!threeReady) return;

    // Resolve the setting's filename convention (legacy `halo_…` vs doubled-prefix
    // `bezel_bezel_…`) before resolving ANY component URL. This is the single gate
    // for every render path — setting, shape, carat, step navigation — so a sparse
    // metaobject can't synthesise a wrong-prefix 404 on a later step. The probe is
    // cached and we defer at most once per setting prefix (the attempted flag stops
    // any re-entry loop if the probe can't determine a convention).
    var namingPrefix = settingNameToFilePrefix(state.setting);
    var namingKey = toLookupKey(namingPrefix);
    if (namingPrefix && namingKey) {
      var needNaming = !settingNamingCache[namingKey] && !settingNamingProbeAttempted[namingKey];
      // Naming convention is a hard prerequisite for ALL component URLs (setting, diamond,
      // band). Block the load until the probe resolves.
      if (needNaming) {
        settingNamingProbeAttempted[namingKey] = true;
        probeSettingNamingStyle(state.setting).then(function () {
          updateDisplayedModelFromState();
          if (!settingNamingCache[namingKey]) {
            settingNamingProbeAttempted[namingKey] = false;
            delete settingNamingProbePromises[namingKey];
          }
        });
        return;
      }
      // Band style probe is non-blocking: fire it so it's cached by the time the band
      // component is assembled, but don't gate setting/diamond loads on it.
      var needBand = selectedComponents.band &&
        !settingBandStyleCache[namingKey] &&
        !settingBandStyleProbeAttempted[namingKey];
      if (needBand) {
        settingBandStyleProbeAttempted[namingKey] = true;
        probeSettingBandStyle(state.setting).then(function () {
          if (!settingBandStyleCache[namingKey]) {
            settingBandStyleProbeAttempted[namingKey] = false;
            delete settingBandStyleProbePromises[namingKey];
          }
          // Re-render to pick up the resolved band style
          updateDisplayedModelFromState();
        });
      }
    }

    var updateToken = ++displayUpdateToken;

    if (hasComponentConfig()) {
      loadComponentAssembly().then(function (assembled) {
        if (updateToken !== displayUpdateToken) return;
        if (assembled === 'stale') return;
        if (!assembled && state.setting && !hasLoadedComponentAssembly()) {
          loadSettingModel(state.setting, updateToken);
        }
      });
      return;
    }

    if (state.setting) {
      loadSettingModel(state.setting, updateToken);
    }
  }

  var updateDisplayedModelForCaratTimer = null;
  function updateDisplayedModelForCaratDebounced() {
    clearTimeout(updateDisplayedModelForCaratTimer);
    updateDisplayedModelForCaratTimer = setTimeout(function () {
      if (!threeReady) return;
      updateDisplayedModelFromState();
      syncComponentVisibility();
    }, 120);
  }

  // ══════════════════════════════════════════════════════
  // INTRO / BUILDER VISIBILITY
  // ══════════════════════════════════════════════════════
  /* Discover any extra setting/shape slideshows rendered by Liquid for merchant
     ring designs (e.g. Hidden Halo, Cathedral, Bezel, Toi Et Moi) and push their
     groups into SETTING_SLIDE_GROUPS / SHAPE_SLIDE_GROUPS / SLIDESHOW_GROUPS so
     setSlideshowVisibility, ensureRbSliderReady, and refreshRbSlider all see them. */
  function discoverDynamicSlideGroups() {
    var nodes = document.querySelectorAll('[id^="rb-swiper-setting-"], [id^="rb-swiper-shape-"]');
    for (var i = 0; i < nodes.length; i += 1) {
      var id = nodes[i].id;
      if (!id) continue;
      var group = id.replace(/^rb-swiper-/, '');
      if (!group) continue;
      if (group.indexOf('setting-') === 0) {
        if (SETTING_SLIDE_GROUPS.indexOf(group) === -1) SETTING_SLIDE_GROUPS.push(group);
      } else if (group.indexOf('shape-') === 0) {
        if (SHAPE_SLIDE_GROUPS.indexOf(group) === -1) SHAPE_SLIDE_GROUPS.push(group);
      }
      if (SLIDESHOW_GROUPS.indexOf(group) === -1) SLIDESHOW_GROUPS.push(group);
    }
  }

  function showBuilder() {
    if (!intro || !builder) return;
    syncRingTitleFromRecipient();
    intro.style.display = 'none';
    builder.style.display = 'grid';

    window.scrollTo({ top: 0, behavior: 'smooth' });

    discoverDynamicSlideGroups();
    ensureDefaultSettingSelection();
    ensureDefaultBandSelection();
    ensureDefaultMetalSelections();
    applyShapeAvailabilityForSetting();
    syncSliderProgress('rb-setting-slider', 'rb-setting-progress');
    syncSliderProgress('rb-shape-slider', 'rb-shape-progress');
    goStep(1);
    requestAnimationFrame(function () {
      var initialGroup = getSlideGroupForStep(1);
      setSlideshowVisibility(initialGroup);
      SLIDESHOW_GROUPS.forEach(function (group) {
        ensureRbSliderReady(group, group === initialGroup);
      });
      syncStepSlideshow(1);
      ensureThree();
      if (threeReady) updateDisplayedModelFromState();
    });
  }

  function showIntro() {
    builder.style.display = 'none';
    intro.style.display = 'flex';
  }

  function getRecipientFirstName() {
    if (!introNameInput) return '';
    var rawName = String(introNameInput.value || '').trim();
    if (!rawName) return '';
    return rawName.split(/\s+/)[0] || '';
  }

  function syncRingTitleFromRecipient() {
    if (!ringNameLabel) return;
    var fallbackTitle = ringNameLabel.dataset.defaultRingName || 'Your Ring';
    var firstName = getRecipientFirstName();
    if (!firstName) {
      ringNameLabel.textContent = fallbackTitle;
    } else {
      ringNameLabel.textContent = firstName + "'s Ring";
    }
    syncReviewRingTitle();
  }

  function syncReviewRingTitle() {
    var reviewRingName = document.getElementById('rb-review-ring-name');
    if (!reviewRingName || !ringNameLabel) return;
    var name = (ringNameLabel.textContent || '').trim();
    reviewRingName.textContent = name || ringNameLabel.dataset.defaultRingName || 'Your Ring';
  }

  function findOptionCard(key, value) {
    if (!value) return null;
    var cards = qsa('[data-rb-option][data-key="' + key + '"]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (key === 'shape' && !isShapeStripCardVisible(card)) continue;
      if (optionValuesMatch(key, card.dataset.value, value)) return card;
    }
    return null;
  }

  /** Force the active chip to the strip's start edge. Respects the CSS
   *  `scroll-padding-left: 30px` so the selected option lines up with the step
   *  heading instead of drifting flush against the card's left edge. Shared by
   *  every horizontal option strip (setting, shape, metal, band, quality). */
  function alignCardToStripStart(card) {
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
    }
  }

  function restoreSettingStripSelection() {
    if (!state.setting) return;
    syncSliderProgress('rb-setting-slider', 'rb-setting-progress');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var card = findOptionCard('setting', state.setting);
        if (!card) return;
        alignCardToStripStart(card);
        syncSliderProgress('rb-setting-slider', 'rb-setting-progress');
      });
    });
  }

  function restoreShapeStripSelection() {
    if (!state.shape) return;
    syncSliderProgress('rb-shape-slider', 'rb-shape-progress');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var card = findOptionCard('shape', state.shape);
        if (!card) return;
        alignCardToStripStart(card);
        syncSliderProgress('rb-shape-slider', 'rb-shape-progress');
      });
    });
  }

  function optionValuesMatch(key, cardValue, wantValue) {
    if (!wantValue) return false;
    if (key === 'diamond_size') return diamondSizeValuesMatch(cardValue, wantValue);
    if (cardValue === wantValue) return true;
    return toLookupKey(cardValue) === toLookupKey(wantValue);
  }

  function ensureSelectionObjectsFromState() {
    if (state.metal) {
      if (!metalSelections.ring) metalSelections.ring = state.metal;
      if (!metalSelections.setting) metalSelections.setting = state.metal;
    }
    if (state.band_thickness && !bandSelections.thickness) {
      bandSelections.thickness = state.band_thickness;
    }
    if (state.band_profile && !bandSelections.profile) {
      bandSelections.profile = state.band_profile;
    }
    if (state.diamond_size && !diamondSelections.size) {
      diamondSelections.size = state.diamond_size;
    }
    if (state.diamond_quality && !diamondSelections.quality) {
      diamondSelections.quality = state.diamond_quality;
    }
  }

  function syncSelectionsFromStateForStep(step) {
    ensureSelectionObjectsFromState();

    if (state.setting) setSelected('setting', state.setting);
    if (step >= 2 && state.shape) setSelected('shape', state.shape);

    if (step >= 3) {
      var metalValue = metalTabMode === 'setting'
        ? (metalSelections.setting || state.metal || '')
        : (metalSelections.ring || state.metal || '');
      if (metalValue) setSelected('metal', metalValue);
    }

    if (step >= 4) {
      var thickness = bandSelections.thickness || state.band_thickness || '';
      var profile = bandSelections.profile || state.band_profile || '';
      if (thickness) setSelected('band_thickness', thickness);
      if (profile) setSelected('band_profile', profile);
    }

    if (step >= 5) {
      var dSize = diamondSelections.size || state.diamond_size || '';
      var dQual = diamondSelections.quality || state.diamond_quality || '';
      if (dSize) setSelected('diamond_size', dSize);
      if (dQual) setSelected('diamond_quality', dQual);
    }

    if (step >= 6 && state.size) setSelected('size', state.size);
  }

  function syncSelectedComponentsForStepNavigation(n, previousStep) {
    if (n < previousStep) {
      if (state.setting) selectedComponents.setting = true;
      if (state.shape) selectedComponents.diamond = true;
      if (state.band_thickness && state.band_profile) selectedComponents.band = true;
      return;
    }
    if (n > previousStep) {
      if (state.setting) selectedComponents.setting = true;
      if (n >= 2 && state.shape) selectedComponents.diamond = true;
      if (n >= 4 && state.band_thickness && state.band_profile) selectedComponents.band = true;
    }
  }

  function hasLoadedComponentAssembly() {
    return COMPONENT_TYPES.some(function (type) {
      return !!componentModels[type];
    });
  }

  // ══════════════════════════════════════════════════════
  // STEPS
  // ══════════════════════════════════════════════════════
  function goStep(n) {
    var previousStep = currentStep;
    if (n < 1) n = 1;
    if (n > TOTAL_STEPS + 1) n = TOTAL_STEPS + 1; // 7 = review

    // Defensive: if a step change ever fires while the mobile info popup
    // is open (e.g. via keyboard / programmatic call), drop the overlay
    // so users aren't stranded on stale content for the previous step.
    if (builder && builder.classList.contains('is-info-popup-open')) {
      builder.classList.remove('is-info-popup-open');
      document.body.classList.remove('rb-info-popup-lock');
    }

    syncSelectedComponentsForStepNavigation(n, previousStep);

    if (n === 3 && previousStep !== 3) {
      metalTabMode = 'ring';
    }
    if (n === 4 && previousStep !== 4) {
      bandTabMode = 'thickness';
    }
    if (n === 5 && previousStep !== 5) {
      diamondTabMode = 'size';
    }

    currentStep = n;
    if (builder) builder.classList.toggle('is-review-step', n === TOTAL_STEPS + 1);
    if (n === TOTAL_STEPS + 1) {
      var reviewLayout = document.getElementById('rb-review-layout');
      if (reviewLayout) reviewLayout.scrollTop = 0;
      syncReviewRingTitle();
    }
    if (nextBtn) nextBtn.textContent = ((n === TOTAL_STEPS) || (n === 5 && diamondTabMode === 'quality')) ? 'Review' : 'Next';

    var isMetalStep = n === 3;
    var isBandStep = n === 4;
    var isDiamondStep = n === 5;
    if (tabsNav) tabsNav.classList.toggle('metal-expanded', isMetalStep);
    if (tabsNav) tabsNav.classList.toggle('band-expanded', isBandStep);
    if (tabsNav) tabsNav.classList.toggle('diamond-expanded', isDiamondStep);

    qsa('.rb-tab').forEach(function (tab) {
      var step = parseInt(tab.dataset.step, 10);
      var active = step === n;
      if (isMetalStep && tab.dataset.metalTab) {
        active = tab.dataset.metalTab === metalTabMode;
      }
      if (isBandStep && tab.dataset.bandTab) {
        active = tab.dataset.bandTab === bandTabMode;
      }
      if (isDiamondStep && tab.dataset.diamondTab) {
        active = tab.dataset.diamondTab === diamondTabMode;
      }
      tab.classList.toggle('active', active);
    });

    // Update sliding underline position under active tab
    if (tabsNav) {
      var tabs = qsa('.rb-tab', tabsNav);
      var activeIndex = 0;
      for (var i = 0; i < tabs.length; i += 1) {
        if (tabs[i].classList.contains('active')) {
          activeIndex = i;
          break;
        }
      }
      tabsNav.style.setProperty('--rb-tab-underline-index', activeIndex);
    }

    qsa('.rb-panel').forEach(function (panel) {
      var panelStep = parseInt(panel.dataset.panel, 10);
      var isActive = panelStep === n;
      if (isActive && n === 4 && panel.dataset.bandPanel) {
        isActive = panel.dataset.bandPanel === bandTabMode;
      }
      if (isActive && n === 5 && panel.dataset.diamondPanel) {
        isActive = panel.dataset.diamondPanel === diamondTabMode;
      }
      panel.classList.toggle('active', isActive);
    });

    /* Each horizontal strip should reopen with the active chip in view when
       needed — not forced to the left edge. Run after the panel is visible
       so `getBoundingClientRect()` returns real numbers. */
    function alignStripStartFor(sliderId, cardSelector) {
      requestAnimationFrame(function () {
        var slider = document.getElementById(sliderId);
        if (!slider) return;
        var cards = Array.prototype.filter.call(
          slider.querySelectorAll(cardSelector),
          isShapeStripCardVisible
        );
        var card = null;
        for (var i = 0; i < cards.length; i++) {
          if (cards[i].classList.contains('active')) card = cards[i];
        }
        if (!card) card = cards[0];
        alignCardToStripStart(card);
      });
    }

    if (n === 2) {
      applyShapeAvailabilityForSetting();
      if (state.shape) {
        selectedComponents.diamond = true;
        scheduleShapeStoneFrontFrame();
      }
    }
    if (n === 3 || n === 4) {
      scheduleFullRingFrame();
    }
    if (n === 5) {
      applyDiamondSizeStopsForSettingShape();
    }

    ensureSelectionObjectsFromState();
    syncSelectionsFromStateForStep(n);

    if (n === 1) {
      restoreSettingStripSelection();
    }
    if (n === 2) {
      restoreShapeStripSelection();
    }
    if (n === 3) {
      alignStripStartFor('rb-metal-slider', '.rb-metal-option[data-key="metal"]');
    }
    if (n === 4) {
      alignStripStartFor('rb-band-thickness-slider', '.rb-band-option[data-key="band_thickness"]');
      alignStripStartFor('rb-band-profile-slider', '.rb-band-option[data-key="band_profile"]');
    }
    if (n === 5) {
      alignStripStartFor('rb-quality-slider', '.rb-quality-card[data-key="diamond_quality"]');
    }

    var pct = Math.min(((n - 1) / TOTAL_STEPS) * 100, 100);
    if (progress) progress.style.width = pct + '%';

    if (atcWrap) atcWrap.style.display = (n === 7) ? 'block' : 'none';

    // Init THREE once the builder is painted (rAF ensures layout is done).
    requestAnimationFrame(function () {
      requestAnimationFrame(ensureThree);
    });

    requestAnimationFrame(function () {
      setRightPanelMode(n);
    });

    requestAnimationFrame(function () {
      setStepAutoplayMode(n);
    });

    requestAnimationFrame(resizeThree);

    syncComponentVisibility();
    if (threeReady && n !== previousStep) {
      var goingBack = n < previousStep;
      if (goingBack && hasLoadedComponentAssembly()) {
        syncGemSliderVisibility();
      } else {
        updateDisplayedModelFromState();
      }
    }
    syncReview();
    syncMobileStepIndicator();
    if (n !== TOTAL_STEPS + 1) syncMobileStepContext();
    requestAnimationFrame(function () {
      syncOptionStripProgressFromState();
    });
  }

  function nextStep() {
    // Walk through sub-tabs (metal/band/diamond) before advancing to the next main step.
    if (currentStep === 3 && metalTabMode === 'ring') {
      metalTabMode = 'setting';
      goStep(3);
      return;
    }
    if (currentStep === 4 && bandTabMode === 'thickness') {
      bandTabMode = 'profile';
      goStep(4);
      return;
    }
    if (currentStep === 5 && diamondTabMode === 'size') {
      diamondTabMode = 'quality';
      goStep(5);
      return;
    }
    // Ring-size step (6) is hidden — its slider now lives in the Review step —
    // so quality advances straight to Review.
    if (currentStep === 5 && diamondTabMode === 'quality') {
      goStep(TOTAL_STEPS + 1);
      return;
    }
    if (currentStep < TOTAL_STEPS + 1) goStep(currentStep + 1);
  }

  function prevStep() {
    // Symmetric back behaviour: if we're on the second sub-tab, return to the first
    // before stepping back to the previous main step.
    // Review → diamond step (5): the ring-size step (6) is hidden.
    if (currentStep === TOTAL_STEPS + 1) {
      goStep(TOTAL_STEPS - 1);
      return;
    }
    if (currentStep === 3 && metalTabMode === 'setting') {
      metalTabMode = 'ring';
      goStep(3);
      return;
    }
    if (currentStep === 4 && bandTabMode === 'profile') {
      bandTabMode = 'thickness';
      goStep(4);
      return;
    }
    if (currentStep === 5 && diamondTabMode === 'quality') {
      diamondTabMode = 'size';
      goStep(5);
      return;
    }
    if (currentStep <= 1) { showIntro(); return; }
    goStep(currentStep - 1);
  }

  // ══════════════════════════════════════════════════════
  // SELECTIONS
  // ══════════════════════════════════════════════════════
  /** Figma M/Setting mobile: centred `n/9` step readout (nine builder checkpoints incl. sub-tabs). */
  function syncMobileStepIndicator() {
    var el = document.getElementById('rb-mobile-step');
    if (!el) return;
    if (window.matchMedia && window.matchMedia('(min-width: 901px)').matches) {
      el.textContent = '';
      return;
    }
    if (!builder || builder.classList.contains('is-review-step')) {
      el.textContent = '';
      return;
    }
    var slot = 1;
    if (currentStep === 1) slot = 1;
    else if (currentStep === 2) slot = 2;
    else if (currentStep === 3) slot = metalTabMode === 'setting' ? 4 : 3;
    else if (currentStep === 4) slot = bandTabMode === 'profile' ? 6 : 5;
    else if (currentStep === 5) slot = diamondTabMode === 'quality' ? 8 : 7;
    else if (currentStep === 6) slot = 9;
    else slot = 9;
    el.textContent = String(slot) + '/9';
  }

  function tabDefaultLabel(tab) {
    if (!tab) return '';
    if (tab.dataset.defaultLabel) return tab.dataset.defaultLabel;
    var l = qs('.rb-tab__label', tab);
    return l ? l.textContent.trim() : '';
  }

  /** Decode HTML entities from `data-rb-mobile-detail` (Liquid `escape` in attrs). */
  function decodeMobileDetailText(str) {
    if (!str || str.indexOf('&') === -1) return str || '';
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  /** Mobile (≤900px): step tab label + selected value + detail for the active builder step (not review). */
  function syncMobileStepContext() {
    var labelEl = document.getElementById('rb-setting-mobile-step-label');
    var valEl = document.getElementById('rb-setting-mobile-value');
    var descEl = document.getElementById('rb-setting-mobile-desc');
    if (!labelEl || !valEl || !descEl) return;
    if (!builder || builder.classList.contains('is-review-step')) return;

    var stepLabel = '';
    var valueLbl = '';
    var detail = '';

    if (currentStep === 1) {
      stepLabel = tabDefaultLabel(document.querySelector('.rb-tab[data-tab-key="setting"]'));
      var sc =
        qs('.rb-panel--setting .rb-setting-card.active')
        || (function () {
          var s = document.getElementById('rb-setting-slider');
          return s
            ? s.querySelector('.rb-setting-card[data-key="setting"].active')
            || s.querySelector('.rb-setting-card[data-key="setting"]')
            : null;
        })();
      if (sc) {
        var sn = qs('.rb-setting-card__name', sc);
        valueLbl = sn ? sn.textContent.trim() : '';
        detail = sc.getAttribute('data-rb-mobile-long-detail')
          || sc.getAttribute('data-rb-mobile-detail')
          || '';
        if (!detail) {
          var scd = qs('.rb-setting-card__desc', sc);
          detail = scd ? scd.textContent.trim() : '';
        }
      } else {
        valueLbl = state.setting || '';
      }
    } else if (currentStep === 2) {
      stepLabel = tabDefaultLabel(document.querySelector('.rb-tab[data-tab-key="shape"]'));
      var sh =
        qs('.rb-panel--shape .rb-setting-card.active:not([hidden])')
        || (function () {
          var s = document.getElementById('rb-shape-slider');
          if (!s) return null;
          var visible = Array.prototype.filter.call(
            s.querySelectorAll('.rb-setting-card[data-key="shape"]'),
            isShapeStripCardVisible
          );
          for (var vi = 0; vi < visible.length; vi++) {
            if (visible[vi].classList.contains('active')) return visible[vi];
          }
          return visible[0] || null;
        })();
      if (sh) {
        var shn = qs('.rb-setting-card__name', sh);
        valueLbl = shn ? shn.textContent.trim() : '';
        detail = sh.getAttribute('data-rb-mobile-long-detail')
          || sh.getAttribute('data-rb-mobile-detail')
          || '';
        if (!detail) {
          var sd = qs('.rb-setting-card__desc', sh);
          detail = sd ? sd.textContent.trim() : '';
        }
      } else {
        valueLbl = state.shape || '';
      }
    } else if (currentStep === 3) {
      if (metalTabMode === 'setting') {
        stepLabel = tabDefaultLabel(document.querySelector('.rb-tab.rb-tab--setting-metal[data-metal-tab="setting"]'));
        valueLbl = displayMetalLabel(metalSelections.setting || state.metal || '');
      } else {
        stepLabel = tabDefaultLabel(document.querySelector('.rb-tab[data-tab-key="metal"][data-metal-tab="ring"]'));
        valueLbl = displayMetalLabel(metalSelections.ring || state.metal || '');
      }
      var mo =
        qs('.rb-panel--metal .rb-metal-option.active')
        || qs('.rb-panel--metal .rb-metal-option.is-selected')
        || document.querySelector('.rb-panel--metal .rb-metal-option');
      if (mo) {
        detail = mo.getAttribute('data-rb-mobile-long-detail')
          || mo.getAttribute('data-rb-mobile-detail')
          || '';
        if (!detail) {
          var md = qs('.rb-metal-desc', mo);
          detail = md ? md.textContent.trim() : '';
        }
      }
    } else if (currentStep === 4) {
      if (bandTabMode === 'profile') {
        stepLabel = tabDefaultLabel(document.querySelector('.rb-tab.rb-tab--band-profile[data-band-tab="profile"]'));
        valueLbl = bandSelections.profile || '';
        var bpo =
          qs('.rb-panel--band[data-band-panel="profile"] .rb-band-option.active')
          || document.querySelector('.rb-panel--band[data-band-panel="profile"] .rb-band-option');
        if (bpo) {
          detail = bpo.getAttribute('data-rb-mobile-long-detail')
            || bpo.getAttribute('data-rb-mobile-detail')
            || '';
          if (!detail) {
            var bd = qs('.rb-band-desc', bpo);
            detail = bd ? bd.textContent.trim() : '';
          }
        }
      } else {
        stepLabel = tabDefaultLabel(document.querySelector('.rb-tab[data-tab-key="band"][data-band-tab="thickness"]'));
        valueLbl = bandSelections.thickness || '';
        var bto =
          qs('.rb-panel--band[data-band-panel="thickness"] .rb-band-option.active')
          || document.querySelector('.rb-panel--band[data-band-panel="thickness"] .rb-band-option');
        if (bto) {
          detail = bto.getAttribute('data-rb-mobile-long-detail')
            || bto.getAttribute('data-rb-mobile-detail')
            || '';
          if (!detail) {
            var btd = qs('.rb-band-desc', bto);
            detail = btd ? btd.textContent.trim() : '';
          }
        }
      }
    } else if (currentStep === 5) {
      if (diamondTabMode === 'quality') {
        stepLabel = tabDefaultLabel(document.querySelector('.rb-tab.rb-tab--diamond-quality[data-diamond-tab="quality"]'));
        valueLbl = diamondSelections.quality || state.diamond_quality || '';
        var qc =
          qs('.rb-panel--diamond-quality .rb-quality-card.active')
          || document.querySelector('.rb-panel--diamond-quality .rb-quality-card');
        if (qc) {
          detail = qc.getAttribute('data-rb-mobile-detail') || '';
          if (!detail) {
            var st = qs('.rb-quality-subtitle', qc);
            detail = st ? st.textContent.trim() : '';
          }
        }
      } else {
        stepLabel = tabDefaultLabel(document.querySelector('.rb-tab[data-tab-key="diamond"][data-diamond-tab="size"]'));
        var dReadout = document.getElementById('rb-dsize-readout-primary');
        valueLbl =
          (dReadout && dReadout.textContent.trim()) ||
          diamondSelections.size ||
          state.diamond_size ||
          '';
        var dst =
          qs('.rb-panel--diamond-size .rb-dsize-stop.active')
          || document.querySelector('.rb-panel--diamond-size .rb-dsize-stop[data-key="diamond_size"]');
        if (dst) {
          detail = dst.getAttribute('data-rb-mobile-detail') || '';
          if (!detail) {
            var dl = qs('.rb-dsize-label', dst);
            detail = dl ? dl.textContent.trim() : '';
          }
        }
      }
    } else if (currentStep === 6) {
      stepLabel = tabDefaultLabel(document.querySelector('.rb-tab[data-tab-key="size"]'));
      var rsp = document.getElementById('rb-size-readout-primary');
      var rss = document.getElementById('rb-size-readout-secondary');
      valueLbl = rsp ? rsp.textContent.trim() : (state.size || '');
      var rsStop =
        qs('.rb-panel--ring-size .rb-dsize-stop.active')
        || document.querySelector('.rb-panel--ring-size .rb-dsize-stop[data-key="size"]');
      if (rsStop) {
        detail = rsStop.getAttribute('data-rb-mobile-detail') || '';
      }
      if (!detail && rsp) {
        detail = rsp.textContent.trim();
        if (rss && !rss.hasAttribute('hidden') && (rss.textContent || '').trim()) {
          detail = detail + ' — ' + rss.textContent.trim();
        }
      }
    }

    var decodedDetail = decodeMobileDetailText(detail);
    // The full `detail` copy is multi-paragraph and too long for the mobile slider
    // description. Metal (3) / band (4) show the first paragraph of the infobox copy;
    // setting (1) / shape (2) show just the first sentence. ponytail: naive matchers,
    // fine for this prose (no decimals/abbreviations, paragraphs split on blank lines).
    if (decodedDetail) {
      if (currentStep === 3 || currentStep === 4) {
        var firstPara = decodedDetail.split(/\n\s*\n/)[0];
        if (firstPara) decodedDetail = firstPara.trim();
      } else {
        var firstSentence = decodedDetail.match(/^[\s\S]*?[.!?](?=\s|$)/);
        if (firstSentence) decodedDetail = firstSentence[0].trim();
      }
    }
    labelEl.textContent = stepLabel;
    valEl.textContent = valueLbl;
    descEl.textContent = decodedDetail;
    descEl.toggleAttribute('hidden', !decodedDetail);
  }

  function setSelected(key, value) {
    if (!value) return;
    var matched = findOptionCard(key, value);
    var canonical = matched ? matched.dataset.value : value;
    if (key === 'setting' && canonical) state.setting = canonical;
    if (key === 'shape' && canonical) state.shape = canonical;
    qsa('[data-key="' + key + '"]').forEach(function (card) {
      var isOn = optionValuesMatch(key, card.dataset.value, canonical);
      card.classList.toggle('active', isOn);
      if (card.hasAttribute('aria-selected')) {
        card.setAttribute('aria-selected', isOn ? 'true' : 'false');
      }
    });
    if (key === 'setting') {
      syncSliderProgress('rb-setting-slider', 'rb-setting-progress');
    } else if (key === 'shape') {
      syncSliderProgress('rb-shape-slider', 'rb-shape-progress');
      syncDiamondSizeKnob();
    } else if (key === 'diamond_size') {
      syncDiamondSizeVisual(value);
    } else if (key === 'size') {
      syncRingSizeVisual(value);
    }
    syncMobileStepContext();
    syncOptionStripProgressFromState();
  }

  /** Parse carat from a stop label such as "3.0 CT". */
  function parseDiamondSizeCarat(rawValue) {
    var range = getDiamondSizeCaratRange();
    var ct = parseFloat(normalizeCaratKey(rawValue));
    if (!Number.isFinite(ct)) ct = range.min;
    if (ct < range.min) ct = range.min;
    if (ct > range.max) ct = range.max;
    return ct;
  }

  /** Map carat range to a restrained knob scale (0.4×–0.6× of the 76px gem). */
  function diamondSizeKnobScaleFromCarat(ct) {
    var range = getDiamondSizeCaratRange();
    if (!Number.isFinite(ct)) ct = range.min;
    if (ct < range.min) ct = range.min;
    if (ct > range.max) ct = range.max;
    var span = range.max - range.min;
    if (!Number.isFinite(span) || span <= 0) span = 1;
    return 0.4 + ((ct - range.min) / span) * 0.2;
  }

  function applyDiamondSizeKnobScale(knob, ct) {
    if (!knob) return;
    var scale = diamondSizeKnobScaleFromCarat(ct).toFixed(3);
    knob.style.setProperty('--rb-dsize-knob-scale', scale);
    var track = knob.closest('.rb-dsize-track');
    if (track) track.style.setProperty('--rb-dsize-knob-scale', scale);
  }

  /**
   * Diamond size knob: visual gem mirrors Step 2 shape selection, and scales
   * linearly with carat weight from 0.4× to 0.6× across the available range.
   * Hit target grows with the knob — see `.rb-panel--diamond-size .rb-dsize-knob`.
   */
  function syncDiamondSizeKnob(value) {
    var panel = qs('.rb-panel--diamond-size');
    if (!panel) return;
    var knob = panel.querySelector('#rb-dsize-knob');
    if (!knob) return;

    // Cache the Liquid-rendered fallback gem on first run so we can revert
    // if no shape has been selected yet.
    var defaultSrc = knob.getAttribute('data-rb-default-src');
    if (!defaultSrc) {
      var existingImg = knob.querySelector('img');
      if (existingImg && existingImg.src) {
        defaultSrc = existingImg.src;
        knob.setAttribute('data-rb-default-src', defaultSrc);
      }
    }

    var shape = state.shape || '';
    var shapeSrc = shape ? getOptionIconSrc('shape', shape) : '';
    var nextSrc = shapeSrc || defaultSrc || '';
    if (nextSrc) {
      /* Update only the gem `<img>` — `setIconSlot` calls `replaceChildren`
         and would wipe the nested `#rb-dsize-readout`. */
      var knobImg = knob.querySelector(':scope > img');
      if (!knobImg) {
        knobImg = document.createElement('img');
        knobImg.width = 56;
        knobImg.height = 76;
        knobImg.loading = 'lazy';
        knob.prepend(knobImg);
      }
      if (knobImg.src !== nextSrc) knobImg.src = nextSrc;
      knobImg.alt = shape || '';
    }

    var rawValue = value;
    if (!rawValue) {
      var activeStop = panel.querySelector('.rb-dsize-stop.active');
      rawValue = activeStop ? activeStop.dataset.value : (state.diamond_size || '');
    }
    applyDiamondSizeKnobScale(knob, parseDiamondSizeCarat(rawValue));
  }

  function diamondSizeStopLeftByIndex(index, totalStops) {
    if (totalStops <= 1) return 0;
    return (index / (totalStops - 1)) * 100;
  }

  function resolveDiamondStopIndexByValue(stops, value) {
    if (!stops || !stops.length) return 0;
    for (var i = 0; i < stops.length; i++) {
      if (diamondSizeValuesMatch(stops[i].dataset.value, value)) return i;
    }
    return 0;
  }

  function syncDiamondSizeVisual(value) {
    var panel = qs('.rb-panel--diamond-size');
    if (!panel) return;
    var range = panel.querySelector('#rb-dsize-range');
    var knob = panel.querySelector('#rb-dsize-knob');
    var fill = panel.querySelector('.rb-dsize-line-fill');
    var stops = qsa('.rb-dsize-stop', panel);
    if (!range || !knob || !fill || !stops.length) return;

    var activeIndex = resolveDiamondStopIndexByValue(stops, value);
    var activeStop = stops[activeIndex];
    if (!activeStop) return;
    var left = diamondSizeStopLeftByIndex(activeIndex, stops.length);

    range.min = '0';
    range.max = String(Math.max(0, stops.length - 1));
    range.step = '1';
    range.value = String(activeIndex);
    knob.style.left = left + '%';
    fill.style.width = left + '%';
    stops.forEach(function (stop, idx) {
      stop.classList.toggle('active', idx === activeIndex);
    });
    syncDiamondSizeKnob(activeStop.dataset.value || value);
    syncDiamondSizeReadout(panel, activeStop, left);
    if (currentStep === 5 && diamondTabMode === 'size') syncMobileStepContext();
  }

  function syncRingSizeVisual(value) {
    var panel = qs('.rb-panel--ring-size');
    if (!panel) return;
    var range = panel.querySelector('#rb-size-range');
    var knob = panel.querySelector('#rb-size-knob');
    var fill = panel.querySelector('.rb-dsize-line-fill');
    var stops = qsa('.rb-dsize-stop', panel);
    if (!range || !knob || !fill || !stops.length) return;

    var activeIndex = 0;
    var matchedExact = false;
    // Match by US-numeric `data-value` OR the region-aware readout label, so
    // this works whether the caller passes the internal numeric or the
    // displayed label (letters for AU/UK).
    for (var i = 0; i < stops.length; i++) {
      var stopVal = stops[i].dataset.value;
      var stopLbl = (stops[i].getAttribute('data-readout-label') || '').trim();
      if (stopVal === value || (stopLbl && stopLbl === value)) {
        activeIndex = i;
        matchedExact = true;
        break;
      }
    }
    if (!matchedExact && value) {
      var vnum = parseFloat(String(value).replace(',', '.'));
      if (Number.isFinite(vnum)) {
        var bestJ = 0;
        var bestDd = Infinity;
        for (var j = 0; j < stops.length; j++) {
          var sv = parseFloat(String(stops[j].dataset.value || '').replace(',', '.'));
          if (!Number.isFinite(sv)) continue;
          var dd = Math.abs(sv - vnum);
          if (dd < bestDd) {
            bestDd = dd;
            bestJ = j;
          }
        }
        activeIndex = bestJ;
      }
    }
    var activeStop = stops[activeIndex];
    if (!activeStop) return;
    var left = parseFloat(activeStop.style.left || '0');
    if (!Number.isFinite(left)) left = 0;

    range.value = String(left);
    knob.style.left = left + '%';
    fill.style.width = left + '%';
    stops.forEach(function (stop, idx) {
      stop.classList.toggle('active', idx === activeIndex);
    });
    syncRingSizeReadout(panel, activeStop, left);
  }

  /**
   * Diamond size slider — drag is sourced from custom pointer handlers on the
   * visible knob/track because the native range thumb is hidden via
   * `opacity: 0` + `pointer-events: none` (the visible knob is a separate
   * `<button>` element that doesn't align with the native thumb position).
   *
   * Per-frame work is now minimal compared to the original implementation:
   *   - Stop positions cached once into a numeric array (no DOM reads in
   *     the drag loop).
   *   - `paint()` only re-iterates stops + swaps the gem image when the
   *     snapped index actually changes (`lastBestIndex` guard).
   *   - Pointer moves coalesce through a single rAF.
   *   - Native `input`/`change` listeners stay so keyboard a11y still works
   *     (Tab to focus, arrow keys nudge).
   */
  function bindDiamondSizeDrag() {
    var panel = qs('.rb-panel--diamond-size');
    if (!panel) return;

    applyDiamondSizeSparseLabels(panel, getDiamondSizePopularLabelTemplate());

    var range = panel.querySelector('#rb-dsize-range');
    var knob = panel.querySelector('#rb-dsize-knob');
    var fill = panel.querySelector('.rb-dsize-line-fill');
    var track = panel.querySelector('.rb-dsize-line');
    var stops = qsa('.rb-dsize-stop', panel);
    if (!range || !knob || !fill || !track || !stops.length) return;

    var stopPositions = [];

    function readStopPositions() {
      stopPositions = [];
      for (var i = 0; i < stops.length; i++) {
        var p = diamondSizeStopLeftByIndex(i, stops.length);
        stops[i].style.left = p + '%';
        stopPositions.push(p);
      }
      if (stops.length) {
        var firstCarat = normalizeCaratKey(stops[0].getAttribute('data-carat-key') || stops[0].dataset.value);
        var lastCarat = normalizeCaratKey(stops[stops.length - 1].getAttribute('data-carat-key') || stops[stops.length - 1].dataset.value);
        if (firstCarat) panel.setAttribute('data-rb-carat-min', firstCarat);
        if (lastCarat) panel.setAttribute('data-rb-carat-max', lastCarat);
      }
    }

    var lastBestIndex = -1;

    function refreshStopCache() {
      stops = qsa('.rb-dsize-stop', panel);
      readStopPositions();
      range.min = '0';
      range.max = String(Math.max(0, stops.length - 1));
      range.step = '1';
      lastBestIndex = -1;
    }

    readStopPositions();
    range.min = '0';
    range.max = String(Math.max(0, stops.length - 1));
    range.step = '1';

    diamondSizeDragApi = {
      refreshStopCache: refreshStopCache
    };

    var pendingPct = null;
    var rafId = 0;

    function paint(pct) {
      if (!Number.isFinite(pct)) pct = 0;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;

      var bestIndex = 0;
      var bestDist = Infinity;
      for (var i = 0; i < stopPositions.length; i++) {
        var d = Math.abs(stopPositions[i] - pct);
        if (d < bestDist) {
          bestDist = d;
          bestIndex = i;
        }
      }

      var bestStop = stops[bestIndex];
      if (!bestStop) return;
      var snappedPct = stopPositions[bestIndex];

      knob.style.left = snappedPct + '%';
      fill.style.width = snappedPct + '%';
      range.value = String(bestIndex);
      applyDiamondSizeKnobScale(knob, parseDiamondSizeCarat(bestStop.dataset.value || ''));
      syncDiamondSizeReadout(panel, bestStop, snappedPct);

      // Only mutate DOM + swap the gem when the snapped stop actually changes.
      if (bestIndex !== lastBestIndex) {
        lastBestIndex = bestIndex;
        for (var j = 0; j < stops.length; j++) {
          stops[j].classList.toggle('active', j === bestIndex);
        }
        if (bestStop) syncDiamondSizeKnob(bestStop.dataset.value || '');
        if (currentStep === 5 && diamondTabMode === 'size') syncMobileStepContext();
      }
    }

    function schedulePaint(pct) {
      pendingPct = pct;
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = 0;
        var p = pendingPct;
        pendingPct = null;
        if (p !== null) paint(p);
      });
    }

    function pctFromClientX(clientX) {
      var rect = track.getBoundingClientRect();
      if (!rect.width || !Number.isFinite(clientX)) return 0;
      var pct = ((clientX - rect.left) / rect.width) * 100;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      return pct;
    }

    function commitNearest() {
      var bestIndex = parseInt(range.value || '0', 10);
      if (Number.isNaN(bestIndex)) bestIndex = 0;
      if (bestIndex < 0) bestIndex = 0;
      if (bestIndex > stopPositions.length - 1) bestIndex = stopPositions.length - 1;
      paint(stopPositions[bestIndex]);
      var bestStop = stops[bestIndex];
      if (bestStop) bestStop.click();
    }

    // Pointer drag — Pointer Capture keeps `pointermove` flowing even when
    // the cursor leaves the small knob during a fast drag.
    var draggingPointerId = null;

    function onKnobOrTrackDown(evt) {
      if (evt.button !== undefined && evt.button !== 0) return;
      draggingPointerId = evt.pointerId;
      try { knob.setPointerCapture(draggingPointerId); } catch (e) { }
      schedulePaint(pctFromClientX(evt.clientX));
      evt.preventDefault();
    }

    function onPointerMove(evt) {
      if (draggingPointerId === null || evt.pointerId !== draggingPointerId) return;
      schedulePaint(pctFromClientX(evt.clientX));
    }

    function onPointerEnd(evt) {
      if (draggingPointerId === null || evt.pointerId !== draggingPointerId) return;
      try { knob.releasePointerCapture(draggingPointerId); } catch (e) { }
      draggingPointerId = null;
      schedulePaint(pctFromClientX(evt.clientX));
      // Run commit after the rAF paint flushes so range.value is final.
      requestAnimationFrame(commitNearest);
    }

    knob.addEventListener('pointerdown', onKnobOrTrackDown);
    knob.addEventListener('pointermove', onPointerMove);
    knob.addEventListener('pointerup', onPointerEnd);
    knob.addEventListener('pointercancel', onPointerEnd);
    track.addEventListener('pointerdown', onKnobOrTrackDown);

    // Native range still drives keyboard a11y.
    range.addEventListener('input', function () {
      var idx = parseInt(range.value || '0', 10);
      if (Number.isNaN(idx)) idx = 0;
      if (idx < 0) idx = 0;
      if (idx > stopPositions.length - 1) idx = stopPositions.length - 1;
      schedulePaint(stopPositions[idx]);
    });
    range.addEventListener('change', commitNearest);

    track.addEventListener('click', function (evt) {
      var stop = evt.target.closest('.rb-dsize-stop');
      if (!stop || !track.contains(stop)) return;
      var idx = stops.indexOf(stop);
      if (idx < 0) {
        refreshStopCache();
        idx = stops.indexOf(stop);
      }
      if (idx >= 0) paint(stopPositions[idx]);
    });

    var active = panel.querySelector('.rb-dsize-stop.active');
    if (!active && state.diamond_size) {
      for (var si = 0; si < stops.length; si++) {
        if (diamondSizeValuesMatch(stops[si].dataset.value, state.diamond_size)) {
          active = stops[si];
          break;
        }
      }
    }
    if (!active && stops.length) {
      active = panel.querySelector('.rb-dsize-stop--popular') || stops[0];
    }
    if (active) {
      syncDiamondSizeVisual(active.dataset.value || '');
    } else if (stopPositions.length) {
      paint(stopPositions[Math.min(4, stopPositions.length - 1)]);
    }
  }

  /**
   * Ring Size metaobject stops: server renders unsorted `left:0%` + `data-sort-key` (US numeric).
   * Sort by US size, space evenly 0–100%, move nodes to end of track (after knob), mark ~US 4 popular.
   */
  function syncRingSizeReadout(panel, activeStop, leftPct) {
    if (!panel || !activeStop) return;
    var readout = panel.querySelector('#rb-size-readout');
    var primary = panel.querySelector('#rb-size-readout-primary');
    var secondary = panel.querySelector('#rb-size-readout-secondary');
    if (!readout && !primary && !secondary) return;

    var label = (activeStop.getAttribute('data-readout-label') || '').trim();
    if (!label) {
      var labelEl = activeStop.querySelector('.rb-dsize-label');
      label = labelEl ? (labelEl.textContent || '').trim() : '';
    }
    var usValue = (activeStop.getAttribute('data-readout-us') || activeStop.dataset.value || '').trim();
    var pos = leftPct;
    if (!Number.isFinite(pos)) {
      pos = parseFloat(activeStop.style.left || '0');
      if (!Number.isFinite(pos)) pos = 0;
    }

    if (readout) {
      readout.classList.remove('rb-size-readout--edge-start', 'rb-size-readout--edge-end');
      if (pos <= 6) {
        readout.classList.add('rb-size-readout--edge-start');
      } else if (pos >= 94) {
        readout.classList.add('rb-size-readout--edge-end');
      }
    }

    if (primary) {
      primary.textContent = label || usValue;
    }
    if (secondary) {
      secondary.hidden = true;
      secondary.textContent = '';
    }
    if (currentStep === 6) syncMobileStepContext();
  }

  function normalizeRingSizeStopsPanel(panel) {
    var track = panel.querySelector('.rb-dsize-track');
    if (!track) return;
    var stops = qsa('.rb-dsize-stop', panel);
    if (!stops.length) return;
    var needsMetaLayout = false;
    for (var si = 0; si < stops.length; si++) {
      if (stops[si].hasAttribute('data-sort-key')) {
        needsMetaLayout = true;
        break;
      }
    }
    if (!needsMetaLayout) return;
    var arr = Array.prototype.map.call(stops, function (el) {
      var k = parseFloat(el.getAttribute('data-sort-key') || '');
      if (!Number.isFinite(k)) k = 0;
      return { el: el, k: k };
    });
    arr.sort(function (a, b) {
      return a.k - b.k;
    });
    var n = arr.length;
    arr.forEach(function (item) {
      item.el.classList.remove('rb-dsize-stop--anchor');
    });
    arr.forEach(function (item, idx) {
      var left = n <= 1 ? 0 : (idx / (n - 1)) * 100;
      item.el.style.left = left + '%';
      item.el.classList.remove('rb-dsize-stop--popular');
      track.appendChild(item.el);
    });
    var targetUs = 1650; // 16.50mm diameter × 100, matching popular handle '16-50'
    var bestI = 0;
    var bestD = Infinity;
    for (var j = 0; j < arr.length; j++) {
      if (arr[j].el.hasAttribute('data-popular')) {
        bestI = j;
        bestD = 0;
        break;
      }
    }
    if (bestD > 0) {
      for (var k = 0; k < arr.length; k++) {
        var dk = Math.abs(arr[k].k - targetUs);
        if (dk < bestD) {
          bestD = dk;
          bestI = k;
        }
      }
    }
    if (arr[bestI] && arr[bestI].el) {
      arr[bestI].el.classList.add('rb-dsize-stop--popular');
      var avgLabelEl = document.getElementById('ring-builder');
      // Region-aware average callout: prefer the metaobject row's own display
      // label (letters for AU/UK stores, numeric otherwise) so the "average"
      // matches the region system. Fall back to the US-numeric locale string
      // (`Average Size 4`) only when no region label is present.
      var avgPrefix =
        (avgLabelEl && avgLabelEl.getAttribute('data-ring-size-avg-prefix')) ||
        'Average Size';
      var regionLabel = (arr[bestI].el.getAttribute('data-readout-label') || '').trim();
      var avgLabel = regionLabel
        ? avgPrefix + ' ' + regionLabel
        : avgLabelEl && avgLabelEl.getAttribute('data-ring-size-avg-label');
      if (avgLabel) {
        var avgLblNode = arr[bestI].el.querySelector('.rb-dsize-label');
        if (avgLblNode) avgLblNode.textContent = avgLabel;
      }
    }
    // Visible dot rhythm: keep the two shoulders + the average, then add
    // several evenly-spaced middle markers so the rail reads denser in the
    // centre (matching the mockup) without exposing every metaobject size as
    // a labelled tick. Granularity is unchanged — every stop still snaps.
    var anchorMap = {};
    var dotMap = {};
    if (n > 0) {
      anchorMap[0] = true;
      anchorMap[n - 1] = true;
      anchorMap[bestI] = true;

      // Larger, evenly-spaced visible dots across the whole rail (~9 markers).
      var visibleDotTarget = Math.min(n, 9);
      if (visibleDotTarget < 2) visibleDotTarget = n;
      for (var d = 0; d < visibleDotTarget; d++) {
        var dotIdx =
          visibleDotTarget <= 1
            ? 0
            : Math.round((d / (visibleDotTarget - 1)) * (n - 1));
        dotMap[dotIdx] = true;
      }
      dotMap[0] = true;
      dotMap[n - 1] = true;
      dotMap[bestI] = true;
    }
    Object.keys(anchorMap).forEach(function (idxStr) {
      var idx = parseInt(idxStr, 10);
      if (Number.isNaN(idx) || !arr[idx] || !arr[idx].el) return;
      arr[idx].el.classList.add('rb-dsize-stop--anchor');
    });
    Object.keys(dotMap).forEach(function (idxStr) {
      var idx = parseInt(idxStr, 10);
      if (Number.isNaN(idx) || !arr[idx] || !arr[idx].el) return;
      arr[idx].el.classList.add('rb-dsize-stop--dot-visible');
    });
  }

  /**
   * Ring size slider — mirrors `bindDiamondSizeDrag`. Drag is sourced from
   * custom pointer handlers on the visible knob/track (the native range thumb
   * is hidden behind `opacity: 0` + `pointer-events: none`), but per-frame
   * work is minimal: cached stop positions, rAF-coalesced paint, and the
   * heavy readout swap only fires when the snapped stop changes.
   */
  function bindRingSizeDrag() {
    var panel = qs('.rb-panel--ring-size');
    if (!panel) return;
    normalizeRingSizeStopsPanel(panel);
    var range = panel.querySelector('#rb-size-range');
    var knob = panel.querySelector('#rb-size-knob');
    var fill = panel.querySelector('.rb-dsize-line-fill');
    var track = panel.querySelector('.rb-dsize-line');
    var stops = qsa('.rb-dsize-stop', panel);
    if (!range || !knob || !fill || !track || !stops.length) return;

    // Re-read stop positions AFTER `normalizeRingSizeStopsPanel` rewrites them.
    var stopPositions = stops.map(function (s) {
      var p = parseFloat(s.style.left || '0');
      return Number.isFinite(p) ? p : 0;
    });

    var pendingPct = null;
    var rafId = 0;
    var lastBestIndex = -1;

    function paint(pct) {
      if (!Number.isFinite(pct)) pct = 0;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;

      knob.style.left = pct + '%';
      fill.style.width = pct + '%';
      range.value = String(pct);

      var bestIndex = 0;
      var bestDist = Infinity;
      for (var i = 0; i < stopPositions.length; i++) {
        var d = Math.abs(stopPositions[i] - pct);
        if (d < bestDist) {
          bestDist = d;
          bestIndex = i;
        }
      }

      if (bestIndex !== lastBestIndex) {
        lastBestIndex = bestIndex;
        for (var j = 0; j < stops.length; j++) {
          stops[j].classList.toggle('active', j === bestIndex);
        }
      }
      syncRingSizeReadout(panel, stops[bestIndex], pct);
    }

    function schedulePaint(pct) {
      pendingPct = pct;
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = 0;
        var p = pendingPct;
        pendingPct = null;
        if (p !== null) paint(p);
      });
    }

    function pctFromClientX(clientX) {
      var rect = track.getBoundingClientRect();
      if (!rect.width || !Number.isFinite(clientX)) return 0;
      var pct = ((clientX - rect.left) / rect.width) * 100;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      return pct;
    }

    function commitNearest() {
      var raw = parseFloat(range.value || '0');
      if (!Number.isFinite(raw)) raw = 0;
      var bestIndex = 0;
      var bestDist = Infinity;
      for (var i = 0; i < stopPositions.length; i++) {
        var d = Math.abs(stopPositions[i] - raw);
        if (d < bestDist) {
          bestDist = d;
          bestIndex = i;
        }
      }
      paint(stopPositions[bestIndex]);
      var bestStop = stops[bestIndex];
      if (bestStop) bestStop.click();
    }

    // Pointer drag — mirrors `bindDiamondSizeDrag`. Pointer Capture keeps
    // `pointermove` flowing even when the cursor leaves the small knob.
    var draggingPointerId = null;

    function onKnobOrTrackDown(evt) {
      if (evt.button !== undefined && evt.button !== 0) return;
      draggingPointerId = evt.pointerId;
      try { knob.setPointerCapture(draggingPointerId); } catch (e) { }
      schedulePaint(pctFromClientX(evt.clientX));
      evt.preventDefault();
    }

    function onPointerMove(evt) {
      if (draggingPointerId === null || evt.pointerId !== draggingPointerId) return;
      schedulePaint(pctFromClientX(evt.clientX));
    }

    function onPointerEnd(evt) {
      if (draggingPointerId === null || evt.pointerId !== draggingPointerId) return;
      try { knob.releasePointerCapture(draggingPointerId); } catch (e) { }
      draggingPointerId = null;
      schedulePaint(pctFromClientX(evt.clientX));
      requestAnimationFrame(commitNearest);
    }

    knob.addEventListener('pointerdown', onKnobOrTrackDown);
    knob.addEventListener('pointermove', onPointerMove);
    knob.addEventListener('pointerup', onPointerEnd);
    knob.addEventListener('pointercancel', onPointerEnd);
    track.addEventListener('pointerdown', onKnobOrTrackDown);

    range.addEventListener('input', function () {
      var raw = parseFloat(range.value || '0');
      if (!Number.isFinite(raw)) raw = 0;
      schedulePaint(raw);
    });
    range.addEventListener('change', commitNearest);

    stops.forEach(function (stop, idx) {
      stop.addEventListener('click', function () {
        paint(stopPositions[idx]);
      });
    });

    var active = panel.querySelector('.rb-dsize-stop--popular') || panel.querySelector('.rb-dsize-stop.active');
    if (!active && stops.length) active = stops[0];
    if (active) {
      syncRingSizeVisual(active.dataset.value || '');
      // The review slider is now the sole size input: commit the default
      // (average) stop to `state.size` on init so pricing (n8n) always ships a
      // ring size, even if the user never drags. User drags still overwrite it
      // via the `[data-rb-option]` click delegation. Guarded so a restored
      // configuration's saved size is never clobbered.
      if (!state.size) {
        var rsDefaultLabel = (active.getAttribute('data-readout-label') || '').trim();
        state.size = rsDefaultLabel || active.dataset.value || '';
        setSelected('size', active.dataset.value || '');
      }
    } else {
      paint(stopPositions[Math.floor(stopPositions.length / 2)] || 50);
    }
  }

  /**
   * Render the progress segment as a real scrollbar thumb: width = visible
   * fraction of the strip, left = scroll position. This makes the bar follow
   * scrolling continuously (and the drag handler in `enableProgressBarScroll`),
   * instead of jumping between discrete chip positions.
   */
  function syncScrollThumb(slider, progress) {
    var maxScroll = slider.scrollWidth - slider.clientWidth;
    if (maxScroll <= 1 || !slider.scrollWidth) {
      progress.style.setProperty('--rb-underline-left', '0%');
      progress.style.setProperty('--rb-underline-width', '100%');
      return;
    }
    /* Fixed ~200px handle instead of a visible-fraction width. When the strip
       only slightly overflows, the fraction approached 100% and the thumb
       filled the track with almost no travel, so it felt broken to drag. */
    var trackPx = progress.getBoundingClientRect().width || slider.clientWidth;
    var thumbPx = trackPx > 0 ? Math.min(200, trackPx) : 200;
    var widthPct = trackPx > 0 ? (thumbPx / trackPx) * 100 : 24;
    if (widthPct > 100) widthPct = 100;
    var scrollRatio = slider.scrollLeft / maxScroll;
    if (scrollRatio < 0) scrollRatio = 0;
    if (scrollRatio > 1) scrollRatio = 1;
    var leftPct = scrollRatio * (100 - widthPct);
    progress.style.setProperty('--rb-underline-left', leftPct + '%');
    progress.style.setProperty('--rb-underline-width', widthPct + '%');
  }

  function syncSliderProgress(sliderId, fillId) {
    var progress = document.getElementById(fillId);
    if (!progress) return;
    var slider = document.getElementById(sliderId);
    if (!slider) return;

    /* Setting + Shape strips use a scrollbar-style thumb that tracks scroll
       position (the bar doubles as a draggable scrollbar). Other strips keep
       the active-chip underline below. */
    if (sliderId === 'rb-setting-slider' || sliderId === 'rb-shape-slider') {
      syncScrollThumb(slider, progress);
      return;
    }

    var allCards = Array.prototype.filter.call(
      slider.querySelectorAll('.rb-strip-card'),
      isShapeStripCardVisible
    );
    if (!allCards.length) return;

    var activeCard =
      slider.querySelector('.rb-strip-card[aria-selected="true"]:not([hidden])') ||
      slider.querySelector('.rb-strip-card.active:not([hidden])') ||
      allCards[0];

    var progressRect = progress.getBoundingClientRect();
    var cardRect = activeCard.getBoundingClientRect();
    var total = allCards.length;

    if (!progressRect.width || !total) {
      var idxFallback = Array.prototype.indexOf.call(allCards, activeCard);
      if (idxFallback < 0) idxFallback = 0;
      var widthFallback = 100 / total;
      var leftFallback = (idxFallback / total) * 100;
      progress.style.setProperty('--rb-underline-left', leftFallback + '%');
      progress.style.setProperty('--rb-underline-width', widthFallback + '%');
      return;
    }

    var progressWidth = progressRect.width;

    if (!cardRect.width) {
      var idxFb = Array.prototype.indexOf.call(allCards, activeCard);
      if (idxFb < 0) idxFb = 0;
      var wFb = 100 / total;
      var lFb = (idxFb / total) * 100;
      progress.style.setProperty('--rb-underline-left', lFb + '%');
      progress.style.setProperty('--rb-underline-width', wFb + '%');
      return;
    }

    /* Underline tracks the active card from its left edge. Width is the
       chip's natural width plus a small padding so the bar reads as the
       active "section" of the strip. Clamp to the track. */
    var desiredWidthPx = Math.min(cardRect.width + 32, progressWidth);
    var widthPct = (desiredWidthPx / progressWidth) * 100;
    var leftPct = ((cardRect.left - progressRect.left) / progressWidth) * 100;
    if (leftPct < 0) leftPct = 0;
    if (leftPct + widthPct > 100) leftPct = Math.max(0, 100 - widthPct);

    progress.style.setProperty('--rb-underline-left', leftPct + '%');
    progress.style.setProperty('--rb-underline-width', widthPct + '%');
  }

  /** Scroll a strip card into view only when it is meaningfully off-screen. */
  function isStripCardComfortablyVisible(card, slider) {
    if (!card || !slider) return true;
    var cr = card.getBoundingClientRect();
    var sr = slider.getBoundingClientRect();
    var pad = 12;
    return cr.left >= sr.left + pad - 1 && cr.right <= sr.right - pad + 1;
  }

  function scrollCardIntoViewIfNeeded(card, opts) {
    var slider = card && card.closest(
      '.rb-setting-slider, .rb-shape-slider, .rb-option-strip'
    );
    if (!slider || !card) return;
    if (isStripCardComfortablyVisible(card, slider)) return;
    opts = opts || {};
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var behavior = opts.instant || reducedMotion ? 'auto' : 'smooth';
    var inline = opts.inline || 'nearest';
    if (typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: behavior, block: 'nearest', inline: inline });
    }
  }

  /** @deprecated Use scrollCardIntoViewIfNeeded — left-align caused jump-left regressions. */
  function scrollCardToAlignStart(card, opts) {
    scrollCardIntoViewIfNeeded(card, opts);
  }

  /** Backwards-compat alias. */
  function scrollCardToCenter(card) {
    scrollCardIntoViewIfNeeded(card, { inline: 'nearest' });
  }

  /** Progress bar under Metal / Band / Diamond Quality horizontal strips (mobile). */
  function syncOptionStripProgressFromState() {
    if (currentStep === 3) {
      syncSliderProgress('rb-metal-slider', 'rb-metal-progress');
    }
    if (currentStep === 4) {
      syncSliderProgress('rb-band-thickness-slider', 'rb-band-thickness-progress');
      syncSliderProgress('rb-band-profile-slider', 'rb-band-profile-progress');
    }
    if (currentStep === 5 && diamondTabMode === 'quality') {
      syncSliderProgress('rb-quality-slider', 'rb-quality-progress');
    }
  }

  /* Look up the icon `<img>` already rendered inside the matching option card
     (Setting / Shape / Metal / Band) so other surfaces (breadcrumb tab,
     summary row) can mirror it. Diamond Size + Quality intentionally return
     empty — the Figma keeps those text-only. Returns the source URL or ''. */
  function getOptionIconSrc(key, value) {
    if (!key || !value) return '';
    var selector;
    switch (key) {
      case 'setting':
      case 'shape':
        selector = '.rb-setting-card[data-key="' + key + '"][data-value="' + cssEscape(value) + '"] .rb-setting-card__icon img';
        break;
      case 'metal': {
        var metalOpt = qs('.rb-metal-option[data-key="metal"][data-value="' + cssEscape(value) + '"]');
        if (!metalOpt) return '';
        var mimg = metalOpt.querySelector('.rb-metal-icon img');
        if (mimg && mimg.src) return mimg.src;
        var du = metalOpt.getAttribute('data-metal-icon-url');
        return du || '';
      }
      case 'band_thickness':
        selector = '.rb-band-option[data-key="band_thickness"][data-value="' + cssEscape(value) + '"] .rb-band-icon img';
        break;
      case 'band_profile':
        selector = '.rb-band-option[data-key="band_profile"][data-value="' + cssEscape(value) + '"] .rb-band-icon img';
        break;
      default:
        return '';
    }
    var img = qs(selector);
    return img && img.src ? img.src : '';
  }

  /* Generic icon-slot updater. Empty `src` clears the slot so its `:empty`
     CSS rule collapses it. No-ops when src + alt match the existing img so
     repeat sync calls don't re-fetch the asset. */
  function setIconSlot(slot, src, alt) {
    if (!slot) return;
    if (!src) {
      if (slot.firstChild) slot.replaceChildren();
      return;
    }
    var existing = slot.querySelector('img');
    if (existing && existing.src === src) {
      if (alt && existing.alt !== alt) existing.alt = alt;
      return;
    }
    var img = document.createElement('img');
    img.src = src;
    img.alt = alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    slot.replaceChildren(img);
  }

  function setTabIcon(tab, src, alt) {
    if (!tab) return;
    setIconSlot(qs('.rb-tab__icon', tab), src, alt);
  }

  function setTabLabel(tab, label) {
    if (!tab) return;
    var labelEl = qs('.rb-tab__label', tab);
    if (labelEl) {
      if (labelEl.textContent !== label) labelEl.textContent = label;
    } else if (tab.textContent !== label) {
      tab.textContent = label;
    }
  }

  function syncTabLabels() {
    qsa('.rb-tab[data-tab-key]').forEach(function (tab) {
      var key = tab.dataset.tabKey;
      if (!key) return;
      var step = parseInt(tab.dataset.step, 10);

      var labelEl = qs('.rb-tab__label', tab);
      var defaultLabel = tab.dataset.defaultLabel || (labelEl ? labelEl.textContent.trim() : tab.textContent.trim());
      if (!tab.dataset.defaultLabel) tab.dataset.defaultLabel = defaultLabel;

      var label = defaultLabel;
      /* Track the value used to source the per-tab icon (cloned from the
         matching option card). Stays empty for active / future tabs so the
         icon slot collapses. */
      var iconKey = '';
      var iconValue = '';
      if (key === 'metal') {
        if (currentStep === 3) {
          // Keep step labels stable while the user is actively in Metal step.
          // Selected values are shown in the panel content, not by replacing
          // the tab title as soon as the step opens.
          label = defaultLabel;
          iconKey = '';
          iconValue = '';
        } else if (currentStep > 3) {
          label = displayMetalLabel(state.metal || metalSelections.ring || metalSelections.setting || '') || defaultLabel;
          iconKey = 'metal';
          iconValue = state.metal || metalSelections.ring || metalSelections.setting || '';
        } else {
          label = defaultLabel;
        }
      } else if (key === 'band') {
        if (currentStep === 4) {
          label = 'Band Thickness';
          iconKey = 'band_thickness';
          iconValue = bandSelections.thickness || '';
        } else if (currentStep > 4) {
          label = state.band || bandSelections.thickness || bandSelections.profile || defaultLabel;
          iconKey = 'band_thickness';
          iconValue = bandSelections.thickness || '';
        } else {
          label = defaultLabel;
        }
      } else if (key === 'diamond') {
        if (currentStep === 5) {
          label = 'Diamond Size';
        } else if (currentStep > 5) {
          label = state.diamond || diamondSelections.size || diamondSelections.quality || defaultLabel;
        } else {
          label = defaultLabel;
        }
      } else if (currentStep > step && state[key]) {
        label = state[key];
        iconKey = key;
        iconValue = state[key];
      }

      setTabLabel(tab, label);
      setTabIcon(tab, getOptionIconSrc(iconKey, iconValue), label);
      tab.setAttribute('title', label);
    });

    var settingMetalTab = qs('.rb-tab[data-metal-tab="setting"]');
    if (settingMetalTab) {
      var settingMetalLabelEl = qs('.rb-tab__label', settingMetalTab);
      var settingDefault = settingMetalTab.dataset.defaultLabel || (settingMetalLabelEl ? settingMetalLabelEl.textContent.trim() : settingMetalTab.textContent.trim());
      if (!settingMetalTab.dataset.defaultLabel) settingMetalTab.dataset.defaultLabel = settingDefault;
      var settingLabel = settingDefault;
      if (currentStep > 3 && metalSelections.setting) {
        settingLabel = displayMetalLabel(metalSelections.setting) || settingDefault;
      }
      setTabLabel(settingMetalTab, settingLabel);
      setTabIcon(settingMetalTab, currentStep === 3 ? '' : getOptionIconSrc('metal', metalSelections.setting || ''), settingLabel);
      settingMetalTab.setAttribute('title', settingLabel);
    }

    var bandProfileTab = qs('.rb-tab[data-band-tab="profile"]');
    if (bandProfileTab) {
      var bandProfileLabelEl = qs('.rb-tab__label', bandProfileTab);
      var bandProfileDefault = bandProfileTab.dataset.defaultLabel || (bandProfileLabelEl ? bandProfileLabelEl.textContent.trim() : bandProfileTab.textContent.trim());
      if (!bandProfileTab.dataset.defaultLabel) bandProfileTab.dataset.defaultLabel = bandProfileDefault;
      var bandProfileLabel = bandProfileDefault;
      setTabLabel(bandProfileTab, bandProfileLabel);
      setTabIcon(bandProfileTab, getOptionIconSrc('band_profile', bandSelections.profile || ''), bandProfileLabel);
      bandProfileTab.setAttribute('title', bandProfileLabel);
    }

    var diamondQualityTab = qs('.rb-tab[data-diamond-tab="quality"]');
    if (diamondQualityTab) {
      var diamondQualityLabelEl = qs('.rb-tab__label', diamondQualityTab);
      var diamondQualityDefault = diamondQualityTab.dataset.defaultLabel || (diamondQualityLabelEl ? diamondQualityLabelEl.textContent.trim() : diamondQualityTab.textContent.trim());
      if (!diamondQualityTab.dataset.defaultLabel) diamondQualityTab.dataset.defaultLabel = diamondQualityDefault;
      var diamondQualityLabel = diamondQualityDefault;
      setTabLabel(diamondQualityTab, diamondQualityLabel);
      setTabIcon(diamondQualityTab, '', diamondQualityLabel);
      diamondQualityTab.setAttribute('title', diamondQualityLabel);
    }

    syncMobileStepContext();
    syncOptionStripProgressFromState();
  }

  function getActiveMetalValue() {
    if (metalTabMode === 'setting') {
      return metalSelections.setting || state.metal || '';
    }
    return metalSelections.ring || state.metal || '';
  }

  function normaliseMetalComponentKey(componentType) {
    var type = toLookupKey(componentType);
    if (type === 'band' || type === 'bandgems') return 'band';
    if (type === 'setting' || type === 'settinggems' || type === 'diamond') return 'setting';
    return '';
  }

  function getMetalValueForComponent(componentType) {
    var key = normaliseMetalComponentKey(componentType);
    if (key === 'band') {
      return metalSelections.ring || state.metal || '';
    }
    if (key === 'setting') {
      return metalSelections.setting || state.metal || '';
    }
    return getActiveMetalValue();
  }

  function syncMetalSideCard() {
    var activeMetal = getActiveMetalValue();
    var metalContent = resolveMetalContent(activeMetal);

    qsa('[data-rb-metal-name]').forEach(function (el) {
      el.textContent = activeMetal ? metalContent.label : 'Select A Metal';
    });
    qsa('[data-rb-metal-desc]').forEach(function (el) {
      el.textContent = activeMetal ? (metalContent.description || '') : '';
    });
    qsa('[data-rb-metal-swatch]').forEach(function (el) {
      var sw = metalContent.swatch || '';
      el.style.background = sw;
      el.style.backgroundSize = sw.indexOf('url(') !== -1 ? 'cover' : '';
      el.style.borderColor = metalContent.lightBorder ? 'rgba(25,22,21,.2)' : 'transparent';
    });
    applyActiveMetalToModel();
  }

  function getMetalPresetForValue(metalValue) {
    var activeMetal = metalValue || '';
    var fbPreset = firstMetalRenderPresetKey();
    var basePreset = (activeMetal && METAL_RENDER_PRESETS[activeMetal]) || METAL_RENDER_PRESETS[fbPreset] || METAL_RENDER_PRESETS['Yellow Gold'];
    var effectivePreset = deepMerge({}, basePreset);

    if (qualityPreset && typeof qualityPreset.envMapIntensityMultiplier === 'number') {
      effectivePreset.envMapIntensity = (effectivePreset.envMapIntensity || 1) * qualityPreset.envMapIntensityMultiplier;
    }

    if (collectionTuning && collectionTuning.metalDefaults) {
      effectivePreset = deepMerge(effectivePreset, collectionTuning.metalDefaults);
    }

    if (collectionTuning && collectionTuning.metals && collectionTuning.metals[activeMetal]) {
      effectivePreset = deepMerge(effectivePreset, collectionTuning.metals[activeMetal]);
    }

    return sanitiseMetalPreset(effectivePreset);
  }

  function getActiveMetalPreset() {
    return getMetalPresetForValue(getActiveMetalValue());
  }

  function getMetalPresetForComponent(componentType) {
    return getMetalPresetForValue(getMetalValueForComponent(componentType));
  }

  function getDiamondPreset() {
    var effectivePreset = deepMerge({}, DIAMOND_RENDER_PRESET);
    if (qualityPreset && typeof qualityPreset.envMapIntensityMultiplier === 'number') {
      effectivePreset.envMapIntensity = (effectivePreset.envMapIntensity || 1) * qualityPreset.envMapIntensityMultiplier;
    }
    if (collectionTuning && collectionTuning.diamondDefaults) {
      effectivePreset = deepMerge(effectivePreset, collectionTuning.diamondDefaults);
    }
    if (collectionTuning && collectionTuning.diamond) {
      effectivePreset = deepMerge(effectivePreset, collectionTuning.diamond);
    }
    return effectivePreset;
  }

  function looksLikeDiamondHeuristic(mesh, material) {
    var meshName = (mesh && mesh.name ? mesh.name : '') + ' ' + (material && material.name ? material.name : '');
    if (DIAMOND_HINT_RE.test(meshName)) return true;
    if (!material) return false;
    if ('transmission' in material && material.transmission > 0.55) return true;
    if (material.transparent && material.opacity < 0.98 && material.color) {
      return material.color.r > 0.8 && material.color.g > 0.8 && material.color.b > 0.8;
    }
    return false;
  }

  function looksLikeMetalHeuristic(mesh, material) {
    var meshName = (mesh && mesh.name ? mesh.name : '') + ' ' + (material && material.name ? material.name : '');
    if (METAL_HINT_RE.test(meshName)) return true;
    if (!material) return false;
    if ('metalness' in material && material.metalness >= 0.35) return true;
    return false;
  }

  function toColorHex(value, fallbackHex) {
    try {
      if (value === undefined || value === null || value === '') return fallbackHex;
      return new THREE.Color(value).getHex();
    } catch (err) {
      return fallbackHex;
    }
  }

  function getRoleConfig(role, componentType) {
    var globalRoleConfig = taggingConfig.roles && taggingConfig.roles[role] ? taggingConfig.roles[role] : {};
    var componentRoleConfig = {};
    if (
      componentType &&
      taggingConfig.byComponent &&
      taggingConfig.byComponent[componentType] &&
      taggingConfig.byComponent[componentType][role]
    ) {
      componentRoleConfig = taggingConfig.byComponent[componentType][role];
    }
    return deepMerge(globalRoleConfig, componentRoleConfig);
  }

  function readRoleValue(userData) {
    if (!userData || typeof userData !== 'object') return '';
    var roleKeys = taggingConfig.roleKeys || [];
    for (var i = 0; i < roleKeys.length; i += 1) {
      var key = roleKeys[i];
      if (userData[key] !== undefined && userData[key] !== null && userData[key] !== '') {
        return toLookupKey(userData[key]);
      }
    }
    return '';
  }

  function roleFromTaggedValue(taggedValue, componentType) {
    if (!taggedValue) return '';
    if (taggedValue === 'metal' || taggedValue === 'diamond') return taggedValue;

    var roles = ['metal', 'diamond'];
    for (var i = 0; i < roles.length; i += 1) {
      var role = roles[i];
      var roleConfig = getRoleConfig(role, componentType);
      var roleValues = roleConfig.values || [];
      for (var j = 0; j < roleValues.length; j += 1) {
        if (toLookupKey(roleValues[j]) === taggedValue) return role;
      }
    }
    return '';
  }

  function nameMatches(name, candidates) {
    if (!name || !candidates || !candidates.length) return false;
    var normalizedName = toLookupKey(name);
    for (var i = 0; i < candidates.length; i += 1) {
      if (toLookupKey(candidates[i]) === normalizedName) return true;
    }
    return false;
  }

  function roleFromTagMapValue(value, componentType) {
    if (value && typeof value === 'object') {
      if (value.role !== undefined) value = value.role;
      else if (value.value !== undefined) value = value.value;
    }
    return roleFromTaggedValue(toLookupKey(value), componentType);
  }

  function roleFromNamedMap(name, map, componentType) {
    if (!name || !map || typeof map !== 'object') return '';
    if (Object.prototype.hasOwnProperty.call(map, name)) {
      return roleFromTagMapValue(map[name], componentType);
    }

    var normalizedName = toLookupKey(name);
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i += 1) {
      if (toLookupKey(keys[i]) === normalizedName) {
        return roleFromTagMapValue(map[keys[i]], componentType);
      }
    }
    return '';
  }

  function resolveRoleFromComponentTagMap(node, material, componentType) {
    var tags = node && node.userData && node.userData.rbComponentTags ? node.userData.rbComponentTags : null;
    if (!tags || typeof tags !== 'object') return '';

    var materialName = material && material.name ? material.name : '';
    var nodeName = node && node.name ? node.name : '';

    var materialTaggedRole = roleFromNamedMap(materialName, tags.materials, componentType);
    if (materialTaggedRole) return materialTaggedRole;

    var nodeTaggedRole = roleFromNamedMap(nodeName, tags.meshes, componentType);
    if (nodeTaggedRole) return nodeTaggedRole;

    return roleFromTagMapValue(tags.defaultRole || tags.default, componentType);
  }

  function resolveMaterialRole(node, material, componentType) {
    var materialTaggedRole = roleFromTaggedValue(readRoleValue(material && material.userData ? material.userData : null), componentType);
    if (materialTaggedRole) return materialTaggedRole;

    var nodeTaggedRole = roleFromTaggedValue(readRoleValue(node && node.userData ? node.userData : null), componentType);
    if (nodeTaggedRole) return nodeTaggedRole;

    var mappedComponentRole = resolveRoleFromComponentTagMap(node, material, componentType);
    if (mappedComponentRole) return mappedComponentRole;

    var nodeName = node && node.name ? node.name : '';
    var materialName = material && material.name ? material.name : '';
    var roles = ['metal', 'diamond'];
    for (var i = 0; i < roles.length; i += 1) {
      var role = roles[i];
      var roleConfig = getRoleConfig(role, componentType);
      if (nameMatches(nodeName, roleConfig.meshNames || [])) return role;
      if (nameMatches(materialName, roleConfig.materialNames || [])) return role;
    }

    if (
      componentType &&
      taggingConfig.componentDefaults &&
      taggingConfig.componentDefaults[componentType]
    ) {
      var defaultRole = toLookupKey(taggingConfig.componentDefaults[componentType]);
      if (defaultRole === 'metal' || defaultRole === 'diamond') return defaultRole;
    }

    if (taggingConfig.mode === 'hybrid' || taggingConfig.fallbackHeuristics) {
      if (looksLikeDiamondHeuristic(node, material)) return 'diamond';
      if (looksLikeMetalHeuristic(node, material)) return 'metal';
    }

    return '';
  }

  function cloneAsPhysicalMaterial(baseMaterial) {
    if (!baseMaterial || (!baseMaterial.isMeshStandardMaterial && !baseMaterial.isMeshPhysicalMaterial)) return null;
    var material = new THREE.MeshPhysicalMaterial();
    try {
      material.copy(baseMaterial);
    } catch (e) {
      if (baseMaterial.color) material.color.copy(baseMaterial.color);
      material.metalness = baseMaterial.metalness !== undefined ? baseMaterial.metalness : 0;
      material.roughness = baseMaterial.roughness !== undefined ? baseMaterial.roughness : 0.5;
      if (baseMaterial.map) material.map = baseMaterial.map;
      if (baseMaterial.normalMap) material.normalMap = baseMaterial.normalMap;
      if (baseMaterial.envMap) material.envMap = baseMaterial.envMap;
      if (baseMaterial.envMapIntensity !== undefined) material.envMapIntensity = baseMaterial.envMapIntensity;
      material.transparent = !!baseMaterial.transparent;
      material.opacity = baseMaterial.opacity !== undefined ? baseMaterial.opacity : 1;
      material.side = baseMaterial.side !== undefined ? baseMaterial.side : THREE.FrontSide;
    }
    material.userData = Object.assign({}, baseMaterial.userData || {}, {
      rbCustomMaterial: true
    });
    return material;
  }

  function createMetalMaterial(baseMaterial, preset) {
    var material = cloneAsPhysicalMaterial(baseMaterial);
    if (!material) return null;

    material.color.setHex(toColorHex(preset.color, 0xe1bf70));
    material.metalness = numberOr(preset.metalness, 0.97);
    material.roughness = numberOr(preset.roughness, 0.19);
    if ('clearcoat' in material) material.clearcoat = numberOr(preset.clearcoat !== undefined ? preset.clearcoat : 1, 1);
    if ('clearcoatRoughness' in material) {
      material.clearcoatRoughness = numberOr(preset.clearcoatRoughness !== undefined ? preset.clearcoatRoughness : 0.03, 0.03);
    }
    if ('envMapIntensity' in material) material.envMapIntensity = numberOr(preset.envMapIntensity, 1.25);
    material.needsUpdate = true;
    return material;
  }

  function applyMetalPresetToMaterial(material, preset) {
    if (!material) return;
    var colorHex = toColorHex(preset.color, 0xe1bf70);
    material.color.setHex(colorHex);
    material.metalness = numberOr(preset.metalness, 0.97);
    material.roughness = numberOr(preset.roughness, 0.19);
    if ('clearcoat' in material) material.clearcoat = numberOr(preset.clearcoat !== undefined ? preset.clearcoat : 1, 1);
    if ('clearcoatRoughness' in material) {
      material.clearcoatRoughness = numberOr(preset.clearcoatRoughness !== undefined ? preset.clearcoatRoughness : 0.03, 0.03);
    }
    if ('envMapIntensity' in material) material.envMapIntensity = numberOr(preset.envMapIntensity, 1.25);
    material.needsUpdate = true;
  }

  function applyActiveMetalToModel() {
    if (!modelMaterialRefs.metal.length) return;

    var byComponent = modelMaterialRefs.metalByComponent || {};
    var bandRefs = Array.isArray(byComponent.band) ? byComponent.band : [];
    var settingRefs = Array.isArray(byComponent.setting) ? byComponent.setting : [];

    if (bandRefs.length || settingRefs.length) {
      var bandPreset = getMetalPresetForComponent('band');
      var settingPreset = getMetalPresetForComponent('setting');
      bandRefs.forEach(function (material) { applyMetalPresetToMaterial(material, bandPreset); });
      settingRefs.forEach(function (material) { applyMetalPresetToMaterial(material, settingPreset); });
      return;
    }

    // Legacy fallback when refs are not component-tagged yet.
    var preset = getActiveMetalPreset();
    modelMaterialRefs.metal.forEach(function (material) {
      applyMetalPresetToMaterial(material, preset);
    });
  }

  function applyModelMaterialBaseline(root) {
    modelMaterialRefs = { metal: [], diamond: [], metalByComponent: { setting: [], band: [] } };
    if (!root) return;

    var rootComponentType = root.userData && root.userData.rbComponentType ? root.userData.rbComponentType : '';

    root.traverse(function (node) {
      if (!node || !node.isMesh || !node.material) return;

      node.castShadow = true;
      node.receiveShadow = false;
      var componentType = node.userData && node.userData.rbComponentType ? node.userData.rbComponentType : rootComponentType;

      var sourceMaterials = Array.isArray(node.material) ? node.material : [node.material];
      var nextMaterials = sourceMaterials.map(function (sourceMaterial) {
        if (!sourceMaterial) return sourceMaterial;
        var materialRole = resolveMaterialRole(node, sourceMaterial, componentType);

        if (materialRole === 'diamond') {
          node.userData.rbR3fGem = true;
          return sourceMaterial;
        }

        if (materialRole === 'metal') {
          var componentKey = normaliseMetalComponentKey(componentType || rootComponentType);
          var renderPreset = getMetalPresetForComponent(componentKey || rootComponentType);
          var metalMaterial = createMetalMaterial(sourceMaterial, renderPreset);
          if (metalMaterial) {
            modelMaterialRefs.metal.push(metalMaterial);
            if (componentKey && modelMaterialRefs.metalByComponent && modelMaterialRefs.metalByComponent[componentKey]) {
              modelMaterialRefs.metalByComponent[componentKey].push(metalMaterial);
            }
            return metalMaterial;
          }
        }

        return sourceMaterial;
      });

      node.material = Array.isArray(node.material) ? nextMaterials : nextMaterials[0];
    });
  }

  /* ─── n8n dynamic pricing ───────────────────────────────── */
  var pricingWebhookMissingWarned = false;
  var ringBuilderSessionIdMemory = '';
  var rbPricingDom = { root: null, wrapper: null, value: null, review: null };

  function cacheRingBuilderPricingDom() {
    var root = document.getElementById('ring-builder');
    if (root !== rbPricingDom.root) {
      rbPricingDom.root = root;
      rbPricingDom.wrapper = root ? root.querySelector('[data-ring-builder-price-wrapper]') : null;
      rbPricingDom.value = root ? root.querySelector('[data-ring-builder-price]') : null;
    }
    if (!rbPricingDom.review || (rbPricingDom.review && !document.contains(rbPricingDom.review))) {
      rbPricingDom.review = document.querySelector('[data-ring-builder-price-review]');
    }
  }

  function debounce(fn, wait) {
    var timeout;
    function debounced() {
      var ctx = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    }
    debounced.cancel = function () { clearTimeout(timeout); };
    return debounced;
  }

  function generatePricingRequestId() {
    return 'rb_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  }

  function getRingBuilderSessionId() {
    var key = 'ring_builder_session_id';
    try {
      var sid = sessionStorage.getItem(key);
      if (sid) return sid;
      sid = 'rb_session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      try {
        sessionStorage.setItem(key, sid);
      } catch (setErr) {
        /* Quota / private mode — still return sid for this session */
      }
      return sid;
    } catch (e) {
      if (!ringBuilderSessionIdMemory) {
        ringBuilderSessionIdMemory =
          'rb_session_mem_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      }
      return ringBuilderSessionIdMemory;
    }
  }

  function getPricingWebhookConfig() {
    cacheRingBuilderPricingDom();
    var root = rbPricingDom.root;
    if (!root || !root.dataset) {
      return { webhookUrl: '', webhookToken: '', checkoutWebhookUrl: '' };
    }
    return {
      webhookUrl: (root.dataset.pricingWebhookUrl || '').trim(),
      webhookToken: (root.dataset.pricingWebhookToken || '').trim(),
      checkoutWebhookUrl: (root.dataset.pricingCheckoutWebhookUrl || '').trim(),
      checkoutAllowedHosts: (root.dataset.checkoutAllowedHosts || '').trim()
    };
  }

  function getRingBuilderPriceAmountCents(priceObj) {
    if (!priceObj) return 0;
    if (priceObj.amountCents != null) {
      var cents = Number(priceObj.amountCents);
      if (Number.isFinite(cents) && cents > 0) return Math.round(cents);
    }
    if (priceObj.amount != null) {
      var major = Number(String(priceObj.amount).replace(/,/g, ''));
      if (Number.isFinite(major) && major > 0) return Math.round(major * 100);
    }
    return 0;
  }

  function getRingBuilderCheckoutVariantId() {
    // Checkout only ever uses the freshly-priced variant from the n8n pricing
    // webhook (price.variantId). There is no static shell variant to fall back
    // to — callers must handle an empty string (block checkout / wait for price).
    var price = state.pricing && state.pricing.price;
    if (price) {
      var fromPrice = price.variantId || price.shopifyVariantId;
      if (fromPrice) return String(fromPrice).trim();
    }
    return '';
  }

  function syncRingBuilderCartVariantInputs(variantId) {
    if (!variantId) return;
    qsa('#ring-builder input[name="id"]').forEach(function (el) {
      el.value = String(variantId);
    });
  }

  function hasValidRingBuilderCheckoutPrice() {
    return getRingBuilderPriceAmountCents(state.pricing && state.pricing.price) > 0;
  }

  function getRingBuilderPricingPayloadKey(options) {
    var payload = buildRingBuilderPricingPayload(options);
    return JSON.stringify(payload.selectedOptions) + '|' + payload.currency + '|' + payload.locale;
  }

  function canReuseRingBuilderCheckoutPrice() {
    if (state.pricing.isLoading || state.pricing.error) return false;
    if (!hasValidRingBuilderCheckoutPrice()) return false;
    if (!getRingBuilderCheckoutVariantId({ requireFromPrice: true })) return false;
    return getRingBuilderPricingPayloadKey() === state.pricing.lastCheckoutPayloadKey;
  }

  /* ponytail: n8n Respond nodes often omit requestId or nest price fields at the
     top level — normalise before strict validation so a successful workflow
     is not rejected by the theme. */
  function normalizeRingBuilderPricingResponse(data, payload) {
    if (!data || typeof data !== 'object') return data;
    var next = data;
    if (next.body && typeof next.body === 'object' && next.status == null) {
      next = next.body;
    }
    if (next.json && typeof next.json === 'object' && next.status == null) {
      next = next.json;
    }
    if (!next.requestId && payload && payload.requestId) {
      next.requestId = payload.requestId;
    }
    if (next.status === 'success') {
      if (!next.price || typeof next.price !== 'object') {
        next.price = {};
      }
      if (next.variantId && !next.price.variantId) {
        next.price.variantId = next.variantId;
      }
      if (next.shopifyVariantId && !next.price.variantId) {
        next.price.variantId = next.shopifyVariantId;
      }
      if (next.amountCents != null && next.price.amountCents == null) {
        next.price.amountCents = next.amountCents;
      }
      if (next.amount != null && next.price.amount == null && next.price.amountCents == null) {
        next.price.amount = next.amount;
      }
      if (next.formatted != null && next.price.formatted == null) {
        next.price.formatted = next.formatted;
      }
      if (next.currency != null && next.price.currency == null) {
        next.price.currency = next.currency;
      }
    }
    return next;
  }

  function waitForRingBuilderPriceRefresh(maxMs) {
    var timeoutMs = typeof maxMs === 'number' ? maxMs : 12000;
    return new Promise(function (resolve) {
      state.pricing.checkoutBusy = true;
      requestRingBuilderPriceDebounced.cancel();

      function finish(ok) {
        state.pricing.checkoutBusy = false;
        resolve(!!ok);
      }

      if (canReuseRingBuilderCheckoutPrice()) {
        finish(true);
        return;
      }

      var started = Date.now();
      requestRingBuilderPrice({ intent: 'checkout' });
      (function poll() {
        if (!state.pricing.isLoading) {
          finish(!state.pricing.error && hasValidRingBuilderCheckoutPrice());
          return;
        }
        if (Date.now() - started >= timeoutMs) {
          finish(false);
          return;
        }
        setTimeout(poll, 80);
      })();
    });
  }

  function collectRingBuilderLineItemProperties(form) {
    var props = {};
    if (!form) return props;
    qsa('input[name^="properties["]', form).forEach(function (el) {
      var match = el.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      var value = el.value != null ? String(el.value).trim() : '';
      if (!value) return;
      props[match[1]] = value;
    });
    return props;
  }

  function resolveCartAddJsonUrl(form) {
    var action = form && form.getAttribute('action');
    if (action) {
      try {
        var url = new URL(action, window.location.href);
        if (url.pathname.endsWith('/cart/add')) url.pathname += '.js';
        else if (!url.pathname.endsWith('/cart/add.js')) url.pathname = url.pathname.replace(/\/?$/, '') + '/cart/add.js';
        return url.href;
      } catch (e) { /* fall through */ }
    }
    var routes = window.FoxTheme && window.FoxTheme.routes;
    if (routes && routes.cart_add_url) {
      return routes.cart_add_url.indexOf('.js') === routes.cart_add_url.length - 3
        ? routes.cart_add_url
        : routes.cart_add_url.replace(/\/?$/, '') + '.js';
    }
    return '/cart/add.js';
  }

  function normalizeCheckoutHost(host) {
    // Treat `www.` and apex as the same registrable domain. Draft-order invoice
    // URLs come back on the shop's primary domain (e.g. www.bondedmatter.com)
    // which may differ from the storefront origin / `request.host` (apex), so an
    // exact match would wrongly reject a valid, trusted checkout URL.
    return String(host || '').toLowerCase().replace(/^www\./, '');
  }

  function isAllowedCheckoutUrl(url) {
    try {
      var parsed = new URL(url, window.location.href);
      if (parsed.protocol !== 'https:' && parsed.protocol !== window.location.protocol) return false;
      if (parsed.origin === window.location.origin) return true;
      var target = normalizeCheckoutHost(parsed.hostname);
      var shop = window.Shopify && window.Shopify.shop;
      if (shop && target === normalizeCheckoutHost(shop)) return true;
      // ponytail: only trust the current shop's myshopify host + Shopify's own checkout domain; blanket *.myshopify.com would allow cross-shop redirects
      if (parsed.hostname === 'checkout.shopify.com') return true;
      // Shop's primary/custom domains (from Liquid, trusted). Draft-order invoice
      // URLs use the shop's primary domain, which can differ from the storefront
      // origin (e.g. www.bondedmatter.com vs www.bondedmatter.com.au).
      var allowed = getPricingWebhookConfig().checkoutAllowedHosts;
      if (allowed) {
        var hosts = allowed.split(',');
        for (var i = 0; i < hosts.length; i++) {
          var h = normalizeCheckoutHost(hosts[i].trim());
          if (h && target === h) return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  function isCartAddSameOrigin(form) {
    try {
      var action = (form && form.getAttribute('action')) || '/cart/add';
      return new URL(action, window.location.href).origin === window.location.origin;
    } catch (e) {
      return true;
    }
  }

  function ringBuilderDelay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function isRingBuilderCartSoldOutError(message) {
    return /sold out|unavailable/i.test(String(message || ''));
  }

  function getRingBuilderFormReturnTo(form) {
    var el = form && form.querySelector('input[name="return_to"]');
    return el && el.value ? el.value : '/cart';
  }

  function isRingBuilderBuyNowCheckoutForm(form) {
    if (!form) return false;
    if (form.classList.contains('rb-review-buy-form')) return true;
    var returnTo = getRingBuilderFormReturnTo(form);
    return returnTo === '/checkout' || String(returnTo).indexOf('/checkout') !== -1;
  }

  async function requestRingBuilderCheckoutUrl(form) {
    var conf = getPricingWebhookConfig();
    if (!conf.checkoutWebhookUrl) {
      console.info('[Ring Builder] Checkout webhook URL not configured — using cart/add fallback.');
      return null;
    }

    var payload = buildRingBuilderPricingPayload({ intent: 'checkout' });
    if (state.pricing && state.pricing.lastSuccessfulRequestId) {
      payload.requestId = state.pricing.lastSuccessfulRequestId;
    }
    payload.lineItemProperties = collectRingBuilderLineItemProperties(form);
    payload.amountCents = getRingBuilderPriceAmountCents(state.pricing && state.pricing.price);
    payload.variantId = getRingBuilderCheckoutVariantId({ requireFromPrice: true });

    console.info('[Ring Builder] POST checkout webhook.', {
      url: conf.checkoutWebhookUrl.replace(/rb_ring_token=[^&]+/i, 'rb_ring_token=REDACTED'),
      requestId: payload.requestId,
      variantId: payload.variantId,
      amountCents: payload.amountCents,
      currency: payload.currency,
      locale: payload.locale
    });

    var response = await fetch(conf.checkoutWebhookUrl, {
      method: 'POST',
      headers: buildRingBuilderPricingHeaders(conf.webhookToken),
      body: JSON.stringify(payload)
    });
    var text = await response.text();
    if (!text) {
      console.warn('[Ring Builder] Checkout webhook returned empty body (HTTP ' + response.status + ').');
      return null;
    }
    var data;
    try {
      data = JSON.parse(text);
    } catch (parseErr) {
      console.warn('[Ring Builder] Checkout webhook returned non-JSON.', parseErr);
      return null;
    }
    if (Array.isArray(data) && data.length) {
      data = data[0];
    }
    // Accept the checkout URL when the webhook returns a usable `checkoutUrl` on
    // a 2xx response. Some n8n "Respond to Webhook" nodes omit an explicit
    // success/status flag (Response Body = { checkoutUrl }), so requiring one
    // wrongly rejected valid responses and fell back to the (unusable for
    // draft-order variants) /cart/add path. Only bail on a non-2xx status, an
    // explicit failure flag, or a missing/blank URL — the URL is still
    // host-validated by isAllowedCheckoutUrl before we redirect.
    var hasCheckoutUrl =
      data && typeof data.checkoutUrl === 'string' && data.checkoutUrl.trim() !== '';
    var explicitFailure =
      data && (data.success === false || data.status === 'error' || data.status === 'failed');
    if (!response.ok || explicitFailure || !hasCheckoutUrl) {
      console.warn('[Ring Builder] Checkout webhook failed — falling back to cart/add.', {
        httpStatus: response.status,
        status: data && data.status,
        success: data && data.success,
        message: data && data.message,
        hasCheckoutUrl: !!hasCheckoutUrl
      });
      return null;
    }
    console.info('[Ring Builder] Checkout webhook succeeded.', {
      checkoutUrl: String(data.checkoutUrl)
    });
    return String(data.checkoutUrl);
  }

  async function addRingBuilderLineViaCartJs(form) {
    /* ponytail: checkout must use the freshly-priced variant from the webhook
       response (price.variantId). Never fall back to the shell/product variant
       here — that variant is inventory-tracked and Shopify rejects it as sold
       out / unavailable. Fresh Admin API variants can take a moment to become
       sellable on the storefront — retry sold-out responses briefly. */
    var variantId = getRingBuilderCheckoutVariantId({ requireFromPrice: true });
    if (!variantId) throw new Error('missing_price_variant_id');

    var cartAddUrl = resolveCartAddJsonUrl(form);
    var payload = {
      items: [{
        id: Number(variantId),
        quantity: 1,
        properties: collectRingBuilderLineItemProperties(form)
      }]
    };
    var maxAttempts = 4;
    var lastError = 'cart_add_failed';

    for (var attempt = 1; attempt <= maxAttempts; attempt++) {
      if (attempt > 1) {
        await ringBuilderDelay(350 * attempt);
      }

      var response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });

      var data = null;
      try {
        data = await response.json();
      } catch (e) { /* ignore */ }

      if (response.ok) return data;

      lastError = data && data.description ? data.description : 'cart_add_failed';
      if (!isRingBuilderCartSoldOutError(lastError) || attempt === maxAttempts) {
        throw new Error(lastError);
      }
      console.warn(
        '[Ring Builder] Cart add sold out for new variant — retrying (' +
        attempt +
        '/' +
        maxAttempts +
        ').',
        { variantId: variantId, description: lastError }
      );
    }

    throw new Error(lastError);
  }

  function getCheckoutSplashMessages(el) {
    var raw = el && el.getAttribute('data-rb-splash-messages');
    if (!raw) return [];
    try {
      var list = JSON.parse(raw);
      return Array.isArray(list) ? list.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  var checkoutSplashMessageTimer = null;

  function stopCheckoutSplashMessageRotation() {
    if (!checkoutSplashMessageTimer) return;
    clearInterval(checkoutSplashMessageTimer);
    checkoutSplashMessageTimer = null;
  }

  function startCheckoutSplashMessageRotation(el) {
    stopCheckoutSplashMessageRotation();
    var messages = getCheckoutSplashMessages(el);
    if (!messages.length) return;
    var textEl = el.querySelector('.rb-checkout-splash__text');
    if (!textEl) return;

    var index = 0;
    textEl.textContent = messages[index];
    if (messages.length < 2) return;

    checkoutSplashMessageTimer = setInterval(function () {
      index = (index + 1) % messages.length;
      textEl.textContent = messages[index];
    }, 1500);
  }

  function showCheckoutSplash() {
    var el = document.getElementById('rb-checkout-splash');
    if (!el) return;
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    startCheckoutSplashMessageRotation(el);
    // rAF so the browser paints the `hidden` → shown display change before the
    // opacity transition kicks in; otherwise the fade-in is skipped.
    requestAnimationFrame(function () { el.classList.add('is-active'); });
  }

  function hideCheckoutSplash() {
    var el = document.getElementById('rb-checkout-splash');
    if (!el) return;
    stopCheckoutSplashMessageRotation();
    el.classList.remove('is-active');
    el.hidden = true;
    el.setAttribute('aria-hidden', 'true');
  }

  async function handleRingBuilderCheckoutSubmit(form, triggerEl) {
    if (!form || form.dataset.rbCheckoutBusy === 'true') return;
    form.dataset.rbCheckoutBusy = 'true';
    if (triggerEl) triggerEl.disabled = true;
    // Keep the splash up through the redirect; only tear it down on the paths
    // that stay on this page (validation failure / error).
    var willRedirect = false;
    showCheckoutSplash();

    try {
      syncReview({ skipPricing: true });
      var priced = await waitForRingBuilderPriceRefresh(12000);
      if (!priced || !hasValidRingBuilderCheckoutPrice()) {
        var pricingFailed = !!(state.pricing && state.pricing.error);
        console.warn('[Ring Builder] Checkout blocked — ' + (pricingFailed ? 'pricing failed.' : 'calculated price not ready.'));
        hideCheckoutSplash();
        window.alert(pricingFailed
          ? 'Unable to calculate the price for this configuration. Please try again.'
          : 'Price is still calculating. Wait a moment and try again.');
        return;
      }

      var checkoutVariantId = getRingBuilderCheckoutVariantId({ requireFromPrice: true });
      if (!checkoutVariantId) {
        console.warn(
          '[Ring Builder] Checkout blocked — pricing webhook did not return price.variantId. ' +
          'Refusing to submit the shell/product variant to /cart/add.'
        );
        hideCheckoutSplash();
        window.alert('Could not prepare checkout for this configuration. Please try again.');
        return;
      }
      syncRingBuilderCartVariantInputs(checkoutVariantId);

      var checkoutUrl = null;
      var useCheckoutWebhook =
        isRingBuilderBuyNowCheckoutForm(form) && !!getPricingWebhookConfig().checkoutWebhookUrl;

      if (useCheckoutWebhook) {
        checkoutUrl = await requestRingBuilderCheckoutUrl(form);
        if (checkoutUrl) {
          if (isAllowedCheckoutUrl(checkoutUrl)) {
            willRedirect = true;
            window.location.assign(checkoutUrl);
            return;
          }
          console.warn(
            '[Ring Builder] Checkout URL rejected — untrusted host, falling back to cart/add.',
            checkoutUrl
          );
          checkoutUrl = null;
        }
      }

      /* ponytail: brief cushion after Admin API variant create before cart/add */
      await ringBuilderDelay(300);

      /* ponytail: Shopify /cart/add cannot set arbitrary prices — checkout total
         comes from the variant catalog price. n8n must either (a) return
         price.variantId for a variant priced to the calculation, (b) PATCH the
         shell variant price via Admin API before the pricing response, or (c)
         expose pricing_checkout_webhook_url that returns a draft-order checkoutUrl. */
      if (isCartAddSameOrigin(form)) {
        try {
          await addRingBuilderLineViaCartJs(form);
          willRedirect = true;
          window.location.assign(getRingBuilderFormReturnTo(form));
          return;
        } catch (cartErr) {
          if (cartErr && cartErr.message === 'missing_price_variant_id') throw cartErr;
          console.warn('[Ring Builder] AJAX cart add failed.', cartErr);
          hideCheckoutSplash();
          window.alert(
            isRingBuilderCartSoldOutError(cartErr && cartErr.message)
              ? 'This configuration is not available yet. Wait a moment and try again.'
              : 'Could not add this ring to checkout. Please try again.'
          );
          return;
        }
      }

      /* Local theme dev only: cross-origin POST to shop.url escapes the proxy. */
      willRedirect = true;
      form.submit();
    } catch (err) {
      console.warn('[Ring Builder] Checkout failed.', err);
      hideCheckoutSplash();
      window.alert('Could not start checkout. Please try again.');
    } finally {
      delete form.dataset.rbCheckoutBusy;
      if (triggerEl) triggerEl.disabled = false;
      if (!willRedirect) hideCheckoutSplash();
    }
  }

  function bindRingBuilderCheckoutForms() {
    qsa('.rb-ring-builder-checkout-form').forEach(function (form) {
      if (form.dataset.rbCheckoutBound === 'true') return;
      form.dataset.rbCheckoutBound = 'true';
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var trigger = ev.submitter || form.querySelector('[type="submit"]');
        handleRingBuilderCheckoutSubmit(form, trigger);
      });
    });
  }

  /**
   * Build request headers for the pricing webhook.
   * The auth token is sent as a custom header rather than a query param to
   * avoid leaking it in server access logs, proxy logs and Referer headers.
   * application/json already triggers CORS preflight so a custom header
   * adds no extra round-trip cost; n8n reads `header.x-rb-ring-token`.
   */
  function buildRingBuilderPricingHeaders(token) {
    var headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['X-RB-Ring-Token'] = token;
    }
    return headers;
  }

  function getRingBuilderPricingCopy() {
    cacheRingBuilderPricingDom();
    var root = rbPricingDom.root;
    var d = root && root.dataset ? root.dataset : {};
    return {
      calculating: d.pricingTextCalculating || 'Calculating price…',
      incomplete: d.pricingTextIncomplete || 'Select options to calculate price',
      error: d.pricingZeroMoney || '0',
      defaultPrice: d.pricingDefaultPrice || ''
    };
  }

  function slugifyRingBuilderOptionId(value) {
    if (!value) return '';
    var s = String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '');
    return s || 'option';
  }

  /* Canonical IDs — mirrors n8n Load Pricing Config `aliases` (keys = slugified UI labels). */
  var PRICING_ID_ALIASES = {
    setting: {
      'three-stone': 'trilogy',
      'three-stone-trilogy': 'trilogy',
      'hidden-halo': 'hidden-halo',
      'toi-et-moi': 'toi-et-moi',
      'toietmoi': 'toi-et-moi',
      solitaire: 'solitaire',
      halo: 'halo',
      trilogy: 'trilogy'
    },
    metal: {
      '950-platinum': 'platinum',
      platinum: 'platinum',
      '18k-yellow-gold': 'yellow-gold',
      'yellow-gold': 'yellow-gold',
      '18k-white-gold': 'white-gold',
      'white-gold': 'white-gold',
      '18k-rose-gold': 'rose-gold',
      'rose-gold': 'rose-gold',
      /* Merchant labels e.g. "18K Gold" → slug `18k-gold` (n8n expects `yellow-gold`) */
      '18k-gold': 'yellow-gold',
      '18-k-gold': 'yellow-gold',
      gold: 'yellow-gold',
      '18k': 'yellow-gold',
      '18-k': 'yellow-gold'
    },
    stoneShape: {
      'round-brilliant': 'round',
      brilliant: 'round',
      round: 'round',
      oval: 'oval',
      cushion: 'cushion',
      emerald: 'emerald',
      radiant: 'radiant',
      pear: 'pear',
      asscher: 'asscher',
      princess: 'princess',
      marquise: 'marquise',
      heart: 'heart',
      'elongated-cushion': 'elongated-cushion',
      'elongated-hexagon': 'elongated-hexagon',
      'elongated-radiant': 'elongated-radiant',
      hexagon: 'hexagon',
      baguette: 'baguette',
      square: 'square',
      trillion: 'trillion',
      mixed: 'mixed'
    },
    bandThickness: {
      thin: 'thin',
      medium: 'medium',
      bold: 'bold',
      thick: 'bold',
      'thick-band': 'bold'
    },
    bandProfile: {
      straight: 'straight',
      classic: 'straight',
      'classic-straight': 'straight',
      'plain-band': 'straight',
      rounded: 'rounded',
      'diamond-band': 'pave',
      pave: 'pave'
    },
    diamondQuality: {
      signature: 'signature',
      standard: 'signature',
      'sparkle-signature-quality': 'signature',
      'sparkle-signature': 'signature',
      premium: 'premium',
      'signature-perfect-precision': 'premium',
      'perfect-precision': 'premium'
    }
  };

  function resolveMetalPricingId(slug, rawLabel) {
    var map = PRICING_ID_ALIASES.metal;
    if (map[slug]) return map[slug];
    var lower = String(rawLabel || '').toLowerCase();
    if (/platinum|pt\s*950|950\s*pt/.test(lower) || slug.indexOf('platinum') >= 0) {
      return 'platinum';
    }
    if (/white/.test(lower) || slug.indexOf('white') >= 0) {
      return 'white-gold';
    }
    if (/rose|pink/.test(lower) || slug.indexOf('rose') >= 0) {
      return 'rose-gold';
    }
    if (
      /yellow/.test(lower) ||
      slug === '18k-gold' ||
      slug === '18-k-gold' ||
      slug === 'gold' ||
      /^18k?$/.test(slug) ||
      (slug.indexOf('gold') >= 0 && slug.indexOf('white') < 0 && slug.indexOf('rose') < 0)
    ) {
      return 'yellow-gold';
    }
    return slug;
  }

  function resolveSettingPricingId(slug, rawLabel) {
    var map = PRICING_ID_ALIASES.setting;
    if (map[slug]) return map[slug];
    var lower = String(rawLabel || '').toLowerCase();
    if (/trilogy|three[\s-]*stone/.test(lower)) return 'trilogy';
    if (/hidden[\s-]*halo/.test(lower)) return 'hidden-halo';
    if (/toi[\s-]*et[\s-]*moi/.test(lower)) return 'toi-et-moi';
    if (/solitaire/.test(lower)) return 'solitaire';
    if (/\bhalo\b/.test(lower)) return 'halo';
    return slug;
  }

  function normalisePricingId(pricingType, slugOrLabel) {
    if (!slugOrLabel) return '';
    var raw = String(slugOrLabel).trim();
    var slug =
      raw.indexOf(' ') >= 0 || /[A-Z]/.test(raw)
        ? slugifyRingBuilderOptionId(raw)
        : raw.toLowerCase();
    var map = PRICING_ID_ALIASES[pricingType] || {};
    if (map[slug]) return map[slug];

    if (pricingType === 'metal') return resolveMetalPricingId(slug, raw);
    if (pricingType === 'setting') return resolveSettingPricingId(slug, raw);

    return slug;
  }

  function buildPricingOptionPayload(pricingType, label) {
    var raw = label == null ? '' : String(label).trim();
    if (!raw) return null;
    var id = normalisePricingId(pricingType, raw);
    if (!id) return null;
    return { id: id, label: raw };
  }

  function normaliseRingSizeId(raw) {
    var s = String(raw || '').replace(',', '.').trim();
    if (!s) return '';
    var n = parseFloat(s);
    if (!Number.isFinite(n)) return normalisePricingId('ringSize', s) || slugifyRingBuilderOptionId(s);
    if (n % 1 === 0) return n.toFixed(1);
    return String(n);
  }

  function parseCaratNumericFromDiamondSize(raw) {
    var m = String(raw || '').match(/(\d+(?:[.,]\d+)?)/);
    if (!m) return null;
    var n = parseFloat(m[1].replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function getRingSizePayload() {
    var val = state.size;
    if (!val) return null;
    var valueRaw = String(val).trim();
    var label = valueRaw;
    var panel = document.querySelector('.rb-panel--ring-size');
    if (panel) {
      var active = panel.querySelector('.rb-dsize-stop.active');
      if (active) {
        var usVal = (active.getAttribute('data-readout-us') || '').trim();
        if (usVal) valueRaw = usVal;
        var rl = (active.getAttribute('data-readout-label') || '').trim();
        if (!rl) {
          var lblEl = active.querySelector('.rb-dsize-label');
          rl = lblEl ? (lblEl.textContent || '').trim() : '';
        }
        if (rl) label = rl;
      }
    }
    var id = normaliseRingSizeId(valueRaw);
    if (!id) return null;
    return { id: id, label: label };
  }

  function hasAnyPricingSelection() {
    return !!(
      state.setting ||
      state.shape ||
      state.metal ||
      (metalSelections && (metalSelections.ring || metalSelections.setting)) ||
      (bandSelections && bandSelections.thickness) ||
      (bandSelections && bandSelections.profile) ||
      state.band_thickness ||
      state.band_profile ||
      (diamondSelections && diamondSelections.size) ||
      (diamondSelections && diamondSelections.quality) ||
      state.diamond_size ||
      state.diamond_quality ||
      state.size
    );
  }

  function canCalculateRingBuilderPrice() {
    return hasAnyPricingSelection();
  }

  function getRingBuilderPricingCurrency() {
    var root = document.getElementById('ring-builder');
    var d = root && root.dataset;
    if (d && d.pricingCurrency) return d.pricingCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    if (window.FoxTheme && window.FoxTheme.settings && window.FoxTheme.settings.bmActiveCurrency) {
      return window.FoxTheme.settings.bmActiveCurrency;
    }
    return (d && d.pricingDefaultCurrency) || 'AUD';
  }

  function getRingBuilderPricingLocale() {
    var root = document.getElementById('ring-builder');
    var d = root && root.dataset;
    return (d && d.pricingLocale) || 'en-AU';
  }

  function buildRingBuilderPricingPayload(options) {
    var cur = getRingBuilderPricingCurrency();
    var loc = getRingBuilderPricingLocale();
    var selectedOptions = {};

    var set = buildPricingOptionPayload('setting', state.setting);
    if (set) selectedOptions.setting = set;

    var metalBlock = {};
    var ringM = buildPricingOptionPayload('metal', metalSelections && metalSelections.ring);
    var setM = buildPricingOptionPayload('metal', metalSelections && metalSelections.setting);
    if (ringM) metalBlock.ring = ringM;
    if (setM) metalBlock.setting = setM;
    if (!ringM && !setM && state.metal) {
      var metalFb = buildPricingOptionPayload('metal', state.metal);
      if (metalFb) {
        if (metalTabMode === 'setting') metalBlock.setting = metalFb;
        else metalBlock.ring = metalFb;
      }
    }
    if (metalBlock.ring || metalBlock.setting) {
      selectedOptions.metal = metalBlock;
    }

    var bandBlock = {};
    var bt = buildPricingOptionPayload(
      'bandThickness',
      (bandSelections && bandSelections.thickness) || state.band_thickness
    );
    var bp = buildPricingOptionPayload(
      'bandProfile',
      (bandSelections && bandSelections.profile) || state.band_profile
    );
    if (bt) bandBlock.thickness = bt;
    if (bp) bandBlock.profile = bp;
    if (bandBlock.thickness || bandBlock.profile) {
      selectedOptions.band = bandBlock;
    }

    var sh = buildPricingOptionPayload('stoneShape', state.shape);
    if (sh) selectedOptions.stoneShape = sh;

    var diamondBlock = {};
    var caratLabel = (diamondSelections && diamondSelections.size) || state.diamond_size || '';
    if (caratLabel) {
      var caratNum = parseCaratNumericFromDiamondSize(caratLabel);
      diamondBlock.carat =
        caratNum != null ? { value: caratNum, label: caratLabel } : { label: caratLabel };
    }
    var qualLabel = (diamondSelections && diamondSelections.quality) || state.diamond_quality || '';
    var qu = buildPricingOptionPayload('diamondQuality', qualLabel);
    if (qu) diamondBlock.quality = qu;
    if (diamondBlock.carat || diamondBlock.quality) {
      selectedOptions.diamond = diamondBlock;
    }

    var rs = getRingSizePayload();
    if (rs) selectedOptions.ringSize = rs;

    var payload = {
      requestId: generatePricingRequestId(),
      sessionId: getRingBuilderSessionId(),
      currency: cur,
      locale: loc,
      selectedOptions: selectedOptions
    };
    if (options && options.intent) payload.intent = options.intent;
    return payload;
  }

  function setRingBuilderPriceStateClasses(mode) {
    cacheRingBuilderPricingDom();
    var w = rbPricingDom.wrapper;
    if (!w) return;
    w.classList.toggle('is-loading', mode === 'loading');
    w.classList.toggle('is-error', mode === 'error');
    w.classList.toggle('is-incomplete', mode === 'incomplete');
    if (mode !== 'loading' && mode !== 'error' && mode !== 'incomplete') {
      w.classList.remove('is-loading', 'is-error', 'is-incomplete');
    }
  }

  function setRingBuilderPriceText(v) {
    cacheRingBuilderPricingDom();
    if (rbPricingDom.value) rbPricingDom.value.textContent = v || '';
    if (rbPricingDom.review) rbPricingDom.review.textContent = v || '';
  }

  function setRingBuilderPriceLoading() {
    var copy = getRingBuilderPricingCopy();
    setRingBuilderPriceStateClasses('loading');
    /* ponytail: never blank the price while recalculating. Keep the last
       successful figure on screen (styled by the 'loading' class) and only
       fall back to the "Calculating price…" copy when there is no prior
       price to show (first load, or after the price was cleared). */
    setRingBuilderPriceText(state.pricing.lastSuccessfulFormatted || copy.calculating);
  }

  function updateRingBuilderPriceDisplay(priceObj) {
    var fmt = priceObj && priceObj.formatted != null ? String(priceObj.formatted) : '';
    if (!fmt && priceObj && priceObj.amount != null && priceObj.currency) {
      fmt = String(priceObj.amount) + ' ' + String(priceObj.currency);
    }
    setRingBuilderPriceStateClasses('');
    setRingBuilderPriceText(fmt);
  }

  function showRingBuilderPriceIncomplete() {
    var copy = getRingBuilderPricingCopy();
    setRingBuilderPriceStateClasses('incomplete');
    setRingBuilderPriceText(copy.incomplete);
  }

  function showRingBuilderPriceError() {
    var copy = getRingBuilderPricingCopy();
    setRingBuilderPriceStateClasses('error');
    var show = state.pricing.lastSuccessfulFormatted || copy.defaultPrice || copy.error;
    setRingBuilderPriceText(show);
  }

  function showRingBuilderPriceDefault() {
    setRingBuilderPriceStateClasses('');
    var copy = getRingBuilderPricingCopy();
    setRingBuilderPriceText(copy.defaultPrice);
  }

  function syncRingBuilderPricingCartProps() {
    var configuredSku = state.pricing.configuredSku || buildConfiguredSku();
    qsa('[data-rb-prop="_configured_sku"]').forEach(function (el) {
      el.value = configuredSku;
    });
    var skuCaption = document.getElementById('rr-configured-sku');
    if (skuCaption) skuCaption.textContent = configuredSku;

    qsa('[data-rb-prop="_rb_stone_shape"]').forEach(function (el) {
      el.value = state.shape || '';
    });
    qsa('[data-rb-prop="_rb_carat"]').forEach(function (el) {
      el.value = state.diamond_size || '';
    });
    qsa('[data-rb-prop="_rb_quality"]').forEach(function (el) {
      el.value = state.diamond_quality || '';
    });
    var fmt =
      state.pricing.price && state.pricing.price.formatted != null
        ? String(state.pricing.price.formatted)
        : '';
    qsa('[data-rb-prop="_rb_calculated_price"]').forEach(function (el) {
      el.value = fmt;
    });
    var cents = getRingBuilderPriceAmountCents(state.pricing && state.pricing.price);
    qsa('[data-rb-prop="_rb_calculated_price_cents"]').forEach(function (el) {
      el.value = cents > 0 ? String(cents) : '';
    });
    qsa('[data-rb-prop="_rb_pricing_request_id"]').forEach(function (el) {
      el.value = state.pricing.lastSuccessfulRequestId || '';
    });

    var checkoutVariantId = getRingBuilderCheckoutVariantId();
    if (checkoutVariantId) syncRingBuilderCartVariantInputs(checkoutVariantId);
  }


/* -----------------------------------------------------------------------
 * RB v3 local pricing engine — mirrors f/Bonded_Matter/lib_pricing +
 * price_webhook (GST + FX + roundTo). Pure functions, no I/O.
 * Config is fetched once on page load via intent:"config".
 * ----------------------------------------------------------------------- */

function rbSlug(v) {
  var s = String(v == null ? '' : v).trim().toLowerCase().replace(/[\s_]+/g, '-');
  while (s.indexOf('--') >= 0) s = s.replace(/--/g, '-');
  return s;
}

function rbResolve(raw, table) {
  var s = rbSlug(raw);
  return (table && table[s]) ? table[s] : s;
}

function rbFactor(fmap, key, layer) {
  var v = fmap ? fmap[key] : undefined;
  if (typeof v !== 'number') throw new Error('Unknown ' + layer + ': "' + key + '"');
  return v;
}

function rbRoundTo(value, step) {
  if (step <= 0) step = 1;
  return Math.floor(value / step + 0.5) * step;
}

function rbLookupStone(carat, cfg) {
  if (typeof carat !== 'number' || carat <= 0) throw new Error('BAD_CARAT: ' + carat);
  var ladder = cfg.stoneLadder || {};
  var keys = Object.keys(ladder).map(Number).sort(function(a,b){ return a-b; });
  var grid = keys.map(function(k){ return [k, Number(ladder[String(k).toFixed(2)] || ladder[k])]; });
  if (!grid.length) throw new Error('CONFIG_ERROR: empty stoneLadder');
  var limits = cfg.carat || {};
  var lo = limits.min != null ? Number(limits.min) : grid[0][0];
  var hi = limits.max != null ? Number(limits.max) : grid[grid.length-1][0];
  var c = Math.min(Math.max(carat, lo), hi);
  var exact = c.toFixed(2);
  if (ladder[exact] != null) return Number(ladder[exact]);
  if (limits.mode === 'strict') throw new Error('OFF_GRID_CARAT: ' + carat);
  if (c <= grid[0][0]) return grid[0][1];
  if (c >= grid[grid.length-1][0]) return grid[grid.length-1][1];
  for (var i = 0; i < grid.length - 1; i++) {
    var x0 = grid[i][0], y0 = grid[i][1], x1 = grid[i+1][0], y1 = grid[i+1][1];
    if (x0 <= c && c <= x1) {
      var t = x1 === x0 ? 0 : (c - x0) / (x1 - x0);
      return Math.round((y0 + (y1 - y0) * t) * 100) / 100;
    }
  }
  throw new Error('OFF_GRID_CARAT: ' + carat);
}

function rbNormalizeOptions(so, cfg) {
  if (!so || typeof so !== 'object') throw new Error('BAD_PAYLOAD: selectedOptions missing');
  var a = cfg.aliases || {};
  var d = cfg.defaults || {};
  function gid(o, p1, p2) {
    var n = o[p1];
    if (p2 != null) n = n ? n[p2] : null;
    return n && typeof n === 'object' ? n.id : null;
  }
  var setting = rbResolve(gid(so, 'setting'), a.setting);
  if (!setting) throw new Error('MISSING_OPTION: setting');
  if ((cfg.settingsDisabled || []).indexOf(setting) >= 0) throw new Error('SETTING_UNAVAILABLE: ' + setting);
  var metalSetting = rbResolve(gid(so, 'metal', 'setting') || gid(so, 'metal', 'ring'), a.metal);
  var metalRing = rbResolve(gid(so, 'metal', 'ring') || gid(so, 'metal', 'setting'), a.metal);
  if (!metalSetting || !metalRing) throw new Error('MISSING_OPTION: metal');
  var thickness = rbResolve(gid(so, 'band', 'thickness'), a.bandThickness) || d.bandThickness;
  var profile = rbResolve(gid(so, 'band', 'profile'), a.bandProfile) || d.bandProfile;
  var shape = rbResolve(gid(so, 'stoneShape'), a.shape) || d.shape;
  var rawQuality = gid(so, 'diamond', 'quality') || gid(so, 'quality');
  var quality = rbResolve(rawQuality, a.quality) || d.quality;
  var splitShank = rbSlug(gid(so, 'splitShank')) || 'standard';
  var rawCarat = so.diamond && so.diamond.carat ? so.diamond.carat.value : null;
  var carat = typeof rawCarat === 'number' ? rawCarat : Number(d.carat || 1.5);
  var ringSizeNode = so.ringSize;
  var ringSize = ringSizeNode && ringSizeNode.id != null ? String(ringSizeNode.id) : null;
  return {
    setting: setting, metalSetting: metalSetting, metalRing: metalRing,
    bandThickness: thickness, bandProfile: profile, splitShank: splitShank,
    shape: shape, carat: carat, quality: quality, ringSize: ringSize
  };
}

function rbCalcRetail(o, cfg) {
  var f = {
    setting: rbFactor(cfg.settingFactor, o.setting, 'setting'),
    bandProfile: rbFactor(cfg.bandProfileFactor, o.bandProfile, 'band profile'),
    bandThickness: rbFactor(cfg.bandThicknessFactor, o.bandThickness, 'band thickness'),
    splitShank: rbFactor(cfg.splitShankFactor, o.splitShank, 'split shank'),
    metal: rbFactor(cfg.metalFactor, o.metalSetting, 'setting metal'),
    twoTone: rbFactor(cfg.twoToneFactor, o.metalRing === o.metalSetting ? 'same' : 'different', 'two-tone')
  };
  rbFactor(cfg.metalFactor, o.metalRing, 'ring metal');
  var settingCost = Number(cfg.settingMetalBase);
  for (var k in f) settingCost *= f[k];
  var ladderValue = rbLookupStone(o.carat, cfg);
  var sf = {
    shape: rbFactor(cfg.shapeFactor, o.shape, 'stone shape'),
    quality: rbFactor(cfg.qualityFactor, o.quality, 'diamond quality'),
    bmPremium: Number(cfg.bmPremium != null ? cfg.bmPremium : 1)
  };
  var stoneCost = ladderValue * sf.shape * sf.quality * sf.bmPremium;
  var whole = Math.max(
    rbFactor(cfg.metalWholeFactor, o.metalSetting, 'setting metal (whole-ring)'),
    rbFactor(cfg.metalWholeFactor, o.metalRing, 'ring metal (whole-ring)')
  );
  var subtotal = (settingCost + stoneCost) * whole;
  var sizeAdd = Number((cfg.ringSizeAdd || {})[o.ringSize] || 0);
  var retail = rbRoundTo(subtotal + sizeAdd, Number(cfg.roundTo || 100));
  return { retailExclGst: retail, subtotal: subtotal, settingCost: settingCost, stoneCost: stoneCost, sizeAdd: sizeAdd, whole: whole };
}

function rbFormatMoney(amount, currency) {
  var symbols = { AUD: '$', NZD: '$', USD: 'US$', EUR: '\u20AC', GBP: '\u00A3' };
  var sym = symbols[currency] || (currency + ' ');
  return sym + Math.round(amount).toLocaleString('en-US');
}

function rbApplyGst(amountExcl, currency, cfg) {
  var gst = cfg.gst || {};
  var mode = gst.mode || 'apply_all';
  var rate = Number(gst.rate || 0);
  if (mode === 'apply_all') return amountExcl * (1 + rate);
  if (mode === 'apply_aud_only' && currency === 'AUD') return amountExcl * (1 + rate);
  return amountExcl;
}

function rbComputePrice(cfg, fx, selectedOptions, currency) {
  var o = rbNormalizeOptions(selectedOptions, cfg);
  var breakdown = rbCalcRetail(o, cfg);
  var step = Number(cfg.roundTo || 100);
  var exclGstAud = breakdown.retailExclGst;
  var retailTargetAud = rbRoundTo(rbApplyGst(exclGstAud, 'AUD', cfg), step);
  var rate = (fx.aud_to || {})[currency];
  if (typeof rate !== 'number') throw new Error('UNKNOWN_CURRENCY: missing FX rate for ' + currency);
  var retailTarget = rbRoundTo(retailTargetAud * rate, step);
  return {
    amount: retailTarget,
    formatted: rbFormatMoney(retailTarget, currency),
    amountAud: retailTargetAud,
    carat: o.carat
  };
}

  /* --- config loader (fetches once on page load; concurrent calls share one request) --- */
  var rbLocalCfg = null;
  var rbLocalCfgPromise = null;
  function loadRbPricingConfig(webhookUrl, headers) {
    if (rbLocalCfg) return Promise.resolve(rbLocalCfg);
    if (rbLocalCfgPromise) return rbLocalCfgPromise;
    rbLocalCfgPromise = fetch(webhookUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ requestId: 'cfg-load', sessionId: 'cfg-load', intent: 'config' })
    }).then(function(r) { return r.json(); }).then(function(d) {
      if (d && d.status === 'success' && d.config && d.fx) {
        rbLocalCfg = { config: d.config, fx: d.fx };
      }
      return rbLocalCfg;
    }).catch(function() { return null; }).then(function(cfg) {
      if (!cfg) rbLocalCfgPromise = null;
      return cfg;
    });
    return rbLocalCfgPromise;
  }

  async function requestRingBuilderPrice(options) {
    cacheRingBuilderPricingDom();
    if (!canCalculateRingBuilderPrice()) {
      state.pricing.isLoading = false;
      state.pricing.error = null;
      showRingBuilderPriceDefault();
      return;
    }

    var conf = getPricingWebhookConfig();
    if (!conf.webhookUrl) {
      if (!pricingWebhookMissingWarned) {
        pricingWebhookMissingWarned = true;
        console.warn(
          '[Ring Builder] Missing pricing webhook URL on #ring-builder (data-pricing-webhook-url).'
        );
      }
      return;
    }

    var isCheckoutIntent = options && options.intent === 'checkout';
    var payload = buildRingBuilderPricingPayload(options);
    var payloadKey = getRingBuilderPricingPayloadKey(options);

    if (!isCheckoutIntent && payloadKey === state.pricing.lastSuccessfulPayloadKey && state.pricing.price) {
      updateRingBuilderPriceDisplay(state.pricing.price);
      return;
    }

    state.pricing.latestRequestId = payload.requestId;
    state.pricing.configuredSku = null;
    state.pricing.error = null;

    /* Instant local pricing — compute in the browser, render immediately.
       The server call below runs as a background confirm and corrects if
       the local calculation ever drifts from the server. */
    if (!isCheckoutIntent && rbLocalCfg) {
      try {
        var lp = rbComputePrice(rbLocalCfg.config, rbLocalCfg.fx, payload.selectedOptions, payload.currency);
        state.pricing.price = { amount: lp.amount, formatted: lp.formatted, currency: payload.currency };
        state.pricing.lastSuccessfulFormatted = lp.formatted;
        state.pricing.isLoading = false;
        updateRingBuilderPriceDisplay(state.pricing.price);
        syncRingBuilderPricingCartProps();
      } catch (e) { /* local calc failed — fall through to server */ }
    }

    /* Background server confirm (or first-load / checkout path). */
    if (!state.pricing.price || isCheckoutIntent) {
      state.pricing.isLoading = true;
      setRingBuilderPriceLoading();
    }
    syncRingBuilderPricingCartProps();

    try {
      var response = await fetch(conf.webhookUrl, {
        method: 'POST',
        headers: buildRingBuilderPricingHeaders(conf.webhookToken),
        body: JSON.stringify(payload)
      });

      var responseText = await response.text();
      var data = null;
      if (responseText && responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch (parseErr) {
          if (payload.requestId === state.pricing.latestRequestId) {
            state.pricing.isLoading = false;
            state.pricing.error = 'parse';
            showRingBuilderPriceError();
            console.warn(
              '[Ring Builder] Pricing response JSON parse failed.',
              { status: response.status, parseErr: parseErr, bodyPreview: responseText.slice(0, 200) }
            );
          }
          return;
        }
      } else if (payload.requestId === state.pricing.latestRequestId) {
        state.pricing.isLoading = false;
        state.pricing.error = 'empty_body';
        showRingBuilderPriceError();
        console.warn(
          '[Ring Builder] Pricing webhook returned an empty body (HTTP ' +
          response.status +
          '). Confirm the n8n workflow is active and the Respond to Webhook node returns JSON. URL: ' +
          conf.webhookUrl.replace(/rb_ring_token=[^&]+/i, 'rb_ring_token=REDACTED')
        );
        syncRingBuilderPricingCartProps();
        return;
      }

      if (Array.isArray(data) && data.length) {
        data = data[0];
      }
      data = normalizeRingBuilderPricingResponse(data, payload);

      if (payload.requestId !== state.pricing.latestRequestId) {
        return;
      }

      if (!data || data.requestId !== payload.requestId) {
        state.pricing.isLoading = false;
        state.pricing.error = 'invalid_response';
        showRingBuilderPriceError();
        console.warn('[Ring Builder] Pricing webhook returned unexpected data.', {
          status: response.status,
          data: data
        });
        syncRingBuilderPricingCartProps();
        return;
      }

      if (!response.ok || data.status === 'error') {
        state.pricing.isLoading = false;
        state.pricing.error = 'http_or_error_status';
        showRingBuilderPriceError();
        console.warn('[Ring Builder] Pricing request failed.', {
          status: response.status,
          bodyStatus: data.status,
          requestId: data.requestId
        });
        syncRingBuilderPricingCartProps();
        return;
      }

      if (data.status === 'incomplete') {
        state.pricing.isLoading = false;
        state.pricing.error = null;
        showRingBuilderPriceIncomplete();
        syncRingBuilderPricingCartProps();
        return;
      }

      if (data.status !== 'success') {
        state.pricing.isLoading = false;
        state.pricing.error = 'unexpected_status';
        showRingBuilderPriceError();
        syncRingBuilderPricingCartProps();
        return;
      }

      state.pricing.price = data.price || null;
      state.pricing.configuredSku =
        data.configuredSku != null && String(data.configuredSku).trim()
          ? String(data.configuredSku).trim()
          : null;
      state.pricing.isLoading = false;
      state.pricing.error = null;
      if (state.pricing.price && state.pricing.price.formatted != null) {
        state.pricing.lastSuccessfulFormatted = String(state.pricing.price.formatted);
      }
      state.pricing.lastSuccessfulRequestId = payload.requestId;
      if (isCheckoutIntent) {
        state.pricing.lastCheckoutPayloadKey = payloadKey;
      } else {
        state.pricing.lastSuccessfulPayloadKey = payloadKey;
      }

      updateRingBuilderPriceDisplay(state.pricing.price);
      syncRingBuilderPricingCartProps();
    } catch (error) {
      if (payload.requestId === state.pricing.latestRequestId) {
        state.pricing.isLoading = false;
        state.pricing.error = 'network';
        showRingBuilderPriceError();
        console.warn(
          '[Ring Builder] Pricing request failed (often CORS or offline). ' +
          'If the console shows a CORS error, configure n8n to return ' +
          'Access-Control-Allow-Origin for your storefront host, or use a same-origin proxy.',
          error
        );
        syncRingBuilderPricingCartProps();
      }
    }
  }

  var requestRingBuilderPriceDebounced = debounce(function (options) {
    if (state.pricing.checkoutBusy) return;
    return requestRingBuilderPrice(options);
  }, 150);

  function syncReview(options) {
    var map = {
      setting: ['rs-setting', 'rr-setting'],
      shape: ['rs-shape', 'rr-shape'],
      metal: ['rs-metal', 'rr-metal'],
      band: ['rs-band', 'rr-band'],
      // `rs-diamond` lives in the side-card concierge view (combined
      // size + quality string is fine there). The summary `rr-carat` /
      // `rr-quality` rows are written separately below so they can show
      // independently.
      diamond: ['rs-diamond'],
      size: []
    };
    // When ring and setting metals differ, show both so review/cart
    // properties match the 3D view and pricing payload.
    var metalDisplayValue = displayMetalLabel(state.metal || '');
    if (metalSelections.ring && metalSelections.setting && metalSelections.ring !== metalSelections.setting) {
      metalDisplayValue = displayMetalLabel(metalSelections.ring) + ' / ' + displayMetalLabel(metalSelections.setting);
    } else if (!metalDisplayValue) {
      metalDisplayValue = displayMetalLabel(metalSelections.ring || metalSelections.setting || '');
    }

    Object.keys(map).forEach(function (key) {
      var text = key === 'metal' ? metalDisplayValue : state[key];
      map[key].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) el.textContent = text || '—';
      });
    });

    // Summary rows: split the combined diamond field into Carat + Quality so
    // each renders on its own line in the configuration table.
    var caratEl = document.getElementById('rr-carat');
    if (caratEl) {
      caratEl.textContent = state.diamond_size
        ? formatCaratStopLabel(state.diamond_size)
        : '—';
    }
    var qualityEl = document.getElementById('rr-quality');
    if (qualityEl) qualityEl.textContent = state.diamond_quality || '—';

    var ringSize = getRingSizePayload();
    var sizeLabel = ringSize ? ringSize.label : (state.size || '');
    var formattedSize = sizeLabel ? formatReviewSizeDisplay(sizeLabel) : '—';
    ['rr-size', 'rs-size'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.textContent = formattedSize;
    });

    /* Mirror the selected option icon next to each summary value (right column
       in the Figma "Review {name}'s Ring" view). Reuses the same option-card
       lookup as the breadcrumb tabs so Setting / Shape / Metal / Band stay in
       lock-step. Diamond + Size intentionally omitted — Figma renders them
       text-only. */
    var REVIEW_ICON_KEYS = {
      setting: 'setting',
      shape: 'shape',
      metal: 'metal',
      band: 'band_thickness'
    };
    Object.keys(REVIEW_ICON_KEYS).forEach(function (key) {
      var textEl = document.getElementById('rr-' + key);
      if (!textEl || !textEl.parentElement) return;
      var slot = textEl.parentElement.querySelector('.rb-review-row__icon');
      if (!slot) return;
      var iconValue;
      if (key === 'metal') {
        iconValue = state.metal || metalSelections.ring || metalSelections.setting || '';
      } else if (key === 'band') {
        iconValue = bandSelections.thickness || '';
      } else {
        iconValue = state[key] || '';
      }
      setIconSlot(slot, getOptionIconSrc(REVIEW_ICON_KEYS[key], iconValue), iconValue || '');
    });

    ['setting', 'shape', 'metal', 'band', 'diamond', 'size', 'engraving'].forEach(function (key) {
      var val = key === 'metal' ? metalDisplayValue : state[key];
      qsa('[data-rb-prop="' + key + '"]').forEach(function (el) {
        el.value = val || '';
      });
    });

    /* Configured SKU + n8n pricing cart props (`properties[…]`). */
    syncRingBuilderPricingCartProps();

    if (engravingInput && engravingInput.value !== (state.engraving || '')) {
      engravingInput.value = state.engraving || '';
    }
    if (engravingCount) {
      var engravingLen = (state.engraving || '').length;
      engravingCount.textContent = engravingLen + '/20 Characters';
    }

    syncMetalSideCard();
    syncTabLabels();
    syncReviewRingTitle();

    if (!(options && options.skipPricing)) {
      if (!rbLocalCfg) {
        var _cfgConf = getPricingWebhookConfig();
        if (_cfgConf.webhookUrl) loadRbPricingConfig(_cfgConf.webhookUrl, buildRingBuilderPricingHeaders(_cfgConf.webhookToken));
      }
      requestRingBuilderPriceDebounced();
    }
  }

  // ══════════════════════════════════════════════════════
  // THREE.JS
  // ══════════════════════════════════════════════════════
  function isRbBuilderVisible() {
    var el = document.getElementById('rb-builder');
    if (!el) return false;
    if (el.style.display === 'grid') return true;
    try {
      return window.getComputedStyle(el).display === 'grid';
    } catch (e) {
      return false;
    }
  }

  function ensureThree() {
    if (threeReady) return;

    var r3fApi = window.BMRingBuilderR3F;
    if (
      !window.THREE ||
      !window.THREE.GLTFLoader ||
      !r3fApi ||
      typeof r3fApi.init !== 'function'
    ) {
      ensureThreeLoadAttempts += 1;
      if (ensureThreeLoadAttempts > 80) {
        console.error(
          'Ring Builder: THREE + GLTFLoader + BMRingBuilderR3F not available after retries — deploy ring-builder-three.bundle.js and bm-ring-builder-r3f.bundle.js; check the console for 404/CSP errors.'
        );
        var mount = document.getElementById('ring-canvas');
        if (mount) {
          mount.innerHTML =
            '<p class="rb-webgl-fallback" style="padding:24px;font:14px/1.4 system-ui;color:#444;max-width:300px;margin:0 auto;text-align:center;">3D preview did not load. Confirm the theme includes <code>ring-builder-three.bundle.js</code> and <code>bm-ring-builder-r3f.bundle.js</code>, then refresh.</p>';
        }
        return;
      }
      setTimeout(ensureThree, 200);
      return;
    }

    canvasContainer = document.getElementById('ring-canvas');
    if (!canvasContainer) return;
    resolveQualityPreset();
    resolveCollectionTuning();
    resolveTaggingConfig();

    canvasContainer.style.minHeight = '600px';

    var r3fBg = getSceneBackgroundHex();
    canvasContainer.style.backgroundColor = new THREE.Color(r3fBg).getStyle();
    /* Background is set on the container; R3F init replaces innerHTML — avoid a
       redundant clear here (mountRingBuilderR3f clears once). */
    var r3fInit = r3fApi.init(canvasContainer, { getBgHex: getSceneBackgroundHex });
    if (r3fInit && typeof r3fInit.then === 'function') {
      r3fInit
        .then(function () {
          loader = new THREE.GLTFLoader();
          var assetsConfigR3f = getRingAssetsConfig();
          if (THREE.DRACOLoader && typeof loader.setDRACOLoader === 'function') {
            dracoLoader = new THREE.DRACOLoader();
            dracoLoader.setDecoderPath(
              assetsConfigR3f.dracoDecoderPath || 'https://www.gstatic.com/draco/versioned/decoders/1.5.6/'
            );
            loader.setDRACOLoader(dracoLoader);
          }
          if (THREE.KTX2Loader && typeof loader.setKTX2Loader === 'function') {
            try {
              ktx2Loader = new THREE.KTX2Loader();
              ktx2Loader.setTranscoderPath(
                assetsConfigR3f.ktx2TranscoderPath ||
                'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/libs/basis/'
              );
              var probeCanvas = document.createElement('canvas');
              var probeRenderer = new THREE.WebGLRenderer({ canvas: probeCanvas, antialias: false });
              if (typeof ktx2Loader.detectSupport === 'function') ktx2Loader.detectSupport(probeRenderer);
              probeRenderer.dispose();
              loader.setKTX2Loader(ktx2Loader);
            } catch (ktxErrR3f) {
              console.warn('Ring Builder: KTX2Loader init skipped.', ktxErrR3f);
            }
          }
          threeReady = true;
          window.addEventListener('resize', resizeThree);
          if (isRbBuilderVisible()) {
            updateDisplayedModelFromState();
          }
        })
        .catch(function (errR3f) {
          console.error('Ring Builder: R3F viewer init failed.', errR3f);
          if (canvasContainer) {
            canvasContainer.innerHTML =
              '<p class="rb-webgl-fallback" style="padding:24px;font:14px/1.4 system-ui;color:#444;max-width:360px;margin:0 auto;text-align:center;">3D preview (React Three Fiber) failed to start. Open the browser console for details.</p>';
          }
        });
    }
  }

  function resizeThree() {
    if (!threeReady || !canvasContainer) return;
    if (window.BMRingBuilderR3F && typeof window.BMRingBuilderR3F.resize === 'function') {
      window.BMRingBuilderR3F.resize();
    }
  }

  function loadSettingModel(settingName, updateToken) {
    if (!threeReady || !loader) return;
    var url = window.RingAssets && window.RingAssets[settingName];
    if (!isValidRingBuilderModelUrl(url) && hasComponentConfig()) {
      var asset = resolveComponentAsset('setting');
      if (asset && asset.url) url = asset.url;
    }
    if (!isValidRingBuilderModelUrl(url)) {
      console.warn('Ring Builder: No model URL for setting:', settingName);
      return;
    }
    /* ponytail: defer clearing the R3F viewer until the replacement GLB is ready —
       clearing at the start of an async load leaves an empty/black canvas for the
       whole fetch (the main first-load flicker on the Setting step). */
    loadModelFromCache(url).then(function (gltf) {
      if (updateToken !== undefined && updateToken !== displayUpdateToken) return;
      if (!gltf || !gltf.scene) return;
      clearComponentAssembly();
      clearSingleModel({ keepViewer: true });
      model = cloneModelScene(gltf.scene);
      model.userData = Object.assign({}, model.userData || {}, {
        rbComponentType: 'setting'
      });
      model.traverse(function (node) {
        if (!node || node === model) return;
        node.userData = Object.assign({}, node.userData || {}, {
          rbComponentType: 'setting'
        });
      });
      applyModelMaterialBaseline(model);
      applyActiveMetalToModel();
      if (window.BMRingBuilderR3F && typeof window.BMRingBuilderR3F.setDisplayedRoot === 'function') {
        window.BMRingBuilderR3F.setDisplayedRoot(model);
      }
      frameModelRoot(model);
    }).catch(function (err) {
      console.error('Ring Builder: GLB load failed:', err);
    });
  }

  // ══════════════════════════════════════════════════════
  // BUY NOW (Review step)
  // ══════════════════════════════════════════════════════
  /* Native Shopify express buy-now: the form POSTs `id` + line item
     `properties[…]` to `/cart/add` with `return_to=/checkout`. Shopify
     handles the add + redirect server-side, then 302s to the Checkout
     Extensibility host. No JS interception needed — the `data-rb-prop="…"`
     inputs are kept in sync by `syncReview()` above so each submit ships
     the latest configuration. */

  // ══════════════════════════════════════════════════════
  // INIT
  // ══════════════════════════════════════════════════════
  document.addEventListener('DOMContentLoaded', function () {
    hydrateRingAssetsR2Urls();

    intro = document.getElementById('rb-intro');
    builder = document.getElementById('rb-builder');
    backBtn = document.getElementById('rb-back');
    nextBtn = document.getElementById('rb-next');
    introNameInput = document.getElementById('rb-name');
    ringNameLabel = document.getElementById('rb-ring-name');
    progress = document.getElementById('rb-progress');
    atcWrap = document.getElementById('rb-atc-wrap');
    tabsNav = qs('.rb-tabs');
    rightPanel = qs('.rb-right');
    collapsiblePanels = document.getElementById('rb-collapsible-panels');
    reviewBackBtn = document.getElementById('rb-review-back');
    engravingInput = document.getElementById('rb-engraving');
    engravingCount = document.getElementById('rb-engraving-count');
    resetViewBtn = document.getElementById('rb-reset-view');
    SLIDESHOW_GROUPS.forEach(function (group) {
      rbSlideshows[group] = document.getElementById('rb-swiper-' + group);
    });
    setSlideshowVisibility(getSlideGroupForStep(1));
    initRbSlideshowVideos();

    /** Blocks scroll-driven Setting selection while `scrollCardToCenter` smooth-scroll runs after a chip click */
    /** Metal / Band / Diamond Quality horizontal strips (mobile). */
    var optionStripScrollLockUntil = 0;

    var startBtn = document.getElementById('rb-start');
    var skipBtn = document.getElementById('rb-skip');
    if (startBtn) startBtn.addEventListener('click', showBuilder);
    if (skipBtn) skipBtn.addEventListener('click', showBuilder);
    if (introNameInput) {
      introNameInput.addEventListener('input', syncRingTitleFromRecipient);
      introNameInput.addEventListener('change', syncRingTitleFromRecipient);
    }
    syncRingTitleFromRecipient();
    bindRingBuilderCheckoutForms();

    /* Intro overlay close (X, top-left of the intro card)
       Behaves like a modal dismiss: pop history if we have it, otherwise route home.
       This is intentionally distinct from `Skip`, which advances into the builder
       without saving the recipient details. */
    var introCloseBtn = document.getElementById('rb-intro-close');
    if (introCloseBtn) {
      introCloseBtn.addEventListener('click', function () {
        try {
          if (window.history && window.history.length > 1) {
            window.history.back();
            return;
          }
        } catch (_) { /* fall through to home */ }
        window.location.assign('/');
      });
    }

    var closeBtn = document.getElementById('rb-close');
    if (closeBtn) closeBtn.addEventListener('click', showIntro);
    if (reviewBackBtn) {
      reviewBackBtn.addEventListener('click', function () {
        // Ring-size step (6) is hidden; step back to the diamond step (5).
        goStep(TOTAL_STEPS - 1);
      });
    }
    if (engravingInput) {
      engravingInput.addEventListener('input', function () {
        state.engraving = engravingInput.value || '';
        syncReview();
      });
    }

    if (backBtn) backBtn.addEventListener('click', prevStep);
    if (nextBtn) nextBtn.addEventListener('click', nextStep);

    /**
     * Mobile-only "info" popup wiring.
     *
     * Tapping the (i) icon in the mobile context bar promotes the static
     * `.rb-right` panel into a full-screen overlay (CSS keys off
     * `.rb-builder.is-info-popup-open`). The same DOM is reused for both
     * variants — `setRightPanelMode(currentStep)` already toggles the
     * correct slideshow group / collapsible view, so the popup just shows
     * whatever the active step rendered. After flipping the class we kick
     * the swiper layout so it recomputes for the new viewport.
     *
     * Closed via the round back button (top-left of the popup), Escape,
     * or any step navigation triggered while the popup is open.
     */
    function openInfoPopup() {
      if (!builder) return;
      if (window.matchMedia && !window.matchMedia('(max-width: 900px)').matches) {
        return;
      }
      if (currentStep === TOTAL_STEPS + 1) return;

      builder.classList.add('is-info-popup-open');
      document.body.classList.add('rb-info-popup-lock');

      var panelState = getRightPanelState(currentStep);
      if (panelState && panelState.mode === 'slideshow' && panelState.slideGroup) {
        // Layout dimensions just changed dramatically — give the swiper a
        // tick to settle, then refresh so dots and slide widths recompute.
        requestAnimationFrame(function () {
          ensureRbSliderReady(panelState.slideGroup, true);
        });
      }
    }

    function closeInfoPopup() {
      if (!builder) return;
      if (!builder.classList.contains('is-info-popup-open')) return;

      builder.classList.remove('is-info-popup-open');
      document.body.classList.remove('rb-info-popup-lock');

      var panelState = getRightPanelState(currentStep);
      if (panelState && panelState.mode === 'slideshow' && panelState.slideGroup) {
        requestAnimationFrame(function () {
          refreshRbSlider(panelState.slideGroup, true);
        });
      }
    }

    var settingInfoBtn = document.getElementById('rb-setting-mobile-info');
    if (settingInfoBtn) {
      settingInfoBtn.addEventListener('click', openInfoPopup);
    }

    var infoPopupBack = document.getElementById('rb-info-popup-back');
    if (infoPopupBack) {
      infoPopupBack.addEventListener('click', closeInfoPopup);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && builder && builder.classList.contains('is-info-popup-open')) {
        closeInfoPopup();
      }
    });

    if (resetViewBtn) {
      resetViewBtn.addEventListener('click', function () {
        resetModelView();
      });
    }

    // Enable drag behaviour for the diamond size track and ring size track.
    bindDiamondSizeDrag();
    applyDiamondSizeStopsForSettingShape();
    bindRingSizeDrag();

    qsa('.rb-tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var step = parseInt(tab.dataset.step, 10);
        if (Number.isNaN(step)) return;
        if (step === 3 && tab.dataset.metalTab) {
          metalTabMode = tab.dataset.metalTab;
        }
        if (step === 4 && tab.dataset.bandTab) {
          bandTabMode = tab.dataset.bandTab;
        }
        if (step === 5 && tab.dataset.diamondTab) {
          diamondTabMode = tab.dataset.diamondTab;
        }
        goStep(step);
      });
    });

    if (builder) {
      builder.addEventListener('click', function (evt) {
        if (evt.target.closest('.rb-quality-info-btn, .rb-quality-info--tooltip-host')) return;
        var card = evt.target.closest('[data-rb-option]');
        if (!card || !builder.contains(card)) return;
        var key = card.dataset.key;
        var value = card.dataset.value;
        if (!key || !value) return;

        if (key === 'metal') {
          var targetMetalTab = currentStep === 3 && metalTabMode === 'setting' ? 'setting' : 'ring';
          metalSelections[targetMetalTab] = value;
          state.metal = metalSelections.ring || metalSelections.setting || value;
          state[key] = value;
        } else if (key === 'band_thickness') {
          bandSelections.thickness = value;
          state.band_thickness = value;
          state.band = [bandSelections.thickness, bandSelections.profile].filter(Boolean).join(' & ');
        } else if (key === 'band_profile') {
          bandSelections.profile = value;
          state.band_profile = value;
          state.band = [bandSelections.thickness, bandSelections.profile].filter(Boolean).join(' & ');
        } else if (key === 'diamond_size') {
          diamondSelections.size = value;
          state.diamond_size = value;
          state.diamond = [diamondSelections.size, diamondSelections.quality].filter(Boolean).join(' / ');
        } else if (key === 'diamond_quality') {
          diamondSelections.quality = value;
          state.diamond_quality = value;
          state.diamond = [diamondSelections.size, diamondSelections.quality].filter(Boolean).join(' / ');
          qsa('.rb-quality-info-btn', builder).forEach(function (btn) {
            btn.blur();
          });
        } else if (key === 'size') {
          // Store the region-aware label (letters for AU/UK, numeric otherwise)
          // for review/summary display. The US-numeric `data-readout-us` is
          // read directly from the active stop in `getRingSizePayload` for the
          // cart id, so internal value integrity is preserved.
          var rsLabel = (card.getAttribute('data-readout-label') || '').trim();
          state[key] = rsLabel || value;
        } else {
          state[key] = value;
        }
        if (key === 'setting') {
          selectedComponents.setting = true;
          ensureRoundDiamondPreviewForSetting();
          applyShapeAvailabilityForSetting();
          syncSliderProgress('rb-shape-slider', 'rb-shape-progress');
        } else if (key === 'shape' || key === 'diamond_size' || key === 'diamond_quality') {
          selectedComponents.diamond = true;
          if (key === 'shape') {
            rbShapeFrontFramePending = true;
            applyDiamondSizeStopsForSettingShape();
          }
        } else if (key === 'band_thickness' || key === 'band_profile') {
          selectedComponents.band = true;
        }
        if (key === 'metal' || key === 'band_thickness' || key === 'band_profile' || key === 'diamond_quality') {
          optionStripScrollLockUntil = performance.now() + 520;
        }

        setSelected(key, value);
        syncReview();

        if (
          key === 'setting' ||
          key === 'shape' ||
          key === 'metal' ||
          key === 'band_thickness' ||
          key === 'band_profile' ||
          key === 'diamond_quality'
        ) {
          scrollCardIntoViewIfNeeded(card);
        }

        goSlideForCard(card);

        if (
          key === 'setting' ||
          key === 'shape' ||
          key === 'band_thickness' ||
          key === 'band_profile' ||
          key === 'diamond_size' ||
          key === 'diamond_quality'
        ) {
          ensureThree();
          if (threeReady) {
            if (key === 'diamond_size') {
              updateDisplayedModelForCaratDebounced();
            } else {
              updateDisplayedModelFromState();
              syncComponentVisibility();
              if (key === 'shape') scheduleShapeStoneFrontFrame();
            }
          }
        }
      });
    }

    qsa('[data-rb-gem-range]').forEach(function (slider) {
      slider.addEventListener('input', function () {
        var componentType = slider.dataset.rbGemRange;
        var count = parseInt(slider.value, 10);
        var countEl = qs('[data-rb-gem-count="' + componentType + '"]');
        if (countEl) countEl.textContent = count;
        applyGemSliderValue(componentType, count);
      });
    });

    /** Map vertical mouse wheel to horizontal scroll; leave native trackpad/touch horizontal scroll alone. */
    function enableWheelHorizontalScroll(el) {
      el.addEventListener(
        'wheel',
        function (e) {
          var maxScroll = el.scrollWidth - el.clientWidth;
          if (maxScroll <= 0) return;
          var dx = e.deltaX;
          var dy = e.deltaY;
          /* Browser handles horizontal trackpad / touch momentum on overflow-x,
             but at scroll edges consume the event to stop chaining into vertical page scroll. */
          if (Math.abs(dx) >= Math.abs(dy)) {
            if (dx !== 0 && ((dx > 0 && el.scrollLeft >= maxScroll) || (dx < 0 && el.scrollLeft <= 0))) {
              e.preventDefault();
            }
            return;
          }
          if (dy === 0) return;
          if ((dy > 0 && el.scrollLeft >= maxScroll) || (dy < 0 && el.scrollLeft <= 0)) return;
          e.preventDefault();
          el.scrollLeft += dy;
        },
        { passive: false }
      );
    }

    /**
     * Horizontal drag-scroll with click-vs-drag discrimination (shared by setting + shape strips).
     * Uses a 5px movement threshold before scrolling so taps on `.rb-setting-card` register in one click.
     */
    function enablePointerDragScroll(el, opts) {
      opts = opts || {};
      var mouseOnly = !!opts.mouseOnly;
      var DRAG_THRESHOLD_PX = 5;
      var pointerDown = false;
      var startX = 0;
      var startY = 0;
      var scrollStart = 0;
      var dragging = false;
      var didScrollDrag = false;
      var activePointerId = null;
      var pendingClickSuppress = false;

      el.addEventListener('pointerdown', function (e) {
        if (mouseOnly && e.pointerType !== 'mouse') return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pointerDown = true;
        dragging = false;
        didScrollDrag = false;
        pendingClickSuppress = false;
        startX = e.clientX;
        startY = e.clientY;
        scrollStart = el.scrollLeft;
        activePointerId = e.pointerId;
        el.style.scrollBehavior = 'auto';
      });

      el.addEventListener('pointermove', function (e) {
        if (!pointerDown || e.pointerId !== activePointerId) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (!dragging) {
          if (Math.hypot(dx, dy) <= DRAG_THRESHOLD_PX) return;
          dragging = true;
          didScrollDrag = true;
          el.style.cursor = 'grabbing';
          el.style.userSelect = 'none';
          try {
            el.setPointerCapture(e.pointerId);
          } catch (err) {
            /* ignore */
          }
        }
        e.preventDefault();
        el.scrollLeft = scrollStart - dx;
      });

      function endPointerDrag(e) {
        if (!pointerDown || e.pointerId !== activePointerId) return;
        pointerDown = false;
        if (didScrollDrag) {
          pendingClickSuppress = true;
        }
        didScrollDrag = false;
        dragging = false;
        el.style.scrollBehavior = '';
        el.style.cursor = '';
        el.style.userSelect = '';
        try {
          el.releasePointerCapture(e.pointerId);
        } catch (err2) {
          /* ignore */
        }
        activePointerId = null;
      }

      el.addEventListener('pointerup', endPointerDrag);
      el.addEventListener('pointercancel', endPointerDrag);
      el.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });

      el.addEventListener(
        'click',
        function (e) {
          if (!pendingClickSuppress) return;
          var card = e.target.closest && e.target.closest('.rb-strip-card');
          if (card && el.contains(card)) {
            e.preventDefault();
            e.stopPropagation();
          }
          pendingClickSuppress = false;
        },
        true
      );
    }

    /**
     * Make the thin progress track under a strip act like a scrollbar: clicking
     * or dragging it scrolls the strip left/right proportionally. The track was
     * previously display-only (position synced from scroll), so users who tried
     * to drag it to move the row saw nothing happen.
     * @param {HTMLElement} progressEl - `.rb-setting-progress` / `.rb-shape-progress`
     * @param {HTMLElement} sliderEl - the horizontally scrollable strip
     */
    function enableProgressBarScroll(progressEl, sliderEl) {
      if (!progressEl || !sliderEl) return;
      var dragging = false;
      var activePointerId = null;

      function scrollToPoint(clientX) {
        var rect = progressEl.getBoundingClientRect();
        if (!rect.width) return;
        var maxScroll = sliderEl.scrollWidth - sliderEl.clientWidth;
        if (maxScroll <= 0) return;
        var ratio = (clientX - rect.left) / rect.width;
        if (ratio < 0) ratio = 0;
        if (ratio > 1) ratio = 1;
        sliderEl.scrollLeft = ratio * maxScroll;
      }

      progressEl.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        dragging = true;
        activePointerId = e.pointerId;
        sliderEl.style.scrollBehavior = 'auto';
        try {
          progressEl.setPointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }
        scrollToPoint(e.clientX);
        e.preventDefault();
      });

      progressEl.addEventListener('pointermove', function (e) {
        if (!dragging || e.pointerId !== activePointerId) return;
        scrollToPoint(e.clientX);
        e.preventDefault();
      });

      function endDrag(e) {
        if (!dragging || e.pointerId !== activePointerId) return;
        dragging = false;
        activePointerId = null;
        sliderEl.style.scrollBehavior = '';
        try {
          progressEl.releasePointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }
      }

      progressEl.addEventListener('pointerup', endDrag);
      progressEl.addEventListener('pointercancel', endDrag);
    }

    /**
     * Shared horizontal-strip “which chip is active” for Setting / Shape sliders (handles first/last + padding).
     * @param {HTMLElement} sliderEl
     * @param {string} dataKey
     * @returns {Element|null}
     */
    function getStripCards(sliderEl, dataKey) {
      return Array.prototype.filter.call(
        sliderEl.querySelectorAll('.rb-strip-card[data-key="' + dataKey + '"]'),
        isShapeStripCardVisible
      );
    }

    /**
     * Wire IntersectionObserver-driven active-chip selection to a horizontal strip.
     * Uses the canonical narrow centre-band root margin so variable-width chips never get skipped.
     * Edge behaviour: when the strip is scrolled to its min/max, the first/last chip is forced active
     * (their geometric centres can sit outside the centre band when there is left/right padding).
     */
    function bindStripScrollSelect(sliderEl, dataKey, opts) {
      var stepCheck = opts.stepCheck;
      var getLockUntil = opts.getLockUntil;
      var onCommit = opts.onCommit;

      if (typeof IntersectionObserver === 'undefined') return;

      var cards = getStripCards(sliderEl, dataKey);
      if (!cards.length) return;

      var intersecting = new Set();
      var lastCommitted = null;
      var pendingFlush = null;

      function commit(card) {
        if (!card) return;
        if (!stepCheck()) return;
        if (performance.now() < getLockUntil()) return;
        var v = card.dataset.value;
        if (!v || v === lastCommitted) return;
        lastCommitted = v;
        onCommit(card, v);
      }

      /* Pick the leftmost visible chip — every strip aligns active to the
         left padding so the chip sitting at that anchor is always "the
         active one". `rootMargin: 0 -45% 0 0` (asymmetric) below clips
         the right side of the viewport so chips drifting toward the
         right edge stop counting before chips on the left do. */
      function pickFromIntersecting() {
        if (!intersecting.size) return null;
        var sr = sliderEl.getBoundingClientRect();
        var padLeft = parseFloat(window.getComputedStyle(sliderEl).paddingLeft) || 0;
        var anchorLeft = sr.left + padLeft;
        var best = null;
        var bestD = Infinity;
        intersecting.forEach(function (el) {
          var cr = el.getBoundingClientRect();
          if (cr.width < 1) return;
          var d = Math.abs(cr.left - anchorLeft);
          if (d < bestD) {
            bestD = d;
            best = el;
          }
        });
        return best;
      }

      function pickActiveOrFirstCard() {
        for (var ci = 0; ci < cards.length; ci++) {
          if (cards[ci].classList.contains('active')) return cards[ci];
        }
        return cards[0];
      }

      function flushNow() {
        pendingFlush = null;
        var card = pickFromIntersecting();
        if (!card) {
          var maxScroll = Math.max(0, sliderEl.scrollWidth - sliderEl.clientWidth);
          var EDGE = 8;
          if (maxScroll > EDGE) {
            if (sliderEl.scrollLeft <= EDGE) card = pickActiveOrFirstCard();
            else if (sliderEl.scrollLeft >= maxScroll - EDGE) card = cards[cards.length - 1];
          } else {
            card = pickActiveOrFirstCard();
          }
        }
        commit(card);
      }

      function scheduleFlush() {
        if (pendingFlush != null) return;
        pendingFlush = requestAnimationFrame(flushNow);
      }

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) intersecting.add(e.target);
            else intersecting.delete(e.target);
          });
          scheduleFlush();
        },
        {
          root: sliderEl,
          /* Asymmetric margin: keep the left edge wide so the leftmost chip
             can stay "active" while later chips peek into view. Trim the
             right so chips drifting off-screen stop being candidates. */
          rootMargin: '0px -45% 0px 0px',
          threshold: 0
        }
      );

      cards.forEach(function (c) { io.observe(c); });

      sliderEl.addEventListener(
        'scroll',
        function () {
          var maxScroll = Math.max(0, sliderEl.scrollWidth - sliderEl.clientWidth);
          var EDGE = 8;
          if (maxScroll <= EDGE) return;
          if (sliderEl.scrollLeft <= EDGE) commit(pickActiveOrFirstCard());
          else if (sliderEl.scrollLeft >= maxScroll - EDGE) commit(cards[cards.length - 1]);
        },
        { passive: true }
      );

      sliderEl.addEventListener('scrollend', flushNow);

      return { flush: flushNow, observer: io, setLastCommitted: function (v) { lastCommitted = v || null; } };
    }

    // Setting slider: fade + native horizontal scroll + progress sync + arrow
    (function initSettingSlider() {
      var slider = document.getElementById('rb-setting-slider');
      var arrow = document.getElementById('rb-setting-arrow');
      if (!slider) return;
      var settingWrap = slider.closest('.rb-setting-wrap');
      var fadeLeft = settingWrap
        ? settingWrap.querySelector('.rb-setting-fade--left:not(.rb-shape-fade-left)')
        : null;

      var settingProgressRaf = null;

      function syncSettingOverflowChrome() {
        if (!settingWrap) return;
        var cards = Array.prototype.filter.call(
          slider.querySelectorAll('.rb-setting-card[data-key="setting"]'),
          function (card) {
            return !card.hidden && card.getClientRects().length > 0;
          }
        );
        if (!cards.length) return;

        var sliderStyles = window.getComputedStyle(slider);
        var gap = parseFloat(sliderStyles.columnGap || sliderStyles.gap) || 0;
        var cardsWidth = cards.reduce(function (total, card) {
          return total + card.getBoundingClientRect().width;
        }, 0);
        var requiredWidth = cardsWidth + gap * Math.max(0, cards.length - 1) + 80;
        var allVisible = requiredWidth <= settingWrap.clientWidth + 1;

        settingWrap.classList.toggle('is-all-visible', allVisible);
        slider.scrollLeft = allVisible ? 0 : slider.scrollLeft;
      }

      function onSettingScroll() {
        if (fadeLeft) fadeLeft.classList.toggle('is-visible', slider.scrollLeft > 10);
        if (settingProgressRaf != null) return;
        settingProgressRaf = requestAnimationFrame(function () {
          settingProgressRaf = null;
          syncSliderProgress('rb-setting-slider', 'rb-setting-progress');
        });
      }

      /* Allow touch drag (was mouseOnly): native touch momentum + smooth + snap
         made touch scrolling feel laggy; a captured pointer drag tracks 1:1. */
      enablePointerDragScroll(slider);
      enableWheelHorizontalScroll(slider);

      slider.addEventListener('scroll', onSettingScroll, { passive: true });
      slider.addEventListener('scrollend', function () {
        if (settingProgressRaf != null) {
          cancelAnimationFrame(settingProgressRaf);
          settingProgressRaf = null;
        }
        syncSliderProgress('rb-setting-slider', 'rb-setting-progress');
        syncMobileStepContext();
      });

      slider.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        var cards = slider.querySelectorAll('.rb-setting-card[data-key="setting"]');
        if (!cards.length) return;
        var activeEl = document.activeElement;
        var idx = Array.prototype.indexOf.call(cards, activeEl);
        if (idx < 0) return;
        e.preventDefault();
        var next = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
        if (next < 0 || next >= cards.length) return;
        cards[next].focus();
        scrollCardIntoViewIfNeeded(cards[next]);
      });

      if (arrow) {
        arrow.addEventListener('click', function () {
          var card = slider.querySelector('.rb-setting-card[data-key="setting"]');
          var step = card ? card.getBoundingClientRect().width + 40 : 280;
          slider.scrollBy({ left: step, behavior: 'smooth' });
        });
      }

      syncSettingOverflowChrome();
      requestAnimationFrame(syncSettingOverflowChrome);
      window.addEventListener('resize', syncSettingOverflowChrome, { passive: true });
      if (typeof ResizeObserver === 'function' && settingWrap) {
        new ResizeObserver(syncSettingOverflowChrome).observe(settingWrap);
      }
      Array.prototype.forEach.call(slider.querySelectorAll('img'), function (image) {
        if (!image.complete) image.addEventListener('load', syncSettingOverflowChrome, { once: true });
      });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(syncSettingOverflowChrome);
      }

      enableProgressBarScroll(document.getElementById('rb-setting-progress'), slider);
    })();

    // Shape slider: fade + native horizontal scroll + progress sync
    (function initShapeSlider() {
      var slider = document.getElementById('rb-shape-slider');
      if (!slider) return;
      var arrow = document.getElementById('rb-shape-arrow');
      var fadeLeft = slider.closest('.rb-shape-wrap');
      fadeLeft = fadeLeft ? fadeLeft.querySelector('.rb-shape-fade-left') : null;

      var shapeProgressRaf = null;

      function onShapeScroll() {
        if (fadeLeft) fadeLeft.classList.toggle('is-visible', slider.scrollLeft > 10);
        if (shapeProgressRaf != null) return;
        shapeProgressRaf = requestAnimationFrame(function () {
          shapeProgressRaf = null;
          syncSliderProgress('rb-shape-slider', 'rb-shape-progress');
        });
      }

      /* Allow touch drag (was mouseOnly) — matches Setting strip for 1:1 feel. */
      enablePointerDragScroll(slider);
      enableWheelHorizontalScroll(slider);

      slider.addEventListener('scroll', onShapeScroll, { passive: true });
      slider.addEventListener('scrollend', function () {
        if (shapeProgressRaf != null) {
          cancelAnimationFrame(shapeProgressRaf);
          shapeProgressRaf = null;
        }
        syncSliderProgress('rb-shape-slider', 'rb-shape-progress');
        syncMobileStepContext();
      });

      if (arrow) {
        arrow.addEventListener('click', function () {
          var card = slider.querySelector('.rb-setting-card[data-key="shape"]');
          var step = card ? card.getBoundingClientRect().width + 40 : 280;
          slider.scrollBy({ left: step, behavior: 'smooth' });
        });
      }

      enableProgressBarScroll(document.getElementById('rb-shape-progress'), slider);
    })();

    /* Metal / Band / Diamond Quality — mobile horizontal strips: IO commit + progress + drag/wheel (CSS shows bar ≤900px). */
    (function initMetalBandQualityOptionStrips() {
      function wireStrip(sliderId, progressId, dataKey, opts) {
        var slider = document.getElementById(sliderId);
        if (!slider) return;
        var stripRaf = null;
        function onStripScroll() {
          if (stripRaf != null) return;
          stripRaf = requestAnimationFrame(function () {
            stripRaf = null;
            syncSliderProgress(sliderId, progressId);
          });
        }
        bindStripScrollSelect(slider, dataKey, opts);
        slider.addEventListener('scrollend', function () {
          if (stripRaf != null) {
            cancelAnimationFrame(stripRaf);
            stripRaf = null;
          }
          syncSliderProgress(sliderId, progressId);
          syncMobileStepContext();
        });
        slider.addEventListener('scroll', onStripScroll, { passive: true });
        enablePointerDragScroll(slider);
        enableWheelHorizontalScroll(slider);
      }

      wireStrip('rb-metal-slider', 'rb-metal-progress', 'metal', {
        stepCheck: function () { return currentStep === 3; },
        getLockUntil: function () { return optionStripScrollLockUntil; },
        onCommit: function (card, value) {
          var targetMetalTab = currentStep === 3 && metalTabMode === 'setting' ? 'setting' : 'ring';
          metalSelections[targetMetalTab] = value;
          state.metal = value;
          setSelected('metal', value);
          syncReview();
          goSlideForCard(card);
        }
      });

      wireStrip('rb-band-thickness-slider', 'rb-band-thickness-progress', 'band_thickness', {
        stepCheck: function () { return currentStep === 4 && bandTabMode === 'thickness'; },
        getLockUntil: function () { return optionStripScrollLockUntil; },
        onCommit: function (card, value) {
          bandSelections.thickness = value;
          state.band_thickness = value;
          state.band = [bandSelections.thickness, bandSelections.profile].filter(Boolean).join(' & ');
          selectedComponents.band = true;
          setSelected('band_thickness', value);
          syncReview();
          goSlideForCard(card);
          ensureThree();
          if (threeReady) {
            updateDisplayedModelFromState();
            syncComponentVisibility();
          }
        }
      });

      wireStrip('rb-band-profile-slider', 'rb-band-profile-progress', 'band_profile', {
        stepCheck: function () { return currentStep === 4 && bandTabMode === 'profile'; },
        getLockUntil: function () { return optionStripScrollLockUntil; },
        onCommit: function (card, value) {
          bandSelections.profile = value;
          state.band_profile = value;
          state.band = [bandSelections.thickness, bandSelections.profile].filter(Boolean).join(' & ');
          selectedComponents.band = true;
          setSelected('band_profile', value);
          syncReview();
          goSlideForCard(card);
          ensureThree();
          if (threeReady) {
            updateDisplayedModelFromState();
            syncComponentVisibility();
          }
        }
      });

      wireStrip('rb-quality-slider', 'rb-quality-progress', 'diamond_quality', {
        stepCheck: function () { return currentStep === 5 && diamondTabMode === 'quality'; },
        getLockUntil: function () { return optionStripScrollLockUntil; },
        onCommit: function (card, value) {
          diamondSelections.quality = value;
          state.diamond_quality = value;
          state.diamond = [diamondSelections.size, diamondSelections.quality].filter(Boolean).join(' / ');
          selectedComponents.diamond = true;
          setSelected('diamond_quality', value);
          syncReview();
          goSlideForCard(card);
          ensureThree();
          if (threeReady) {
            updateDisplayedModelFromState();
            syncComponentVisibility();
          }
        }
      });
    })();

    syncReview();
  });

})();
